import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AnalogStick from "./AnalogStick";
import DPad from "./DPad";
import { wanderPokemons } from "./pokemon";
import { getPlayerSpriteRowOffset } from "./player";
import "../styles/CaptureAnimations.css";
import "../styles/ShinyEffect.css";
import "../Components/ReceiverPowerModal.css";
import DrillModal from "../Components/DrillModal";
import ResearcherModal from "../Components/ResearcherModal";
import { clamp } from "./helper";
import {
  boxesOverlap,
  rockOverlap,
  shrinkOverlap,
  shrinkRectOverlap,
  
} from "./collision";
import api from "../../../api";
import { mineLureImg, dropLureImg, pouchIcon, mineralPotionImg, POKEBALL_ITEM_ID, 
  GREATBALL_ITEM_ID, ULTRABALL_ITEM_ID, LONGBALL_ITEM_ID, RANGEBALL_ITEM_ID, MUDBALL_ITEM_ID, RUBYBALL_ITEM_ID,
   SAPPHIREBALL_ITEM_ID, EMERALDBALL_ITEM_ID, GOLDENBALL_ITEM_ID, DIAMONDBALL_ITEM_ID, SHADOWBALL_ITEM_ID, MASTERBALL_ITEM_ID } from "../../../constants/items";

// Import gem images for special ball rewards
import sapphireImg from "../../../assets/images/items/sapphire.png";
import rubyImg from "../../../assets/images/items/ruby.png";
import emeraldImg from "../../../assets/images/items/emerald.png";
import goldImg from "../../../assets/images/items/gold.png";
import diamondImg from "../../../assets/images/items/diamond.png";
import rockImg from "../../../assets/images/items/rock.png";

import PokemonLayer from "./PokemonLayer";
import RocksLayer from "./RockLayer";
import NPCLayer from "./NPCLayer";
import PortalLayer from "../logic/PortalLayer";
import TeleportZonesLayer from "./TeleportZonesLayer";
import TrainersLayer from "./TrainersLayer";
import InventoryPanel from "../Components/InventoryPanel";
import HeldItemQuickSwap from "../Components/HeldItemQuickSwap";
import { rockIcons } from "../MapConfig/rockIcons";
// import MineralPotionIndicator from "../Components/MineralPotionIndicator";
// import ShinyPotionIndicator from "../Components/ShinyPotionIndicator";
import BagItemsCard from "../Components/BagItemsCard";
import FishingAnimation from "../Components/FishingAnimation";
import FishingSpots from "../Components/FishingSpots";

import BattleScreen from "../../Battle/BattleScreen";
import DialogueModal from "../../../components/Modals/DialogueModal";
import RaffleModal from "../../../components/Modals/RaffleModal";
import LuckyDipModal from "../../../components/Modals/LuckyDipModal";
import CollectionContestModal from "../../../components/Modals/CollectionContestModal";
import FishingMissionsModal from "../../../components/Modals/FishingMissionsModal";
import FarmingMissionsModal from "../../../components/Modals/FarmingMissionsModal";
import AuctionHouseModal from "../../../components/Modals/AuctionHouseModal";
import RacetrackModal from "../../../components/Modals/RacetrackModal";
import WheelOfFortuneModal from "../../../components/Modals/WheelOfFortuneModal";
import BalloonPopModal from "../../../components/Modals/BalloonPopModal";
import TicketExchangeModal from "../../../components/Modals/TicketExchangeModal";
import DiglettDashModal from "../../../components/Modals/DiglettDashModal";
import PlayerShopLotModal from "../../../components/Modals/PlayerShopLotModal";
import PlayerShopBrowseModal from "../../../components/Modals/PlayerShopBrowseModal";
import PlayerShopManageModal from "../../../components/Modals/PlayerShopManageModal";
import ShopLotsLayer from "./ShopLotsLayer";
import DepartmentStoreModal from "../../../components/Modals/DepartmentStoreModal";
import IslandShopModal from "../../../components/Modals/IslandShopModal";
import FishingShopPanel from "../Components/FishingShopPanel";
import BerryShopPanel from "../Components/BerryShopPanel";
import BerryFusionPanel from "../Components/BerryFusionPanel";
import Achievements from "../../Profile/Achievements";
import MyIslandDecorPanel from "../Components/MyIslandDecorPanel";

export default function GameMap({ config, onNpcInteraction, onPlayerPositionChange }) {
  const navigate = useNavigate();
  const gameContainerRef = useRef(null);
  const [showDebugZones, setShowDebugZones] = useState(() => {
    const stored = localStorage.getItem("showDebugZones");
    if (stored === null) return true;
    return stored === "true";
  });

  const [specialTrainerDefeatedById, setSpecialTrainerDefeatedById] = useState({});
  
  const {
    id: MAP_ID,
    image: mapImageUrl,
    over: overImageUrl,
    width: MAP_WIDTH,
    characterSprite: characterSpriteUrl,
    lureImages: { lureImg, superLureImg, ultraLureImg },
    height: MAP_HEIGHT,
    tileSize: TILE_SIZE,
    viewport: { width: VIEWPORT_WIDTH, height: VIEWPORT_HEIGHT },
    initialPlayer,
    collisionAreas,
    teleportZones,
    battleBackground,
  } = config;

  const MY_ISLAND_MAP_ID = 103;
  const isMyIsland = Number(MAP_ID) === MY_ISLAND_MAP_ID;

  const [decorCatalog, setDecorCatalog] = useState([]);
  const [decorPlacements, setDecorPlacements] = useState([]);
  const [receiverWorldPowerStatus, setReceiverWorldPowerStatus] = useState(null);

  const berryShopPower = useMemo(() => {
    if (!isMyIsland) {
      return { byShopPlacementId: {}, anyPowered: false };
    }

    const chargers = receiverWorldPowerStatus?.chargers || [];
    const chargerByPid = new Map(
      (Array.isArray(chargers) ? chargers : []).map((c) => [Number(c?.charger_placement_id), c])
    );

    const chargerPlacements = (decorPlacements || []).filter((p) =>
      ["batterycharger", "bigbatterycharger"].includes(String(p?.decor_key || ""))
    );

    const shops = (decorPlacements || []).filter((p) => String(p?.decor_key || "") === "farmingshop");
    const radius = 500;
    const radiusSq = radius * radius;
    const need = 50000;

    const byShopPlacementId = {};
    let anyPowered = false;

    const shopsSorted = [...shops].sort((a, b) => (Number(a?.placement_id) || 0) - (Number(b?.placement_id) || 0));

    shopsSorted.forEach((sp) => {
      const pid = Number(sp?.placement_id) || 0;
      if (!pid) return;

      const sx = Number(sp?.x) || 0;
      const sy = Number(sp?.y) || 0;
      const sw = Number(sp?.sprite_w) || 96;
      const sh = Number(sp?.sprite_h) || 64;
      const ax = sx + Math.floor(sw / 2);
      const ay = sy + sh;

      let rawTotal = 0;
      let inRangeCount = 0;

      chargerPlacements.forEach((cp) => {
        const cpid = Number(cp?.placement_id) || 0;
        if (!cpid) return;
        const cx = Number(cp?.x) || 0;
        const cy = Number(cp?.y) || 0;
        const dx = cx - ax;
        const dy = cy - ay;
        if ((dx * dx + dy * dy) > radiusSq) return;

        inRangeCount += 1;
        const st = chargerByPid.get(cpid);
        const storedRaw = Math.max(0, Number(st?.stored_voltage) || 0);
        rawTotal += storedRaw;
      });

      const online = rawTotal > 0;
      const canPower = rawTotal >= need;
      if (online) anyPowered = true;
      byShopPlacementId[pid] = {
        powered: online,
        activated: false,
        canPower,
        totalVoltage: rawTotal,
        availableVoltage: rawTotal,
        inRangeCount,
      };
    });

    return { byShopPlacementId, anyPowered };
  }, [isMyIsland, decorPlacements, receiverWorldPowerStatus]);
  const [showDecorPanel, setShowDecorPanel] = useState(false);
  const [selectedDecor, setSelectedDecor] = useState(null);
  const [decorRemoveMode, setDecorRemoveMode] = useState(false);
  const [selectedRemovalPlacementId, setSelectedRemovalPlacementId] = useState(null);
  const [decorMoveMode, setDecorMoveMode] = useState(false);
  const [selectedMovePlacementId, setSelectedMovePlacementId] = useState(null);
  const [movingDecor, setMovingDecor] = useState(null);
  const [mouseGhostPos, setMouseGhostPos] = useState(null);
  const placingDecor = Boolean(selectedDecor && isMyIsland);

  const [trainerNpcCatalog, setTrainerNpcCatalog] = useState([]);
  const [ownedTrainerNpcs, setOwnedTrainerNpcs] = useState([]);
  const [trainerNpcMode, setTrainerNpcMode] = useState(null); // null | 'assign' | 'move'
  const [selectedOwnedTrainerNpc, setSelectedOwnedTrainerNpc] = useState(null);

  const [gameCornerNpcCatalog, setGameCornerNpcCatalog] = useState([]);
  const [ownedGameCornerNpcs, setOwnedGameCornerNpcs] = useState([]);
  const [gameCornerNpcMode, setGameCornerNpcMode] = useState(null); // null | 'assign' | 'move'
  const [selectedOwnedGameCornerNpc, setSelectedOwnedGameCornerNpc] = useState(null);

  const SOILBED_SLOT_BASE = 1000000;
  const soilbedSlotPositions = useMemo(() => {
    if (!isMyIsland) return {};
    const out = {};
    (decorPlacements || []).forEach((p) => {
      if (String(p?.decor_key || "") !== "soilbed") return;
      const pid = Number(p?.placement_id) || 0;
      if (!pid) return;
      const x = Number(p?.x) || 0;
      const y = Number(p?.y) || 0;
      const base = SOILBED_SLOT_BASE + pid * 10;
      out[base + 0] = [x + 0, y + 4];
      out[base + 1] = [x + 16, y + 4];
      out[base + 2] = [x + 32, y + 4];
    });
    return out;
  }, [isMyIsland, decorPlacements]);

  // Researcher modal state
  const [researcherModalOpen, setResearcherModalOpen] = useState(false);
  const [selectedResearchPlacementId, setSelectedResearchPlacementId] = useState(null);
  const [researchUnlocks, setResearchUnlocks] = useState(null);

  useEffect(() => {
    const ids = Array.from(
      new Set(
        (config?.npcs || [])
          .map((n) => n?.hideIfSpecialTrainerDefeated)
          .filter((v) => typeof v === "number" && Number.isFinite(v))
      )
    );

    if (ids.length === 0) return;

    let cancelled = false;

    (async () => {
      try {
        const results = await Promise.all(
          ids.map((trainerId) =>
            api
              .get(`/v1/special-trainers/${trainerId}/state`, { withCredentials: true })
              .then((r) => ({ trainerId, defeated: Boolean(r?.data?.defeated) }))
              .catch(() => ({ trainerId, defeated: false }))
          )
        );

        if (cancelled) return;

        setSpecialTrainerDefeatedById((prev) => {
          const next = { ...prev };
          results.forEach(({ trainerId, defeated }) => {
            next[trainerId] = defeated;
          });
          return next;
        });
      } catch (e) {
        if (!cancelled) {
          setSpecialTrainerDefeatedById((prev) => ({ ...prev }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [config?.npcs]);

  const effectiveNpcs = useMemo(() => {
    const npcs = config?.npcs || [];
    return npcs.filter((n) => {
      const gate = n?.hideIfSpecialTrainerDefeated;
      if (typeof gate === "number" && Number.isFinite(gate)) {
        return !Boolean(specialTrainerDefeatedById[gate]);
      }
      return true;
    });
  }, [config?.npcs, specialTrainerDefeatedById]);

  const mapNpcs = useMemo(() => {
    const baseRaw = Array.isArray(effectiveNpcs) ? effectiveNpcs : [];
    if (!isMyIsland) return baseRaw;

    const base = baseRaw.filter((n) => String(n?.modalType || "") !== "berry-shop");

    const shops = (decorPlacements || []).filter((p) => String(p?.decor_key || "") === "farmingshop");
    const shopNpcs = shops.map((p) => {
      const x = Number(p?.x) || 0;
      const y = Number(p?.y) || 0;
      const w = Number(p?.sprite_w) || 96;
      const h = Number(p?.sprite_h) || 64;

      const nx = Math.round((x + Math.floor(w / 2) - 8) / 8) * 8;
      const ny = Math.round((y + h - 16) / 8) * 8;
      const pid = Number(p?.placement_id) || 0;

      return {
        id: `MyIslandBerryShopOwner_${pid || nx}_${ny}`,
        shop_placement_id: pid,
        x: nx,
        y: ny,
        width: 16,
        height: 24,
        sprite: "/Trainers/shopownerfront.png",
        zIndex: 1000 + Math.round(ny + 24),
        disabled: !Boolean((berryShopPower.byShopPlacementId?.[pid] || {}).powered),
        dialogue: "Welcome to the Berry Shop! Need supplies, want to sell berries, or repair gear?",
        modalType: "berry-shop",
        name: "Berry Shop Owner",
      };
    });

    // Add researcher NPCs from research building placements
    const researchBuildings = (decorPlacements || []).filter((p) => String(p?.decor_key || "") === "research");
    const researcherNpcs = researchBuildings.map((p) => {
      const x = Number(p?.x) || 0;
      const y = Number(p?.y) || 0;
      const w = Number(p?.sprite_w) || 178;
      const h = Number(p?.sprite_h) || 122;

      // Position NPC in front of building entrance (center-bottom)
      const nx = x + 81; // Center of 178px wide building
      const ny = y + 106; // Just in front of entrance
      const pid = Number(p?.placement_id) || 0;

      const poweredResearch = receiverWorldPowerStatus?.powered_research || [];
      const isPowered = poweredResearch.includes(pid);

      return {
        id: `MyIslandResearcher_${pid}`,
        research_placement_id: pid,
        x: nx,
        y: ny,
        width: 16,
        height: 24,
        sprite: "/Trainers/researcher.png",
        zIndex: 1000 + Math.round(ny + 24),
        disabled: !isPowered,
        dialogue: "Welcome to the Research Building! I can help you unlock new technologies.",
        modalType: "researcher",
        name: "Researcher",
      };
    });

    return [...base, ...shopNpcs, ...researcherNpcs];
  }, [effectiveNpcs, isMyIsland, decorPlacements, berryShopPower, receiverWorldPowerStatus]);

  // —— States ——
  // Check for teleport target coordinates in localStorage
  const getTeleportTarget = () => {
    const teleportData = localStorage.getItem('teleportTarget');
    if (teleportData) {
      localStorage.removeItem('teleportTarget'); // Clear after use
      return JSON.parse(teleportData);
    }
    return null;
  };

  const handleApplyMulchToPlant = async (plant) => {
    if (!selectedAction || selectedAction.type !== "mulch") {
      spawnText("Select a mulch from your pouch first!", playerPos.x, playerPos.y - 16, "#ff0000");
      return;
    }

    if (plant?.mulch?.mulch_item_id) {
      spawnText("A mulch is already applied!", plant.x, plant.y - 16, "#ffaa00");
      return;
    }

    if (Number(plant?.growth_percent || 0) >= 30) {
      spawnText("Mulch can only be applied before 30% growth!", plant.x, plant.y - 16, "#ff0000");
      return;
    }

    const mulchId = selectedAction.payload.item;

    try {
      const res = await api.post(
        "/v1/berry-plants/apply-mulch",
        { slot_index: plant.slot_index, mulch_item_id: mulchId, map_id: MAP_ID },
        { withCredentials: true }
      );

      if (res.data?.success) {
        spawnText("Mulch applied!", plant.x, plant.y - 16, "#00ff00");
        refreshBerryPlants();

        api.get("/v1/inventory", { withCredentials: true })
          .then((r) => {
            const filtered = r.data.filter((i) =>
              [2, 3, 4, 63, 141, 142, 143, 199,200,201,202,203,204,205,206,207,208,209,210, 234,167, 235, 116, 117, 6, 7, 8, 9, 10, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007,4008, 214, 701780, 701781].includes(i.item) &&
              i.amount > 0
            );
            setInventory(filtered);
          })
          .catch(console.error);
      }
    } catch (error) {
      console.error("Error applying mulch:", error);
      const errorMsg = error.response?.data?.detail || "Failed to apply mulch";
      spawnText(errorMsg, plant.x, plant.y - 16, "#ff0000");
    }
  };
  
  const teleportTarget = getTeleportTarget();
  const [playerPos, setPlayerPos] = useState({
    x: teleportTarget?.x ?? initialPlayer.x,
    y: teleportTarget?.y ?? initialPlayer.y,
  });
  const [direction, setDirection] = useState(initialPlayer.direction);
  const [playerStep, setPlayerStep] = useState(initialPlayer.step);
  const [isNight, setIsNight] = useState(false); // Day/night cycle state

  const startTrainerBattle = async (trainerId, trainerName, ownedNpcId = null, mapId = null) => {
    try {
      const battlePayload = {
        opponent_type: "trainer",
        trainer_id: trainerId,
        owned_npc_id: ownedNpcId,
      };
      if (mapId) {
        battlePayload.map_id = mapId;
      }

      const res = await api.post("/v1/battle/start", battlePayload, { withCredentials: true });
      const trainerNameResolved = res.data?.trainer_name || trainerName;
      setInitialBattleData({
        ...res.data,
        opponent_type: "trainer",
        trainer_id: trainerId,
        trainer_name: trainerNameResolved,
      });
      setInBattle(true);
    } catch (err) {
      console.error("Failed to start trainer battle:", err);
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Can't battle right now!";
      spawnText(msg, playerPos.x, playerPos.y - 16, "red");
    }
  };

  // Control type (analog stick or D-pad)
  const [controlType, setControlType] = useState(
    localStorage.getItem("controlType") || "analog"
  );

  // Listen for control type changes from Settings
  useEffect(() => {
    const handleStorageChange = () => {
      const newControlType = localStorage.getItem("controlType") || "analog";
      setControlType(newControlType);
    };

    // Listen for storage events (changes from other tabs/windows)
    window.addEventListener("storage", handleStorageChange);

    // Also check periodically in case same-tab changes don't trigger storage event
    const interval = setInterval(handleStorageChange, 1000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const [showPouch, setShowPouch] = useState(false);
  const togglePouch = () => setShowPouch((v) => !v);

  const [showFishingShop, setShowFishingShop] = useState(false);
  const toggleFishingShop = () => setShowFishingShop((v) => !v);

  const [showBerryShop, setShowBerryShop] = useState(false);
  const toggleBerryShop = () => setShowBerryShop((v) => !v);
  const [activeBerryShopPlacementId, setActiveBerryShopPlacementId] = useState(null);

  const [showBerryFusion, setShowBerryFusion] = useState(false);
  const toggleBerryFusion = () => setShowBerryFusion((v) => !v);
  const [activeBerryFusionStationId, setActiveBerryFusionStationId] = useState(null);

  const [achievementsModalOpen, setAchievementsModalOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F2" || e.key === "`") {
        setShowDebugZones((prev) => {
          const next = !prev;
          localStorage.setItem("showDebugZones", next ? "true" : "false");
          return next;
        });
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const stickActiveRef = useRef(false);
  const stickSeqTimeoutsRef = useRef([]);
  const walkDistanceRef = useRef(0);
  const miningPositionRef = useRef(null); // Ref to store mining position during animation
  const capturePositionRef = useRef(null); // Ref to store capture position during animation
  // Refs to provide latest state inside intervals/callbacks
  const playerPosRef = useRef(playerPos);
  const [expLureHits, setExpLureHits] = useState(0);
  const [dropLureHits, setDropLureHits] = useState(0);
  const [activeGummy, setActiveGummy] = useState(null);
  
  // Pickaxe max durability values
  const pickaxeMaxDurability = {
    228: 50,  // Normal pickaxe
    229: 200, // Ruby pickaxe
    230: 150, // Emerald pickaxe
    231: 75,  // Golden pickaxe
    232: 250, // Diamond pickaxe
    233: 200, // Sapphire pickaxe
  };

  const [rocks, setRocks] = useState([]);
  const [breakingFrames, setBreaking] = useState({});
  const [trainerStates, setTrainerStates] = useState([]);
  const [berryPlants, setBerryPlants] = useState([]);

  // Berry planting positions - matches backend config (island_positions.py)
  const BERRY_PLANT_POSITIONS = [
    [97, 386],   // Slot 0
    [111, 386],  // Slot 1
    [125, 386],  // Slot 2
    [178, 386],  // Slot 3
    [192, 386],  // Slot 4
    [206, 386],  // Slot 5
    [256, 386],  // Slot 6
    [270, 386],  // Slot 7
    [284, 386],  // Slot 8
    [338, 386],  // Slot 9
    [352, 386],  // Slot 10
    [366, 386],  // Slot 11
    [96, 12],
    [112, 12],
    [128, 12],
    [179, 12],
    [192, 12],
    [206, 12],
    [256, 12],
    [270, 12],
    [284, 12],
    [338, 12],
    [352, 12],
    [366, 12],  
  ];

  const [wildPokemon, setWildPokemon] = useState([]);
  const [floatingTexts, setFloatingTexts] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [steamPuffs, setSteamPuffs] = useState([]);
  const [portals, setPortals] = useState([]);

  const wildPokemonRef = useRef(wildPokemon);
  const lastTeamRocketLogRef = useRef(0);
  useEffect(() => {
    wildPokemonRef.current = wildPokemon;
  }, [wildPokemon]);

  const [inventory, setInventory] = useState([]);
  const [selectedBall, setSelectedBall] = useState(null);
  const [catchRates, setCatchRates] = useState([]);
  const [selectedAction, setSelectedAction] = useState(null);

  const [mineralPotionActive, setMineralPotionActive] = useState(false);
  const [mineralPotionExpire, setMineralPotionExpire] = useState(null);
  const [mineralPotionCountdown, setMineralPotionCountdown] = useState(0);
  const [shinyPotionActive, setShinyPotionActive] = useState(false);
  const [shinyPotionExpire, setShinyPotionExpire] = useState(null);
  const [shinyPotionCountdown, setShinyPotionCountdown] = useState(0);
  
  // Held Item Quick Swap state
  const [showHeldItemSwap, setShowHeldItemSwap] = useState(false);

  const [activeLure, setActiveLure] = useState(null);
  const [lureExpire, setLureExpire] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [gems, setGems] = useState({});
  const [shinyCharm, setShinyCharm] = useState(null);
  const [shinySkillBonus, setShinySkillBonus] = useState(0);
  const [rodDurabilityBonus, setRodDurabilityBonus] = useState(0);
  const [pickaxeEfficiency, setPickaxeEfficiency] = useState(0);
  const [miningCharmStacks, setMiningCharmStacks] = useState(0);
  const [equippedPickaxe, setEquippedPickaxe] = useState(null);
  const [equippedRod, setEquippedRod] = useState(null);
  const [equippedItemFinder, setEquippedItemFinder] = useState(null);
  const [equippedCatchingGloves, setEquippedCatchingGloves] = useState(null);
  const [hasEquippedRing, setHasEquippedRing] = useState(false);
  const [hasDepartmentRing, setHasDepartmentRing] = useState(false);
  const [equippedRings, setEquippedRings] = useState([]);
  const [hasGlideBoots, setHasGlideBoots] = useState(false);

  const [rodConfig, setRodConfig] = useState({});
  const CONTROLS_ROW_HEIGHT_PX = 180;
  const MIN_MOBILE_SCALE = 2.5;
  const MAX_MOBILE_SCALE = 4;
  const [viewportSize, setViewportSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  
  // Effect to close held item swap popup if ring is unequipped
  useEffect(() => {
    if (!hasEquippedRing && showHeldItemSwap) {
      setShowHeldItemSwap(false);
    }
  }, [hasEquippedRing, showHeldItemSwap]);
  const [mapScale, setMapScale] = useState(1);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inBattle, setInBattle] = useState(false);
  const [initialBattleData, setInitialBattleData] = useState(null);
  const [battleTowerVictory, setBattleTowerVictory] = useState(false);
  const [battleTowerFloor, setBattleTowerFloor] = useState(null);

  const ws = useRef(null);
  const expireTO = useRef(null);
  const reconnectTO = useRef(null);
  const [battleSpawnId, setBattleSpawnId] = useState(null);
  const [captureEffects, setCaptureEffects] = useState([]);
  
  // NPC interaction states
  const [dialogueModalOpen, setDialogueModalOpen] = useState(false);
  const [raffleModalOpen, setRaffleModalOpen] = useState(false);
  const [luckyDipModalOpen, setLuckyDipModalOpen] = useState(false);
  const [collectionContestModalOpen, setCollectionContestModalOpen] = useState(false);
  const [fishingMissionsModalOpen, setFishingMissionsModalOpen] = useState(false);
  const [farmingMissionsModalOpen, setFarmingMissionsModalOpen] = useState(false);
  const [auctionHouseModalOpen, setAuctionHouseModalOpen] = useState(false);
  const [racetrackModalOpen, setRacetrackModalOpen] = useState(false);
  const [wheelOfFortuneModalOpen, setWheelOfFortuneModalOpen] = useState(false);
  const [balloonPopModalOpen, setBalloonPopModalOpen] = useState(false);
  const [ticketExchangeModalOpen, setTicketExchangeModalOpen] = useState(false);
  const [receiverPowerModalOpen, setReceiverPowerModalOpen] = useState(false);
  const [receiverPowerPlacementId, setReceiverPowerPlacementId] = useState(null);
  const [receiverPowerStatus, setReceiverPowerStatus] = useState(null);
  const [receiverEligiblePokemon, setReceiverEligiblePokemon] = useState([]);
  const [receiverSelectedPokemonId, setReceiverSelectedPokemonId] = useState("");
  const [receiverPowerLoading, setReceiverPowerLoading] = useState(false);
  const [receiverPowerError, setReceiverPowerError] = useState(null);
  const [drillModalOpen, setDrillModalOpen] = useState(false);
  const [drillPlacementId, setDrillPlacementId] = useState(null);
  const [drillStatus, setDrillStatus] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  
  const [activeNPC, setActiveNPC] = useState(null);
  const [diglettDashModalOpen, setDiglettDashModalOpen] = useState(false);
  const [playerShopLotModalOpen, setPlayerShopLotModalOpen] = useState(false);
  const [playerShopBrowseModalOpen, setPlayerShopBrowseModalOpen] = useState(false);
  const [playerShopManageModalOpen, setPlayerShopManageModalOpen] = useState(false);
  const [selectedLotId, setSelectedLotId] = useState(null);
  const [selectedShopName, setSelectedShopName] = useState('');
  
  const [departmentStoreModalOpen, setDepartmentStoreModalOpen] = useState(false);
  const [islandShopModalOpen, setIslandShopModalOpen] = useState(false);
  const [storeItems, setStoreItems] = useState([]);
  const [currency, setCurrency] = useState({ money: 0, coins: 0 });
  const [quantityMap, setQuantityMap] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');
  
  // Fishing state
  const [fishingSpots, setFishingSpots] = useState([]);
  const [isFishing, setIsFishing] = useState(false);
  const [fishingAnimation, setFishingAnimation] = useState(null);
  const berryPlantInFlightRef = useRef(new Set());

  const refreshRocks = useCallback(() => {
    api
      .get("/v1/rocks", { params: { map_id: MAP_ID }, withCredentials: true })
      .then((r) => setRocks(r.data.data || []))
      .catch(console.error);
  }, [MAP_ID]);

  const refreshDecor = useCallback(() => {
    if (!isMyIsland) return;
    Promise.all([
      api.get("/v1/island/decor/catalog", { withCredentials: true }),
      api.get("/v1/island/decor/placements", { params: { map_id: MY_ISLAND_MAP_ID }, withCredentials: true }),
      api.get("/v1/island/trainer-npcs/catalog", { withCredentials: true }),
      api.get("/v1/island/trainer-npcs/owned", { params: { map_id: MY_ISLAND_MAP_ID }, withCredentials: true }),
      api.get("/v1/island/gamecorner-npcs/catalog", { withCredentials: true }),
      api.get("/v1/island/gamecorner-npcs/owned", { params: { map_id: MY_ISLAND_MAP_ID }, withCredentials: true }),
    ])
      .then(([catRes, placeRes, npcCatRes, ownedRes, gcCatRes, gcOwnedRes]) => {
        const cat = catRes?.data?.data;
        if (Array.isArray(cat)) setDecorCatalog(cat);
        setDecorPlacements(placeRes?.data?.data || []);

        const npcCat = npcCatRes?.data?.data;
        if (Array.isArray(npcCat)) setTrainerNpcCatalog(npcCat);
        const owned = ownedRes?.data?.data;
        if (Array.isArray(owned)) setOwnedTrainerNpcs(owned);

        const gcCat = gcCatRes?.data?.data;
        if (Array.isArray(gcCat)) setGameCornerNpcCatalog(gcCat);
        const gcOwned = gcOwnedRes?.data?.data;
        if (Array.isArray(gcOwned)) setOwnedGameCornerNpcs(gcOwned);
      })
      .catch(console.error);
  }, [isMyIsland]);

  const directionRef = useRef(direction);
  useEffect(() => {
    directionRef.current = direction;
  }, [direction]);

  const rocksRef = useRef(rocks);
  useEffect(() => {
    rocksRef.current = rocks;
  }, [rocks]);

  const spawnPointAppliedRef = useRef(false);
  useEffect(() => {
    spawnPointAppliedRef.current = false;
  }, [MAP_ID]);

  useEffect(() => {
    if (!isMyIsland) return;
    if (spawnPointAppliedRef.current) return;

    api
      .get("/v1/island/decor/spawn-point", { withCredentials: true })
      .then((res) => {
        const sp = res?.data?.spawn_point;
        if (sp && !teleportTarget) {
          setPlayerPos({ x: Number(sp.x) || initialPlayer.x, y: Number(sp.y) || initialPlayer.y });
        }
      })
      .catch(() => {})
      .finally(() => {
        spawnPointAppliedRef.current = true;
      });
  }, [isMyIsland, teleportTarget, MAP_ID, initialPlayer.x, initialPlayer.y]);

  const breakingFramesRef = useRef(breakingFrames);
  useEffect(() => {
    breakingFramesRef.current = breakingFrames;
  }, [breakingFrames]);

  const trainerStatesRef = useRef(trainerStates);
  useEffect(() => {
    trainerStatesRef.current = trainerStates;
  }, [trainerStates]);

  const berryPlantsRef = useRef(berryPlants);
  useEffect(() => {
    berryPlantsRef.current = berryPlants;
  }, [berryPlants]);

  const soilbedSlotPositionsRef = useRef(soilbedSlotPositions);
  useEffect(() => {
    soilbedSlotPositionsRef.current = soilbedSlotPositions;
  }, [soilbedSlotPositions]);

  const mapNpcsRef = useRef(mapNpcs);
  useEffect(() => {
    mapNpcsRef.current = mapNpcs;
  }, [mapNpcs]);

  const refreshOwnedTrainerNpcs = useCallback(() => {
    if (!isMyIsland) return;
    api
      .get("/v1/island/trainer-npcs/owned", { params: { map_id: MY_ISLAND_MAP_ID }, withCredentials: true })
      .then((r) => setOwnedTrainerNpcs(r?.data?.data || []))
      .catch(() => setOwnedTrainerNpcs((prev) => [...prev]));
  }, [isMyIsland]);

  const refreshOwnedGameCornerNpcs = useCallback(() => {
    if (!isMyIsland) return;
    api
      .get("/v1/island/gamecorner-npcs/owned", { params: { map_id: MY_ISLAND_MAP_ID }, withCredentials: true })
      .then((r) => setOwnedGameCornerNpcs(r?.data?.data || []))
      .catch(() => setOwnedGameCornerNpcs((prev) => [...prev]));
  }, [isMyIsland]);

  const handleBuyTrainerNpc = useCallback(
    async (row) => {
      const trainerId = Number(row?.trainer_id) || 0;
      if (!trainerId) return;
      try {
        await api.post(
          "/v1/island/trainer-npcs/buy",
          { trainer_id: trainerId, quantity: 1 },
          { withCredentials: true }
        );
        spawnText("Purchased!", playerPos.x, playerPos.y - 16, "#22c55e");
        refreshOwnedTrainerNpcs();
      } catch (e) {
        const msg = e?.response?.data?.detail || "Failed to buy Trainer NPC";
        spawnText(msg, playerPos.x, playerPos.y - 16, "#f87171");
      }
    },
    [spawnText, playerPos, refreshOwnedTrainerNpcs]
  );

  const handleBuyGameCornerNpc = useCallback(
    async (row) => {
      const npcId = Number(row?.npc_id) || 0;
      if (!npcId) return;
      try {
        await api.post(
          "/v1/island/gamecorner-npcs/buy",
          { npc_id: npcId, quantity: 1 },
          { withCredentials: true }
        );
        spawnText("Purchased!", playerPos.x, playerPos.y - 16, "#22c55e");
        refreshOwnedGameCornerNpcs();
      } catch (e) {
        const msg = e?.response?.data?.detail || "Failed to buy Game Corner NPC";
        spawnText(msg, playerPos.x, playerPos.y - 16, "#f87171");
      }
    },
    [spawnText, playerPos, refreshOwnedGameCornerNpcs]
  );

  const enterAssignTrainerNpcMode = useCallback((ownedRow) => {
    setTrainerNpcMode("assign");
    setSelectedOwnedTrainerNpc(ownedRow || null);
    setSelectedDecor(null);
    setMouseGhostPos(null);
    setDecorRemoveMode(false);
    setSelectedRemovalPlacementId(null);
    setShowDecorPanel(false);
  }, []);

  const enterMoveTrainerNpcMode = useCallback((ownedRow) => {
    setTrainerNpcMode("move");
    setSelectedOwnedTrainerNpc(ownedRow || null);
    setSelectedDecor(null);
    setMouseGhostPos(null);
    setDecorRemoveMode(false);
    setSelectedRemovalPlacementId(null);
    setShowDecorPanel(false);
  }, []);

  const enterAssignGameCornerNpcMode = useCallback((ownedRow) => {
    setGameCornerNpcMode("assign");
    setSelectedOwnedGameCornerNpc(ownedRow || null);
    setSelectedDecor(null);
    setMouseGhostPos(null);
    setDecorRemoveMode(false);
    setSelectedRemovalPlacementId(null);
    setShowDecorPanel(false);
  }, []);

  const enterMoveGameCornerNpcMode = useCallback((ownedRow) => {
    setGameCornerNpcMode("move");
    setSelectedOwnedGameCornerNpc(ownedRow || null);
    setSelectedDecor(null);
    setMouseGhostPos(null);
    setDecorRemoveMode(false);
    setSelectedRemovalPlacementId(null);
    setShowDecorPanel(false);
  }, []);

  const exitTrainerNpcMode = useCallback(() => {
    setTrainerNpcMode(null);
    setSelectedOwnedTrainerNpc(null);
  }, []);

  useEffect(() => {
    if (!receiverPowerModalOpen) return;
    if (!isMyIsland) return;

    let cancelled = false;
    let iv = null;

    const loadEligible = async () => {
      try {
        const res = await api.get("/v1/island/decor/receiver/eligible-pokemon", { withCredentials: true });
        if (cancelled) return;
        setReceiverEligiblePokemon(res?.data?.data || []);
      } catch (e) {
      }
    };

    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await api.get("/v1/island/decor/power/status", {
          params: { map_id: MY_ISLAND_MAP_ID },
          withCredentials: true,
        });
        if (cancelled) return;
        setReceiverPowerStatus(res?.data || null);
        setReceiverPowerError(null);
      } catch (e) {
        if (cancelled) return;
        setReceiverPowerError(e?.response?.data?.detail || "Failed to load power status");
      }
    };

    loadEligible();
    tick();
    iv = setInterval(tick, 15000);

    return () => {
      cancelled = true;
      if (iv) clearInterval(iv);
    };
  }, [receiverPowerModalOpen, receiverPowerPlacementId, isMyIsland]);

  // Fetch drill status when drill modal opens
  useEffect(() => {
    if (!drillModalOpen || !drillPlacementId) return;
    if (!isMyIsland) return;

    let cancelled = false;
    let iv = null;

    const tick = async () => {
      if (cancelled) return;
      setDrillLoading(true);
      try {
        const res = await api.get("/v1/island/drill/status", {
          params: { drill_placement_id: drillPlacementId, map_id: MY_ISLAND_MAP_ID },
          withCredentials: true,
        });
        if (cancelled) return;
        setDrillStatus(res?.data || null);
      } catch (e) {
        console.error("Failed to load drill status:", e);
      } finally {
        if (!cancelled) setDrillLoading(false);
      }
    };

    tick();
    iv = setInterval(tick, 10000); // Update every 10 seconds

    return () => {
      cancelled = true;
      if (iv) clearInterval(iv);
    };
  }, [drillModalOpen, drillPlacementId, isMyIsland]);

  // Fetch research unlocks when modal opens
  useEffect(() => {
    if (!researcherModalOpen) return;

    const fetchUnlocks = async () => {
      try {
        const res = await api.get('/v1/island/research/unlocks', {
          params: { map_id: MY_ISLAND_MAP_ID },
          withCredentials: true
        });
        setResearchUnlocks(res?.data || null);
      } catch (e) {
        console.error('Failed to fetch research unlocks:', e);
      }
    };

    fetchUnlocks();
  }, [researcherModalOpen]);

  useEffect(() => {
    if (!isMyIsland) return;

    let cancelled = false;
    let iv = null;

    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await api.get("/v1/island/decor/power/status", {
          params: { map_id: MY_ISLAND_MAP_ID },
          withCredentials: true,
        });
        if (cancelled) return;
        setReceiverWorldPowerStatus(res?.data || null);
      } catch (e) {
      }
    };

    tick();
    iv = setInterval(tick, 30000);

    return () => {
      cancelled = true;
      if (iv) clearInterval(iv);
    };
  }, [isMyIsland]);

  const refreshFishingSpots = useCallback(() => {
    if (!(MAP_ID === 1 || MAP_ID === MY_ISLAND_MAP_ID)) return;
    api
      .get('/v1/fishing/spots', { params: { map_id: MAP_ID }, withCredentials: true })
      .then(response => {
        setFishingSpots(response.data.spots || []);
      })
      .catch(err => console.error('Error loading fishing spots:', err));
  }, [MAP_ID]);

  useEffect(() => {
    refreshFishingSpots();
  }, [refreshFishingSpots]);

  useEffect(() => {
    if (!isMyIsland) return;
    refreshFishingSpots();
  }, [isMyIsland, decorPlacements, refreshFishingSpots]);

  useEffect(() => {
    refreshDecor();
  }, [refreshDecor]);

  const refreshBerryPlants = useCallback(() => {
    if (MAP_ID === 1 || MAP_ID === MY_ISLAND_MAP_ID) {
      api
        .get("/v1/berry-plants/", { params: { map_id: MAP_ID }, withCredentials: true })
        .then((r) => setBerryPlants(r.data.data || []))
        .catch(console.error);
    }
  }, [MAP_ID]);

  useEffect(() => {
    refreshBerryPlants();
  }, [refreshBerryPlants, MAP_ID]);

  useEffect(() => {
    api
      .get("/v1/island/rod-config", { withCredentials: true })
      .then((r) => {
        const rows = r.data?.rods || [];
        const map = {};
        rows.forEach((row) => {
          if (row && row.rod_item_id != null) map[row.rod_item_id] = row;
        });
        setRodConfig(map);
      })
      .catch(console.error);
  }, []);

  // Handle fishing spot click
  const handleFishingSpotClick = async (spot, spotIndex) => {
    if (isFishing) return;
    
    // Check if player has any rod equipped (Old Rod 137, Good Rod 138, or Super Rod 139)
    if (!equippedRod || equippedRod.durability <= 0) {
      spawnText('You need a fishing rod equipped to fish!', playerPos.x, playerPos.y - 20, '#f87171');
      return;
    }
    
    // Validate rod item ID
    const validRodIds = Object.keys(rodConfig).map((k) => Number(k));
    if (!validRodIds.includes(equippedRod.item)) {
      spawnText('You need a fishing rod equipped to fish!', playerPos.x, playerPos.y - 20, '#f87171');
      return;
    }
    
    const findSafeFishingStand = (baseX, baseY) => {
      const wallFn = isCollidingRef.current;
      const decorFn = isCollidingWithDecorRef.current;
      const isBad = (x, y) => {
        const hitWall = typeof wallFn === "function" ? wallFn(x, y) : false;
        const hitDecor = typeof decorFn === "function" ? decorFn(x, y) : false;
        return Boolean(hitWall || hitDecor);
      };

      const snap = (n) => Math.round((Number(n) || 0) / 8) * 8;

      const bx = snap(baseX);
      const by = snap(baseY);

      const offsets = [
        [0, 0],
        [0, 8],
        [0, -8],
        [8, 0],
        [-8, 0],
        [0, 16],
        [0, -16],
        [16, 0],
        [-16, 0],
        [0, 24],
        [0, -24],
        [24, 0],
        [-24, 0],
      ];

      for (const [dx, dy] of offsets) {
        const tx = bx + dx;
        const ty = by + dy;
        if (!isBad(tx, ty)) return { x: tx, y: ty };
      }
      return null;
    };

    const safeStand = findSafeFishingStand(spot.player_x, spot.player_y);
    if (!safeStand) {
      spawnText("Can't fish here (blocked)", playerPos.x, playerPos.y - 20, "#f87171");
      return;
    }

    const computeCastDirection = () => {
      const targetX = Number(spot?.click_x ?? spot?.player_x ?? safeStand.x) || 0;
      const targetY = Number(spot?.click_y ?? spot?.player_y ?? safeStand.y) || 0;
      const cx = (Number(safeStand.x) || 0) + 16;
      const cy = (Number(safeStand.y) || 0) + 16;
      const dx = targetX - cx;
      const dy = targetY - cy;
      if (Math.abs(dx) > Math.abs(dy)) {
        return dx > 0 ? "right" : "left";
      }
      return dy > 0 ? "down" : "up";
    };

    const castDirection = String(spot?.direction || computeCastDirection());

    setIsFishing(true);

    // Move player to fishing position
    setPlayerPos({ x: safeStand.x, y: safeStand.y });
    setDirection(castDirection);
    
    try {
      const response = await api.post('/v1/fishing/use-rod', {
        rod_item_id: equippedRod.item, // Use the equipped rod's item ID
        spot_index: spotIndex,
        map_id: MAP_ID
      }, { withCredentials: true });
      
      if (response.data.success) {
        // Show fishing animation
        setFishingAnimation({
          x: safeStand.x,
          y: safeStand.y,
          direction: castDirection,
          duration: response.data.duration_seconds * 1000
        });
        
        // Wait for animation to complete
        setTimeout(() => {
          setFishingAnimation(null);
          setIsFishing(false);
          
          // Handle reward
          if (response.data.reward_type === 'pokemon') {
            // Spawn Pokemon on map near the player based on fishing direction
            const pokemonName = response.data.reward_data.pokemon;
            spawnText(`A wild ${pokemonName} appeared!`, playerPos.x, playerPos.y - 20, '#4ade80');
            
            // Calculate Pokemon spawn position based on fishing direction
            let pokeX = playerPos.x;
            let pokeY = playerPos.y;
            
            switch(castDirection) {
              case 'left':
                pokeX = playerPos.x - 32;
                break;
              case 'right':
                pokeX = playerPos.x + 32;
                break;
              case 'up':
                pokeY = playerPos.y - 32;
                break;
              case 'down':
                pokeY = playerPos.y + 32;
                break;
            }

            if (MAP_ID === MY_ISLAND_MAP_ID) {
              const wallFn = isCollidingRef.current;
              const decorFn = isPokemonCollidingWithDecorRef.current;
              const isBad = (x, y) => {
                const hitWall = typeof wallFn === "function" ? wallFn(x, y) : false;
                const hitDecor = typeof decorFn === "function" ? decorFn(x, y) : false;
                return Boolean(hitWall || hitDecor);
              };

              const offsets = [
                [0, 0],
                [32, 0],
                [-32, 0],
                [0, 32],
                [0, -32],
                [64, 0],
                [-64, 0],
                [0, 64],
                [0, -64],
              ];

              for (const [dx, dy] of offsets) {
                const tx = Number(pokeX) + dx;
                const ty = Number(pokeY) + dy;
                if (!isBad(tx, ty)) {
                  pokeX = tx;
                  pokeY = ty;
                  break;
                }
              }
            }
            
            // Add Pokemon to wildPokemon array with all required properties
            setWildPokemon(prev => [...prev, {
              id: `fishing_${Date.now()}`,
              species: pokemonName,
              name: pokemonName,
              x: pokeX,
              y: pokeY,
              sprite: `/Maps/Overworld/${pokemonName}.png`,
              shiny: false,
              level: Math.floor(Math.random() * 10) + 5,
              direction: 'down',
              step: 0,
              capturing: false,
              captured: false
            }]);
            
          } else if (response.data.reward_type === 'fisher_coin') {
            const amount = response.data.reward_data.amount;
            spawnText(`+${amount} Fisher Coin!`, playerPos.x, playerPos.y - 20, '#fbbf24');
            
          } else if (response.data.reward_type === 'item') {
            const amount = response.data.reward_data.amount;
            spawnText(`+${amount} ${response.data.reward_data.item_name}!`, playerPos.x, playerPos.y - 20, '#a78bfa');
          }
          
          // Display fishing EXP gained
          if (response.data.fishing_exp) {
            setTimeout(() => {
              spawnText(`+${response.data.fishing_exp} Fishing EXP`, playerPos.x, playerPos.y - 40, '#60a5fa');
            }, 250);
          }
          
          // Update equipped rod durability locally
          setEquippedRod(rod => {
            if (!rod) return null;
            const newDurability = response.data.rod_durability;
            if (newDurability <= 0) {
              spawnText('Your Rod broke!', playerPos.x, playerPos.y - 60, '#f87171');
              // Reload inventory to reflect broken rod
              api.get("/v1/inventory", { withCredentials: true }).then((r) => {
                const filtered = r.data.filter(
                  (i) =>
                    [2, 3, 4, 137, 141, 142, 143, 199,200,201,202,203,204,205,206,207,208,209,210, 234,167, 235, 116, 117, 6, 7, 8, 9, 10, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007,4008, 214, 701780, 701781].includes(i.item) &&
                    i.amount > 0
                );
                setInventory(filtered);
              }).catch(console.error);
              return null;
            } else {
              // Get max durability based on rod type
              const maxDurability = rodConfig?.[rod.item]?.max_durability || rod.durability;
              spawnText(`Rod: ${newDurability}/${maxDurability}`, playerPos.x, playerPos.y - 60, '#94a3b8');
              return { ...rod, durability: newDurability };
            }
          });
          
          // Refresh inventory and equipped items
          api.get("/v1/inventory", { withCredentials: true })
            .then((r) => {
              const filtered = r.data.filter(
                (i) =>
                  [2, 3, 4, 137, 141, 142, 143, 199,200,201,202,203,204,205,206,207,208,209,210, 234,167, 235, 116, 117, 6, 7, 8, 9, 10, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007,4008, 214, 701780, 701781].includes(i.item) &&
                  i.amount > 0
              );
              setInventory(filtered);
              
              // Refresh equipped rod (Old Rod 137, Good Rod 138, Super Rod 139)
              const updatedRod = r.data.find(i => [137, 138, 139,279,280,281].includes(i.item) && i.stack_amount === 1);
              setEquippedRod(updatedRod || null);
            }).catch(console.error);
        }, response.data.duration_seconds * 1000);
      }
    } catch (error) {
      setIsFishing(false);
      setFishingAnimation(null);
      const errorMsg = error.response?.data?.detail || 'Fishing failed!';
      spawnText(errorMsg, playerPos.x, playerPos.y - 20, '#f87171');
    }
  };

  // Initialize mineral potion status when component mounts
  useEffect(() => {
    // Check mineral potion status
    api.get("/v1/mineral-potion-status", { withCredentials: true })
      .then(response => {
        if (response.data.active) {
          const currentTime = Math.floor(Date.now() / 1000);
          const remainingSeconds = Math.max(0, response.data.expiration - currentTime);

          // Set mineral potion state
          setMineralPotionActive(true);
          setMineralPotionExpire(response.data.expiration);
          setMineralPotionCountdown(remainingSeconds);

          // Set up interval to update countdown
          const interval = setInterval(() => {
            setMineralPotionCountdown(prev => {
              if (prev <= 1) {
                clearInterval(interval);
                setMineralPotionActive(false);
                setMineralPotionExpire(null);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

          // Store the interval reference for cleanup
          expireTO.current = interval;

          // Return cleanup function
          return () => {
            if (interval) {
              clearInterval(interval);
            }
          };
        }
      })
      .catch(error => {
        console.error('Error checking mineral potion status:', error);
      });
  }, []);

  // Handle Mineral Potion use with stacking support
  const handleMineralPotionUse = (expirationTimestamp) => {
    // Clear any existing countdown interval
    if (expireTO.current) {
      clearInterval(expireTO.current);
      expireTO.current = null;
    }

    // If there's an active potion, calculate remaining time to stack
    let newExpiration = expirationTimestamp;
    if (mineralPotionActive && mineralPotionExpire) {
      const currentTime = Math.floor(Date.now() / 1000);
      const remainingSeconds = Math.max(0, mineralPotionExpire - currentTime);

      // Stack the time by adding remaining seconds to the new expiration
      if (remainingSeconds > 0) {
        newExpiration = expirationTimestamp + remainingSeconds;
      }
    }

    setMineralPotionActive(true);
    setMineralPotionExpire(newExpiration);

    // Calculate remaining seconds
    const currentTime = Math.floor(Date.now() / 1000);
    const remainingSeconds = Math.max(0, newExpiration - currentTime);
    setMineralPotionCountdown(remainingSeconds);

    // Refresh rocks to show the mineral effect
    refreshRocks();

    // Set up interval to update countdown
    const interval = setInterval(() => {
      setMineralPotionCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setMineralPotionActive(false);
          setMineralPotionExpire(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Store the interval reference for cleanup
    expireTO.current = interval;

    // Return cleanup function
    return () => {
      if (expireTO.current) {
        clearInterval(expireTO.current);
        expireTO.current = null;
      }
    };
  };

  // Handle battle end
  const handleBattleEnd = (result) => {
    // If this was a Battle Tower battle and player won
    if (initialBattleData?.opponent_type === "battle_tower" && result?.winner === "player") {
      // Set victory state to trigger the post-battle flow
      setBattleTowerVictory(true);

      // Only show Magma Coin message if player actually earned one (first-time floor completion)
      if (result?.item_drop === "Magma Coin") {
        // Show victory message with Magma Coin
        spawnText(
          "Victory! Earned 1 Magma Coin!",
          playerPos.x,
          playerPos.y - 32,
          "gold",
          "/Magma Coin.png"
        );
      } else {
        // Regular victory message without Magma Coin for repeat clears
        spawnText(
          "Victory!",
          playerPos.x,
          playerPos.y - 32,
          "gold"
        );
      }

      // Set a timeout to close the battle and show teleport message
      setTimeout(() => {
        setInBattle(false);
        setInitialBattleData(null);

        // Show teleport message
        spawnText(
          `Teleporting to floor ${battleTowerFloor + 1} in 3 seconds...`,
          playerPos.x,
          playerPos.y - 16,
          "cyan"
        );

        // Set another timeout to navigate to the next floor
        setTimeout(() => {
          // Reset victory state
          setBattleTowerVictory(false);

          // Navigate to the next floor (this would typically be handled by the BattleTower component)
          if (config.onBattleVictory) {
            // Check if this is Maxie (floor 100) to pass boss parameter
            const boss = battleTowerFloor === 100 ? 'maxie' : null;
            config.onBattleVictory(battleTowerFloor, boss);
          }
        }, 3000);
      }, 1000);
    } else if (initialBattleData?.opponent_type === "battle_tower" && result?.winner === "foe") {

      setInBattle(false);
      setInitialBattleData(null);

      spawnText(
        "Defeat! Resetting to checkpoint...",
        playerPos.x,
        playerPos.y - 16,
        "red"
      );

      // Call the Battle Tower's loss handler if available
      if (config.onBattleLoss) {
        const floor = result?.floor || initialBattleData?.battle_tower_floor || 1;
        config.onBattleLoss(floor);
      }
    } else {
      const isZevionWin =
        initialBattleData?.opponent_type === "trainer" &&
        result?.winner === "player" &&
        Number(initialBattleData?.trainer_id) === 19 &&
        Number(config?.id) === 101;

      // For non-Battle Tower battles, close the battle screen
      setWildPokemon((wp) => {
        const filtered = wp.filter(p => p.id !== battleSpawnId);
        if (
          initialBattleData?.opponent_type === "trainer" &&
          result?.winner === "player" &&
          String(initialBattleData?.trainer_name || "").toLowerCase().includes("team rocket")
        ) {
          return filtered.filter(p => !p?.is_team_rocket);
        }
        return filtered;
      });
      setInBattle(false);
      setInitialBattleData(null);

      if (isZevionWin) {
        spawnText(
          "Water Badge obtained!",
          playerPos.x,
          playerPos.y - 32,
          "#60a5fa",
          "/WaterBadge.png"
        );

        setTimeout(() => {
          setActiveNPC({
            id: "watergym-zevion-victory",
            name: "Zevion",
            dialogue:
              "Congratulations, Trainer! You fought with honor and skill. Take this Water Badge as proof of your victory.",
            buttonText: "Close",
            secondaryButtonText: "View Badges",
            onSecondary: () => {
              setAchievementsModalOpen(true);
              setDialogueModalOpen(false);
            },
          });
          setDialogueModalOpen(true);
        }, 700);
      }
    }
  };

  // Handle NPC interactions
  const handleNPCClick = async (npc) => {
    if (config.onNPCClick && (npc.type === "grunt" || npc.type === "maxie" || npc.type === "groudon" || npc.type === "minute" || npc.type === "endless")) {
      // For Battle Tower NPCs, trigger battle directly
      if (npc.type === "grunt" || npc.type === "minute" || npc.type === "endless") {
        try {
          // Check if player has any Pokemon with HP > 0
          const r = await api.get("/v1/roster", { withCredentials: true });
          const anyAlive = r.data.some((p) => p.current_hp > 0);
          if (!anyAlive) {
            spawnText(
              "All your Pokémon are fainted! You can't battle.",
              playerPos.x,
              playerPos.y - 16,
              "red"
            );
            return;
          }

          // Get the floor from the config or default to 1
          const floor = config.floor || 1;

          // Initialize battle with the backend, similar to trainer battles
          try {
            // Determine tower type based on NPC type
            let tower_type = "original"; // default
            if (npc.type === "minute") {
              tower_type = "60second";
            } else if (npc.type === "endless") {
              tower_type = "endless";
            }

            const res = await api.post(
              "/v1/battle/start",
              {
                opponent_type: "battle_tower",
                battle_tower_floor: floor,
                tower_type: tower_type
              },
              { withCredentials: true }
            );

            // Set battle data from backend response and trigger battle screen
            setInitialBattleData({
              ...res.data,
              opponent_type: "battle_tower"
            });
            setBattleTowerFloor(floor);
            setInBattle(true);
            return;
          } catch (err) {
            console.error("Failed to start Battle Tower battle:", err);
            const msg = err.response?.data?.detail || 
                       err.response?.data?.message || 
                       "Can't battle right now!";
            spawnText(msg, playerPos.x, playerPos.y - 16, "red");
            return;
          }
        } catch (err) {
          console.error("Error checking roster for Battle Tower battle:", err);
          spawnText("Error starting battle", playerPos.x, playerPos.y - 16, "red");
          return;
        }
      }else if(npc.type === "maxie"){
        try {
          // Check if player has any Pokemon with HP > 0
          const r = await api.get("/v1/roster", { withCredentials: true });
          const anyAlive = r.data.some((p) => p.current_hp > 0);
          if (!anyAlive) {
            spawnText(
              "All your Pokémon are fainted! You can't battle.",
              playerPos.x,
              playerPos.y - 16,
              "red"
            );
            return;
          }

          // Get the floor from the config or default to 1
          const floor = config.floor || 1;

          // Initialize battle with the backend, similar to trainer battles
          try {
            // Determine tower type based on NPC type
            let tower_type = "original"; // default
            if (npc.type === "minute") {
              tower_type = "60second";
            } else if (npc.type === "endless") {
              tower_type = "endless";
            }

            const res = await api.post(
              "/v1/battle/start",
              {
                opponent_type: "battle_tower",
                battle_tower_floor: floor,
                tower_type: tower_type
              },
              { withCredentials: true }
            );

            // Set battle data from backend response and trigger battle screen
            setInitialBattleData({
              ...res.data,
              opponent_type: "battle_tower"
            });
            setBattleTowerFloor(floor);
            setInBattle(true);
            return;
          } catch (err) {
            console.error("Failed to start Battle Tower battle:", err);
            const msg = err.response?.data?.detail || 
                       err.response?.data?.message || 
                       "Can't battle right now!";
            spawnText(msg, playerPos.x, playerPos.y - 16, "red");
            return;
          }
        } catch (err) {
          console.error("Error checking roster for Battle Tower battle:", err);
          spawnText("Error starting battle", playerPos.x, playerPos.y - 16, "red");
          return;
        }
      } else {
        // For other Battle Tower NPCs (maxie, groudon), use the custom handler
        config.onNPCClick(npc);
        return;
      }
    }

    if (npc?.trainer_id) {
      try {
        const st = await api.get(`/v1/special-trainers/${npc.trainer_id}/state`, { withCredentials: true });
        const data = st?.data;
        const defeated = Boolean(data?.defeated);
        setActiveNPC({
          ...npc,
          dialogue: defeated ? (data?.dialogue_after || npc.dialogue) : (data?.dialogue_before || npc.dialogue),
          buttonText: "Close",
          secondaryButtonText: defeated ? null : "Battle",
          onSecondary: defeated ? null : () => startTrainerBattle(npc.trainer_id, npc.name || "Trainer"),
        });
        setDialogueModalOpen(true);
        return;
      } catch (e) {
        setActiveNPC({
          ...npc,
          buttonText: "Close",
          secondaryButtonText: "Battle",
          onSecondary: () => startTrainerBattle(npc.trainer_id, npc.name || "Trainer"),
        });
        setDialogueModalOpen(true);
        return;
      }
    }
    // If external handler is provided, call it first
    if (onNpcInteraction && npc.id) {
      onNpcInteraction(npc.id, playerPos);
    }
    
    // If user has Ring of Department equipped, show a market emoji for shop-related NPCs
    
    if (hasDepartmentRing && (
      // Check for shop-related modal types
      npc.modalType === 'auctionhouse' || 
      npc.modalType === 'playerShopLot' || 
      npc.modalType === 'playerShopBrowse' || 
      npc.modalType === 'playerShopManage' ||
      // Check for shop-related NPC types
      npc.type === 'shop' ||
      npc.type === 'promo' ||
      npc.type === 'craft' ||
      npc.type === 'event' ||
      npc.type === 'trainer-potion' ||
      npc.type === 'trainer-ev' ||
      npc.type === 'tm-trainer'
    )) {
      // Create a market emoji floating text
      const id = Date.now() + Math.random();
      setFloatingTexts((ft) => [
        ...ft,
        { id, text: '🛒', x: npc.x || playerPos.x, y: (npc.y || playerPos.y) - 20, color: '#ffd700', created: Date.now() },
      ]);
      setTimeout(() => {
        setFloatingTexts((ft) => ft.filter((f) => f.id !== id));
      }, 2500);
    }
    
    if (npc.modalType) {
      // Handle modal-opening NPCs
      setActiveNPC(npc);
      switch (npc.modalType) {
        case 'dialogue':
          setDialogueModalOpen(true);
          break;
        case 'raffle':
          setRaffleModalOpen(true);
          break;
        case 'luckyDip':
          setLuckyDipModalOpen(true);
          break;
        case 'collection-contest':
          setCollectionContestModalOpen(true);
          break;
        case 'fishing-missions':
          setFishingMissionsModalOpen(true);
          break;
        case 'farming-missions':
          setFarmingMissionsModalOpen(true);
          break;
        case 'auctionhouse':
          setAuctionHouseModalOpen(true);
          break;
        case 'racetrack':
          setRacetrackModalOpen(true);
          break;
        case 'wheeloffortune':
          setWheelOfFortuneModalOpen(true);
          break;
          case 'diglettdash':
            setDiglettDashModalOpen(true);
            break;
        case 'ballon-pop':
          setBalloonPopModalOpen(true);
          break;
        case 'ticket-exchange':
          setTicketExchangeModalOpen(true);
          break;
        case 'playerShopLot':
          // For empty lots, open the lot purchase modal
          setSelectedLotId(npc.lotId);
          setPlayerShopLotModalOpen(true);
          break;
        case 'playerShopBrowse':
          // For other players' shops, open the browse modal
          setSelectedLotId(npc.lotId);
          setSelectedShopName(npc.name);
          setPlayerShopBrowseModalOpen(true);
          break;
        case 'playerShopManage':
          setSelectedLotId(npc.lotId);
          setPlayerShopManageModalOpen(true);
          break;
        case 'island':
          setIslandShopModalOpen(true);
          break;
        case 'fishing-shop':
          setShowFishingShop(true);
          break;
        case 'berry-shop':
          setActiveBerryShopPlacementId(Number(npc?.shop_placement_id) || null);
          setShowBerryShop(true);
          break;
        case 'berry-fusion':
          setActiveBerryFusionStationId(String(npc?.id || ""));
          setShowBerryFusion(true);
          break;
        case 'researcher':
          if (npc.disabled) {
            spawnText('Research building needs power!', playerPos.x, playerPos.y - 16, 'red');
            return;
          }
          setSelectedResearchPlacementId(Number(npc?.research_placement_id) || null);
          setResearcherModalOpen(true);
          break;
        default:
          break;
      }
    } else if (npc.dialogue) {
      // Simple dialogue NPCs
      setActiveNPC(npc);
      setDialogueModalOpen(true);
    } else {
      // Custom action NPCs
      if (npc.action && typeof npc.action === 'function') {
        npc.action();
      } else {
        // Default behavior - spawn floating text
        spawnText(npc.defaultText || "Hello there!", playerPos.x, playerPos.y - 20, "#ffffff");
      }
    }
  };

  // Helper to map ball item IDs to CSS class names
  const getBallClass = (ballItemId) => {
    switch (Number(ballItemId)) {
      case POKEBALL_ITEM_ID: return 'pokeball';
      case GREATBALL_ITEM_ID: return 'greatball';
      case ULTRABALL_ITEM_ID: return 'ultraball';
      case LONGBALL_ITEM_ID: return 'longball';
      case RANGEBALL_ITEM_ID: return 'rangeball';
      case MUDBALL_ITEM_ID: return 'mudball';
      case RUBYBALL_ITEM_ID: return 'rubyball';
      case SAPPHIREBALL_ITEM_ID: return 'sapphireball';
      case EMERALDBALL_ITEM_ID: return 'emeraldball';
      case GOLDENBALL_ITEM_ID: return 'goldenball';
      case DIAMONDBALL_ITEM_ID: return 'diamondball';
      case SHADOWBALL_ITEM_ID: return 'shadowball';
      case MASTERBALL_ITEM_ID: return 'masterball';
      default: return 'pokeball';
    }
  };

  // —— Teleport Detection ——
  const checkTeleport = useCallback((x, y) =>
    teleportZones?.find(zone =>
      x < zone.x + zone.width &&
      x + 32 > zone.x &&
      y < zone.y + zone.height &&
      y + 32 > zone.y
    ),
  [teleportZones]);

  const handleTeleport = useCallback((targetFloor, targetX, targetY) => {
    // Store target coordinates in localStorage for the destination map
    localStorage.setItem('teleportTarget', JSON.stringify({ x: targetX, y: targetY }));
    // Navigate to target map
    const targetMapName = targetFloor.charAt(0).toUpperCase() + targetFloor.slice(1);
    navigate(`/map/${targetMapName}`);
  }, [navigate]);

  const checkTeleportRef = useRef(checkTeleport);
  useEffect(() => {
    checkTeleportRef.current = checkTeleport;
  }, [checkTeleport]);

  const handleTeleportRef = useRef(handleTeleport);
  useEffect(() => {
    handleTeleportRef.current = handleTeleport;
  }, [handleTeleport]);


  // —— Helpers: sort inventory so lures come last, etc. —— 
  const sortedInv = [...inventory].sort((a, b) => {
    const isL = (id) => [6,7,8,9,10,234, 235,167, 214,116,117, 701780, 701781].includes(id);
    return isL(a.item) === isL(b.item) ? 0 : isL(a.item) ? 1 : -1;
  });

  function clearStickSeq() {
    stickSeqTimeoutsRef.current.forEach((id) => clearTimeout(id));
    stickSeqTimeoutsRef.current = [];
  }
  
  // Keep latest player position in a ref for wandering/callbacks
  useEffect(() => {
    playerPosRef.current = playerPos;
  }, [playerPos]);
  
  // Collision check against collisionAreas - wrapped in useCallback to maintain stable reference
  const isColliding = React.useCallback((x, y) => {
    const pw = 32,
      ph = 32;
    return collisionAreas.some((w) => {
      const padX = w.padX ?? 0;
      const padY = w.padY ?? 0;
      return shrinkRectOverlap(
        x, y, pw, ph,
        w.x + padX, w.y + padY, w.width - padX * 2, w.height - padY * 2
      );
    });
  }, [collisionAreas]);

  const isCollidingWithDecor = useCallback(
    (x, y) => {
      if (!isMyIsland) return false;

      const px = Number(x) || 0;
      const py = Number(y) || 0;

      // Use a smaller "feet" hitbox for decor collision so the player can walk closer
      // when approaching an object from the bottom (more classic top-down feel).
      const footX = px + 8;
      const footY = py + 20;
      const footW = 16;
      const footH = 12;

      return (decorPlacements || []).some((p) => {
        const ox = Number(p?.x) || 0;
        const oy = Number(p?.y) || 0;
        const cx = Number(p?.collision_x) || 0;
        const cy = Number(p?.collision_y) || 0;
        const cw = Number(p?.collision_w) || 0;
        const ch = Number(p?.collision_h) || 0;
        if (cw <= 0 || ch <= 0) return false;
        return shrinkRectOverlap(footX, footY, footW, footH, ox + cx, oy + cy, cw, ch, 0, 0, 0, 0);
      });
    },
    [decorPlacements, isMyIsland]
  );

  const isPokemonCollidingWithDecor = useCallback(
    (x, y) => {
      if (!isMyIsland) return false;

      const px = Number(x) || 0;
      const py = Number(y) || 0;

      const bx = px + 8;
      const by = py + 8;
      const bw = 16;
      const bh = 16;

      return (decorPlacements || []).some((p) => {
        const ox = Number(p?.x) || 0;
        const oy = Number(p?.y) || 0;
        const cx = Number(p?.collision_x) || 0;
        const cy = Number(p?.collision_y) || 0;
        const cw = Number(p?.collision_w) || 0;
        const ch = Number(p?.collision_h) || 0;
        if (cw <= 0 || ch <= 0) return false;
        return shrinkRectOverlap(bx, by, bw, bh, ox + cx, oy + cy, cw, ch, 0, 0, 0, 0);
      });
    },
    [decorPlacements, isMyIsland]
  );
  
  // Keep latest collision function in a ref to avoid stale closures in intervals
  const isCollidingRef = useRef(isColliding);
  useEffect(() => {
    isCollidingRef.current = isColliding;
  }, [isColliding]);

  // Keep latest decor collision fn in a ref so wanderPokemons can respect placed decor
  const isCollidingWithDecorRef = useRef(isCollidingWithDecor);
  useEffect(() => {
    isCollidingWithDecorRef.current = isCollidingWithDecor;
  }, [isCollidingWithDecor]);

  const isPokemonCollidingWithDecorRef = useRef(isPokemonCollidingWithDecor);
  useEffect(() => {
    isPokemonCollidingWithDecorRef.current = isPokemonCollidingWithDecor;
  }, [isPokemonCollidingWithDecor]);
  
  // —— D-Pad movement (keyboard-style) ——
  const lastDPadMoveRef = useRef(0);
  const handleDPad = React.useCallback(({ x, y }) => {
    // Throttle D-pad input to prevent too-fast movement
    const now = Date.now();
    if (now - lastDPadMoveRef.current < 150) {
      return; // Ignore if less than 150ms since last move
    }
    
    const moving = Math.abs(x) >= 0.1 || Math.abs(y) >= 0.1;
    if (!moving) {
      setPlayerStep(0);
      return;
    }

    lastDPadMoveRef.current = now;

    let nx = playerPos.x;
    let ny = playerPos.y;
    let dir = direction;

    // Move by full TILE_SIZE like keyboard
    if (Math.abs(x) > Math.abs(y)) {
      dir = x > 0 ? "right" : "left";
      nx += (x > 0 ? 1 : -1) * TILE_SIZE;
    } else {
      dir = y > 0 ? "down" : "up";
      ny += (y > 0 ? 1 : -1) * TILE_SIZE;
    }

    nx = clamp(nx, 0, MAP_WIDTH - 32);
    ny = clamp(ny, 0, MAP_HEIGHT - 32);

    const hitRock = !hasGlideBoots && rocks
      .filter((r) => !r.collected)
      .some((r) => rockOverlap(nx, ny, r.x, r.y));
    const hitWall = isColliding(nx, ny) || isCollidingWithDecor(nx, ny);
    
    const anyCapturing = wildPokemon.some(p => p.capturing);
    const anyMining = Object.keys(breakingFrames).length > 0;
    
    const hitPokemon = !anyCapturing && !anyMining && wildPokemon.some(p => {
      const dist = Math.hypot(nx - p.x, ny - p.y);
      return dist < 24;
    });

    const hitTrainer = trainerStates.some(trainer => {
      const isStatic = Number(trainer.wander) === 0;
      const trainerWidth = isStatic ? 16 : 32;
      const trainerHeight = isStatic ? 24 : 32;
      const tPadX = isStatic ? 1 : 4;
      const tPadY = isStatic ? 1 : 4;
      
      return shrinkRectOverlap(
        nx, ny, 32, 32,
        trainer.x, trainer.y, trainerWidth, trainerHeight,
        8, 8, tPadX, tPadY
      );
    });
    
    const hitNPC = mapNpcs && mapNpcs.some(npc => {
      const npcWidth = Number(npc.width || 16);
      const npcHeight = Number(npc.height || 24);
      return shrinkRectOverlap(
        nx, ny, 32, 32,
        npc.x, npc.y, npcWidth, npcHeight,
        8, 8, 4, 4
      );
    });
    
    if (!hitRock && !hitWall && !hitPokemon && !hitTrainer && !hitNPC) {
      const teleportZone = checkTeleport(nx, ny);
      if (teleportZone) {
        handleTeleport(teleportZone.targetFloor, teleportZone.targetX, teleportZone.targetY);
        return;
      }
      
      setPlayerPos({ x: nx, y: ny });
      setDirection(dir);
      
      if (onPlayerPositionChange) {
        onPlayerPositionChange({ x: nx, y: ny });
      }
      
      // Animate walk cycle
      const walkSeq = [1, 2, 0];
      walkSeq.forEach((step, i) =>
        setTimeout(() => setPlayerStep(step), i * 150)
      );
    }
  }, [playerPos, direction, rocks, isColliding, isCollidingWithDecor, wildPokemon, breakingFrames, hasGlideBoots, trainerStates, mapNpcs, checkTeleport, handleTeleport, onPlayerPositionChange, TILE_SIZE, MAP_WIDTH, MAP_HEIGHT]);

  // —— Analog‐stick movement —— 
  // Using useCallback to preserve the function reference across renders
  // This prevents the function from losing context during capture animations
  const handleStick = React.useCallback(({ x, y }) => {
    const TH = 0.1,
      speed = 0.2;
    const moving = Math.abs(x) >= TH || Math.abs(y) >= TH;

    if (!moving) {
      if (stickActiveRef.current) {
        stickActiveRef.current = false;
        clearStickSeq();
        walkDistanceRef.current = 0;
        setPlayerStep(0);
      }
      return;
    }

    const prevPos = playerPosRef.current || playerPos;
    let nx = prevPos.x,
      ny = prevPos.y,
      dir = direction;
    if (Math.abs(x) > Math.abs(y)) {
      dir = x > 0 ? "right" : "left";
      nx += (x > 0 ? 1 : -1) * TILE_SIZE * speed;
    } else {
      dir = y > 0 ? "down" : "up";
      ny += (y > 0 ? 1 : -1) * TILE_SIZE * speed;
    }
    nx = clamp(nx, 0, MAP_WIDTH - 32);
    ny = clamp(ny, 0, MAP_HEIGHT - 32);



    const hitRock = !hasGlideBoots && rocks
      .filter((r) => !r.collected)
      .some((r) => rockOverlap(nx, ny, r.x, r.y));
    const hitWall = isColliding(nx, ny) || isCollidingWithDecor(nx, ny);
    
    // Check for Pokemon collisions, but ONLY when not capturing or mining
    // This allows walking through Pokemon only during active capture/mining animations
    const anyCapturing = wildPokemon.some(p => p.capturing);
    const anyMining = Object.keys(breakingFrames).length > 0;
    
    // Only check Pokemon collisions if we're not capturing or mining
    const hitPokemon = !hasGlideBoots && !anyCapturing && !anyMining && wildPokemon.some(p => {
      const dist = Math.hypot(nx - p.x, ny - p.y);
      return dist < 12; // Collision radius with Pokemon
    });

    // Check for trainer collisions - prevent walking through trainers
    const hitTrainer = trainerStates.some(trainer => {
      // For static trainers (wander=0), use 16x24 dimensions
      const isStatic = Number(trainer.wander) === 0;
      const trainerWidth = isStatic ? 16 : 32;
      const trainerHeight = isStatic ? 24 : 32;
      const tPadX = isStatic ? 1 : 4;
      const tPadY = isStatic ? 1 : 4;
      
      // Use shrinkRectOverlap for precise collision detection
      return shrinkRectOverlap(
        nx, ny, 32, 32,
        trainer.x, trainer.y, trainerWidth, trainerHeight,
        8, 8, tPadX, tPadY // Add some padding to make collision feel natural
      );
    });
    
    // Check for NPC collisions - prevent walking through NPCs
    const hitNPC = mapNpcs && mapNpcs.some(npc => {
      const npcWidth = Number(npc.width || 16);
      const npcHeight = Number(npc.height || 24);
      return shrinkRectOverlap(
        nx, ny, 32, 32, // Player hitbox
        npc.x, npc.y, npcWidth, npcHeight, // NPC hitbox
        8, 8, 4, 4 // Padding for natural feel
      );
    });
    
    // Check for berry plant collisions - prevent walking through plants and empty slots
    let hitBerryPlant = false;
    if (MAP_ID === 1 || MAP_ID === MY_ISLAND_MAP_ID) {
      // Check planted berries
      hitBerryPlant = berryPlants.some(plant => {
        return shrinkRectOverlap(
          nx, ny, 32, 32, // Player hitbox
          plant.x, plant.y, 16, 16, // Berry plant hitbox (16x16)
          8, 8, 4, 4 // Padding for natural feel
        );
      });
      
      // Also check empty planting spots (all 12 positions)
      if (!hitBerryPlant) {
        const occupiedSlots = berryPlants.map(p => p.slot_index);

        if (MAP_ID === 1) {
          hitBerryPlant = BERRY_PLANT_POSITIONS.some(([x, y], index) => {
            if (occupiedSlots.includes(index)) return false;
            return shrinkRectOverlap(nx, ny, 32, 32, x, y, 16, 16, 8, 8, 4, 4);
          });
        } else {
          const entries = Object.entries(soilbedSlotPositions || {});
          hitBerryPlant = entries.some(([slotStr, xy]) => {
            const idx = Number(slotStr);
            if (occupiedSlots.includes(idx)) return false;
            const x = Number(xy?.[0]) || 0;
            const y = Number(xy?.[1]) || 0;
            return shrinkRectOverlap(nx, ny, 32, 32, x, y, 16, 16, 8, 8, 4, 4);
          });
        }
      }
    }
    
    if (!hitRock && !hitWall && !hitPokemon && !hitTrainer && !hitNPC && !hitBerryPlant) {
      // Check for teleport before updating position
      const teleportZone = checkTeleport(nx, ny);
      if (teleportZone) {
        handleTeleport(teleportZone.targetFloor, teleportZone.targetX, teleportZone.targetY);
        return;
      }
      
      // Only move if we don't hit any obstacle (wall, rock, or Pokemon)
      // Unless we're currently capturing or mining, in which case Pokemon collisions are ignored
      setPlayerPos({ x: nx, y: ny });
      setDirection(dir);
      
      // Notify parent component about position change if callback exists
      if (onPlayerPositionChange) {
        onPlayerPositionChange({ x: nx, y: ny });
      }
      
      const dist = Math.hypot(Number(nx) - Number(prevPos.x), Number(ny) - Number(prevPos.y));
      if (Number.isFinite(dist) && dist > 0) {
        walkDistanceRef.current += dist;
      }

      const STEP_DISTANCE = TILE_SIZE / 2;
      if (walkDistanceRef.current >= STEP_DISTANCE) {
        walkDistanceRef.current = 0;
        setPlayerStep((prev) => {
          const p = Number(prev) || 0;
          if (p === 1) return 2;
          return 1;
        });
      } else if (!stickActiveRef.current) {
        setPlayerStep((prev) => (Number(prev) === 0 ? 1 : prev));
      }

      stickActiveRef.current = true;
    }
  }, [playerPos, direction, rocks, isColliding, isCollidingWithDecor, TILE_SIZE, MAP_WIDTH, MAP_HEIGHT, wildPokemon, breakingFrames, hasGlideBoots, trainerStates, effectiveNpcs, checkTeleport, handleTeleport, onPlayerPositionChange]);

  const handlePlaceDecorAt = useCallback(
    async (worldX, worldY) => {
      if (!placingDecor || !selectedDecor) return;
      const decorId = Number(selectedDecor?.decor_id);
      if (!decorId) return;

      try {
        const res = await api.post(
          "/v1/island/decor/place",
          {
            decor_id: decorId,
            x: Math.round(Number(worldX) || 0),
            y: Math.round(Number(worldY) || 0),
            map_id: MY_ISLAND_MAP_ID,
          },
          { withCredentials: true }
        );

        const placed = res?.data?.placement;
        if (placed) {
          setDecorPlacements((prev) => [...(prev || []), placed]);
          spawnText?.("Placed!", placed.x, placed.y - 12, "#4ade80");

          const key = String(placed?.decor_key || "");
          if (key === "pond") {
            refreshFishingSpots();
          }
          if (key === "soilbed") {
            refreshBerryPlants();
          }
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || "Cannot place there";
        spawnText?.(msg, Number(worldX) || 0, (Number(worldY) || 0) - 12, "#f87171");
      }
    },
    [placingDecor, selectedDecor, spawnText, refreshFishingSpots, refreshBerryPlants]
  );

  const decorSnap = useCallback((v) => {
    const n = Number(v) || 0;
    return Math.round(n / 8) * 8;
  }, []);

  const handleRemovePlacement = useCallback(
    async (placement) => {
      const placementId = Number(placement?.placement_id);
      if (!placementId) return;
      try {
        await api.delete(`/v1/island/decor/remove/${placementId}`, {
          params: { map_id: MY_ISLAND_MAP_ID },
          withCredentials: true,
        });
        setDecorPlacements((prev) => (prev || []).filter((p) => Number(p?.placement_id) !== placementId));

        const key = String(placement?.decor_key || "");
        if (key === "pond") {
          refreshFishingSpots();
        }
        if (key === "soilbed") {
          refreshBerryPlants();
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || "Failed to remove";
        spawnText?.(msg, playerPos?.x || 0, (playerPos?.y || 0) - 16, "#f87171");
      }
    },
    [spawnText, playerPos, refreshFishingSpots, refreshBerryPlants]
  );

  // ESC key handler for move mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (decorMoveMode) {
          setDecorMoveMode(false);
          setSelectedMovePlacementId(null);
          setMovingDecor(null);
          setMouseGhostPos(null);
        }
        if (decorRemoveMode) {
          setDecorRemoveMode(false);
          setSelectedRemovalPlacementId(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [decorMoveMode, decorRemoveMode]);

  const handleMoveDecorAt = useCallback(
    async (worldX, worldY) => {
      if (!movingDecor || !selectedMovePlacementId) return;
      
      const snappedX = decorSnap(worldX);
      const snappedY = decorSnap(worldY);
      
      try {
        const res = await api.post(
          "/v1/island/decor/move",
          {
            placement_id: selectedMovePlacementId,
            x: snappedX,
            y: snappedY,
            map_id: MY_ISLAND_MAP_ID,
          },
          { withCredentials: true }
        );

        const updated = res?.data?.placement;
        if (updated) {
          setDecorPlacements((prev) => 
            (prev || []).map((p) => 
              Number(p?.placement_id) === selectedMovePlacementId ? updated : p
            )
          );
          spawnText?.("Moved!", snappedX, snappedY - 12, "#4ade80");
          setSelectedMovePlacementId(null);
          setMovingDecor(null);
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || "Cannot move there";
        spawnText?.(msg, snappedX, snappedY - 12, "#f87171");
      }
    },
    [movingDecor, selectedMovePlacementId, decorSnap, spawnText]
  );

  // This comment is left intentionally to mark where the duplicate isColliding function was removed

  

  // —— Recompute scale on window resize —— 
  useEffect(() => {
    function updateScale() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setViewportSize({ width: w, height: h });

      const availableWidth = w;
      const availableHeight = Math.max(0, h - CONTROLS_ROW_HEIGHT_PX);

      const scaleX = availableWidth / VIEWPORT_WIDTH;
      const scaleY = availableHeight / VIEWPORT_HEIGHT;
      const isMobile = availableWidth <= 768;
     const targetScale = isMobile ? Math.max(2.0, scaleX) : Math.min(scaleX, scaleY);
 
      setMapScale(clamp(targetScale, 1, 3));
    }
    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [VIEWPORT_WIDTH, VIEWPORT_HEIGHT, CONTROLS_ROW_HEIGHT_PX]);


  useEffect(() => {
  api
    .get(`/v1/maps/${MAP_ID}/trainers`, { withCredentials: true })
    .then(r => setTrainers(r.data))
    .catch(console.error);
}, [MAP_ID]);

useEffect(() => {
    const base = (Array.isArray(trainers) ? trainers : []).map((t) => ({
      ...t,
      dirIndex: 0,      // 0=right,1=down,2=left,3=up
      moved:    0,      // px moved along current side
      stepFrame: 0      // animation frame (0–2)
    }));

    if (!isMyIsland) {
      setTrainerStates(base);
      return;
    }

    const ownedPlaced = (Array.isArray(ownedTrainerNpcs) ? ownedTrainerNpcs : [])
      .filter((o) => Boolean(o?.placed) && o?.x != null && o?.y != null)
      .map((o) => ({
        ...o,
        trainer_id: Number(o?.trainer_id) || 0,
        trainer_name: o?.trainer_name,
        sprite_sheet: o?.sprite_sheet,
        x: Number(o?.x) || 0,
        y: Number(o?.y) || 0,
        wander: 0,
        dirIndex: 0,
        moved: 0,
        stepFrame: 0,
      }))
      .filter((o) => Number(o?.trainer_id) > 0);

    const ownedGameCornerPlaced = (Array.isArray(ownedGameCornerNpcs) ? ownedGameCornerNpcs : [])
      .filter((o) => Boolean(o?.placed) && o?.x != null && o?.y != null)
      .map((o) => ({
        ...o,
        trainer_id: Number(o?.npc_id) || 0,
        trainer_name: o?.npc_name,
        sprite_sheet: o?.sprite_sheet,
        x: Number(o?.x) || 0,
        y: Number(o?.y) || 0,
        wander: 0,
        dirIndex: 0,
        moved: 0,
        stepFrame: 0,
        isGameCornerNpc: true,
      }))
      .filter((o) => Number(o?.trainer_id) > 0);

    setTrainerStates([...base, ...ownedPlaced, ...ownedGameCornerPlaced]);
  }, [trainers, isMyIsland, ownedTrainerNpcs, ownedGameCornerNpcs]);

  // Function to refresh equipped items
  const refreshEquippedItems = useCallback(async () => {
    try {
      const [invRes, skillRes] = await Promise.all([
        api.get("/v1/inventory", { withCredentials: true }),
        api.get("/v1/skilltree/state", { withCredentials: true }).catch(() => null),
      ]);
      const allItems = invRes?.data || [];

      const glideBoots = allItems.find((i) => i.item === 172 && ((i.stack_amount || 0) > 0));
      setHasGlideBoots(!!glideBoots);

      const shinyBonus = skillRes?.data?.modifiers?.shiny_bonus;
      setShinySkillBonus(typeof shinyBonus === 'number' ? shinyBonus : 0);

      const rodBonus = skillRes?.data?.modifiers?.rod_durability_bonus;
      setRodDurabilityBonus(typeof rodBonus === 'number' ? rodBonus : 0);

      const pe = skillRes?.data?.modifiers?.pickaxe_efficiency;
      setPickaxeEfficiency(typeof pe === 'number' ? pe : 0);

      // Find shiny charm (item ID 55)
      const charm = allItems.find(
        (i) => i.item === 194 && i.stack_amount > 0
      );
      setShinyCharm(charm || null);

      // Find equipped pickaxe (items 228-233)
      const pickaxe = allItems.find(
        (i) =>
          [228, 229, 230, 231, 232, 233].includes(i.item) &&
          i.stack_amount === 1
      );
      setEquippedPickaxe(pickaxe || null);

      // Mining Charm stacks (item 121)
      const mc = allItems.find((i) => i.item === 121);
      const stacks = Math.max(0, Math.min(10, Number(mc?.stack_amount || 0)));
      setMiningCharmStacks(Number.isFinite(stacks) ? stacks : 0);

      // Find equipped fishing rod (Old Rod 137, Good Rod 138, or Super Rod 139)
      const rod = allItems.find(
        (i) => [137, 138, 139,279,280,281].includes(i.item) && i.stack_amount === 1
      );
      setEquippedRod(rod || null);

      // Find equipped item finder (item ID 56)
      const itemFinder = allItems.find(
        (i) => i.item === 56 && i.stack_amount > 0
      );
      setEquippedItemFinder(itemFinder || null);
      
      // Find equipped catching gloves (item ID 113)
      const catchingGloves = allItems.find(
        (i) => i.item === 113 && i.stack_amount > 0
      );
      setEquippedCatchingGloves(catchingGloves || null);
      
      // Get all equipped rings (stack_amount === 1)
      const rings = allItems.filter(
        (i) => i.stack_amount === 1 && 
        // Ring item IDs: 28 (Equipment), 140 (Department), 161 (Health), and any other rings
        [28, 140, 161].includes(i.item)
      );
      setEquippedRings(rings);
      
      // Check for Equipment Ring (item ID 28)
      const equipmentRing = rings.find((i) => i.item === 28);
      setHasEquippedRing(!!equipmentRing);
      
      // Check for Ring of Department (item ID 140)
      const departmentRing = rings.find((i) => i.item === 140);
      setHasDepartmentRing(!!departmentRing);
    } catch (error) {
      console.error("Failed to refresh equipped items:", error);
    }
  }, []);

  // —— Fetch initial data: catch rates, inventory, lure status, rocks —— 
  useEffect(() => {
    api
      .get("/v1/catchrate", { withCredentials: true })
      .then((r) => setCatchRates(r.data))
      .catch(console.error);

    // Call the refresh function to load equipped items
    refreshEquippedItems();

    api
      .get("/v1/inventory", { withCredentials: true })
      .then((r) => {
        // filter inventory down to balls & lures, berries, etc.
        const filtered = r.data.filter((i) =>
          [2, 3, 4, 63, 141, 142, 143, 199,200,201,202,203,204,205,206,207,208,209,210, 234,167, 235, 116, 117, 6, 7, 8, 9, 10, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007,4008, 214, 701780, 701781].includes(i.item) &&
          i.amount > 0
        );
        setInventory(filtered);
        const saved = localStorage.getItem("selectedBall");
        if (saved) {
          const savedId = parseInt(saved, 10);
          const found = filtered.find((i) => i.item === savedId && i.amount > 0);
          if (found) {
            setSelectedBall(found);
          } 
        }
      }).catch(error => {
        // Removed console.error
      }).finally(() => {
        setInventoryLoading(false);
      });

    api
      .get("/v1/lure-status", { withCredentials: true })
      .then((r) => {
        if (r.data.lure) {
          setActiveLure("lure");
          setLureExpire(r.data.expiration);
        } else if (r.data.super_lure) {
          setActiveLure("super_lure");
          setLureExpire(r.data.expiration);
        } else if (r.data.ultra_lure) {
          setActiveLure("ultra_lure");
          setLureExpire(r.data.expiration);
        }
        setExpLureHits(r.data.exp_lure || 0);
        setDropLureHits(r.data.drop_lure || 0);
      })
      .catch(console.error);
// Check for Mineral Potion status using the new endpoint
api
.get("/v1/mineral-potion-status", { withCredentials: true })
.then((r) => {
  if (r.data.active) {
    const currentTime = Math.floor(Date.now() / 1000);
    const remainingSeconds = Math.max(0, r.data.expiration - currentTime);

    if (remainingSeconds > 0) {
      setMineralPotionActive(true);
      setMineralPotionExpire(r.data.expiration);
      setMineralPotionCountdown(remainingSeconds);

      // Set up interval to update countdown
      if (expireTO.current) {
        clearInterval(expireTO.current);
      }

      const interval = setInterval(() => {
        setMineralPotionCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setMineralPotionActive(false);
            setMineralPotionExpire(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      expireTO.current = interval;
    } else {
      setMineralPotionActive(false);
      setMineralPotionExpire(null);
      setMineralPotionCountdown(0);
    }
  } else {
    setMineralPotionActive(false);
    setMineralPotionExpire(null);
    setMineralPotionCountdown(0);
  }
})
.catch(error => {
  console.error('Error checking mineral potion status:', error);
  setMineralPotionActive(false);
  setMineralPotionExpire(null);
  setMineralPotionCountdown(0);
});

// Check for Shiny Potion status
api
.get("/v1/shiny-potion-status", { withCredentials: true })
.then((r) => {
  if (r.data.active) {
    const currentTime = Math.floor(Date.now() / 1000);
    const remainingSeconds = Math.max(0, r.data.expiration - currentTime);

    if (remainingSeconds > 0) {
      setShinyPotionActive(true);
      setShinyPotionExpire(r.data.expiration);
      setShinyPotionCountdown(remainingSeconds);

      // Set up interval to update countdown
      const shinyInterval = setInterval(() => {
        setShinyPotionCountdown(prev => {
          if (prev <= 1) {
            clearInterval(shinyInterval);
            setShinyPotionActive(false);
            setShinyPotionExpire(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Store interval ID for cleanup
      return () => clearInterval(shinyInterval);
    } else {
      setShinyPotionActive(false);
      setShinyPotionExpire(null);
      setShinyPotionCountdown(0);
    }
  } else {
    setShinyPotionActive(false);
    setShinyPotionExpire(null);
    setShinyPotionCountdown(0);
  }
})
.catch(error => {
  console.error('Error checking shiny potion status:', error);
  setShinyPotionActive(false);
  setShinyPotionExpire(null);
  setShinyPotionCountdown(0);
});
    api
      .get("/v1/rocks", { params: { map_id: MAP_ID }, withCredentials: true })
      .then((r) => setRocks(r.data.data || []))
      .catch(console.error);

    // Load berry plants on Island + My Island
    if (MAP_ID === 1 || MAP_ID === MY_ISLAND_MAP_ID) {
      refreshBerryPlants();
    }

      api
   .get("/v1/mining/info", { withCredentials: true })
   .then(r => {
     if (r.data.success) {
       setGems(r.data.resources);
     }
   })
   .catch(console.error);
  }, [MAP_ID]);


  useEffect(() => {
    if (localStorage.getItem("selectedRock") === "true") {
      setSelectedAction({ type: "item", payload: "rock" });
    }
  }, []);

  // Auto-refresh berry plants every 30 seconds on Island map
  useEffect(() => {
    if (MAP_ID === 1 || MAP_ID === MY_ISLAND_MAP_ID) {
      const interval = setInterval(() => {
        refreshBerryPlants();
      }, 30000); // 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [MAP_ID, refreshBerryPlants]);
  

  //
  // ── PERSIST “selectedBall” TO localStorage ────────────────────────────────────────
  //
  // 1) When `inventory` first arrives (or changes), check localStorage for a saved ball ID.
  //    If it’s still in inventory (amount>0), re‐apply it to state. Otherwise clear it.
  //
  useEffect(() => {
    if (inventoryLoading) return;
    const savedId = localStorage.getItem("selectedBall");
    if (savedId && inventory.length) {
      const match = inventory.find((i) => i.item.toString() === savedId);
  
     
      if (match && match.amount > 0) {
    
        if (!selectedBall || selectedBall.item !== match.item) {
          setSelectedBall(match);
          
        }
      } else {
        
      }
    }
  }, [inventory,inventoryLoading]);

  //
  // 2) Whenever selectedBall in state changes, write it (or remove) in localStorage.
  //
  useEffect(() => {
    if (selectedBall) {
      localStorage.setItem("selectedBall", selectedBall.item.toString());
    } 
  }, [selectedBall]);
  
  useEffect(() => {
    if (selectedBall) {
      setSelectedAction({ type: 'ball', payload: selectedBall });
    }
  }, [selectedBall]);

  //
  // 3) If inventory updates causes the selected‐ball’s amount to drop to 0 (e.g. just used the last one),
  //    we clear both the React state and the localStorage entry. We do that inside any place inventory changes:
  //
  //    – Already, we deduct one ball in capture(). So right after we do setInventory(…), we’ll inspect if
  //      that made selectedBall.amount === 0, and if so clear it. Below in capture() we’ll add that logic.
  //

  // —— Fetch gems when pouch opens —— 
  useEffect(() => {
    if (!showPouch) return;
    api
      .get("/v1/mining/info", { withCredentials: true })
      .then((r) => {
        if (r.data.success) {
          setGems(r.data.resources); 
        }
      })
      .catch(console.error);
  }, [showPouch]);

  // —— Lure countdown —— 
  useEffect(() => {
    if (!lureExpire) {
      setCountdown(0);
      return;
    }
    const iv = setInterval(() => {
      const rem = lureExpire - Math.floor(Date.now() / 1000);
      if (rem <= 0) {
        setActiveLure(null);
        setLureExpire(null);
        setCountdown(0);
        clearInterval(iv);
      } else {
        setCountdown(rem);
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [lureExpire]);

  // —— Pokemon wandering effect —— 
  useEffect(() => {
    const iv = setInterval(() => {
      const currentPlayer = playerPosRef.current;
      const collisionFn = isCollidingRef.current;
      const decorCollisionFn = isPokemonCollidingWithDecorRef.current;
      const combinedCollisionFn = (x, y) => {
        const hitWall = typeof collisionFn === "function" ? collisionFn(x, y) : false;
        const hitDecor = typeof decorCollisionFn === "function" ? decorCollisionFn(x, y) : false;
        return Boolean(hitWall || hitDecor);
      };
      setWildPokemon((prev) => {
        const next = wanderPokemons(prev, TILE_SIZE, MAP_WIDTH, MAP_HEIGHT, currentPlayer, MAP_ID === MY_ISLAND_MAP_ID ? combinedCollisionFn : collisionFn);
        if (MAP_ID !== MY_ISLAND_MAP_ID) return next;

        const px = Number(currentPlayer?.x) || 0;
        const py = Number(currentPlayer?.y) || 0;
        const aggroRange = 120;
        return (next || []).map((m) => {
          if (!Boolean(m?.aggressive)) return m;
          const dist = Math.hypot(px - (Number(m?.x) || 0), py - (Number(m?.y) || 0));
          if (dist <= aggroRange) {
            const original = (prev || []).find((p) => String(p?.id) === String(m?.id));
            return original || m;
          }
          return m;
        });
      });
    }, 250);
    return () => clearInterval(iv);
  }, [TILE_SIZE, MAP_WIDTH, MAP_HEIGHT, MAP_ID]);

  const lastAggroEngageAtRef = useRef(0);
  const lastTeamRocketEngageAtRef = useRef(0);

  useEffect(() => {
    if (MAP_ID !== MY_ISLAND_MAP_ID) return;

    const AGGRO_RANGE_PX = 120;
    const ENGAGE_RANGE_PX = 30;
    const COOLDOWN_MS = 4500;

    const iv = setInterval(() => {
      if (inBattle) return;
      if (isFishing) return;
      if (showPouch || showFishingShop || achievementsModalOpen || dialogueModalOpen) return;
      if (placingDecor || decorRemoveMode || showDecorPanel) return;

      const currentPlayer = playerPosRef.current;
      if (!currentPlayer) return;

      const mons = wildPokemonRef.current || [];
      const now = Date.now();

      const nearestAggro = mons
        .filter((m) => Boolean(m?.aggressive) && !m?.capturing && !(m?.id && m.id.toString().startsWith('fishing_')))
        .map((m) => ({
          mon: m,
          dist: Math.hypot((Number(currentPlayer.x) || 0) - (Number(m.x) || 0), (Number(currentPlayer.y) || 0) - (Number(m.y) || 0)),
        }))
        .sort((a, b) => a.dist - b.dist)[0];

      if (
        nearestAggro &&
        nearestAggro.dist <= ENGAGE_RANGE_PX &&
        now - (Number(lastAggroEngageAtRef.current) || 0) >= COOLDOWN_MS
      ) {
        lastAggroEngageAtRef.current = now;
        const target = nearestAggro.mon;

        // Check if all player Pokemon are fainted
        api
          .get("/v1/roster", { withCredentials: true })
          .then((rosterRes) => {
            const roster = rosterRes.data || [];
            const anyAlive = roster.some((p) => p.current_hp > 0);

            if (!anyAlive) {
              // All Pokemon fainted - deduct coins/money instead of battle
              api
                .post(
                  "/v1/battle/aggressive-penalty",
                  { map_id: MAP_ID },
                  { withCredentials: true }
                )
                .then((penaltyRes) => {
                  const lostCoins = penaltyRes.data?.lost_coins || 0;
                  const lostMoney = penaltyRes.data?.lost_money || 0;
                  
                  if (lostCoins > 0) {
                    spawnText(
                      `Lost ${lostCoins} coins to aggressive Pokémon!`,
                      currentPlayer.x,
                      currentPlayer.y - 16,
                      "#ef4444"
                    );
                    setGems((prev) => ({ ...prev, coins: Math.max(0, (prev.coins || 0) - lostCoins) }));
                  } else if (lostMoney > 0) {
                    spawnText(
                      `Lost $${lostMoney.toLocaleString()} to aggressive Pokémon!`,
                      currentPlayer.x,
                      currentPlayer.y - 16,
                      "#ef4444"
                    );
                  }
                })
                .catch((err) => {
                  console.error("Aggressive penalty error:", err);
                  spawnText(
                    "Escaped from aggressive Pokémon!",
                    currentPlayer.x,
                    currentPlayer.y - 16,
                    "#fbbf24"
                  );
                });
              return;
            }

            // At least one Pokemon alive - start battle normally
            api
              .post(
                "/v1/battle/start",
                {
                  opponent_type: "wild",
                  spawn_id: target.id,
                  map_id: MAP_ID,
                  engagement: "aggro",
                },
                { withCredentials: true }
              )
              .then((res) => {
                setBattleSpawnId(target.id);
                setInitialBattleData({ ...res.data, opponent_type: "wild", engagement: "aggro" });
                setInBattle(true);
              })
              .catch((err) => {
                const msg = err.response?.data?.detail || err.response?.data?.message || "Can't battle right now!";
                spawnText(msg, currentPlayer.x, currentPlayer.y - 16, "red");
              });
          })
          .catch((err) => {
            console.error("Failed to check roster:", err);
          });

        return;
      }

      const wallFn = isCollidingRef.current;
      const decorFn = isPokemonCollidingWithDecorRef.current;
      const isBlocked = (x, y) => {
        const hitWall = typeof wallFn === "function" ? wallFn(x, y) : false;
        const hitDecor = typeof decorFn === "function" ? decorFn(x, y) : false;
        return Boolean(hitWall || hitDecor);
      };

      const hr = new Date().getHours();
      const isNight = hr < 6 || hr >= 18;
      const receivers = (receiverWorldPowerStatus?.receivers || []).filter(
        (r) => Number(r?.pokemon_id) > 0
      );
      const activeReceiverIds = new Set(receivers.map((r) => Number(r?.receiver_placement_id)));
      const activeReceiverPlacements = (decorPlacements || [])
        .filter((p) => String(p?.decor_key || "") === "reciever" && activeReceiverIds.has(Number(p?.placement_id)))
        .map((p) => ({
          placement_id: Number(p?.placement_id),
          x: Number(p?.x) || 0,
          y: Number(p?.y) || 0,
        }));

      setWildPokemon((prev) => {
        return (prev || []).map((mon, idx, all) => {
          if (mon?.id && mon.id.toString().startsWith('fishing_')) return mon;

          if (mon?.is_team_rocket && !mon?.capturing) {
            const mx = Number(mon.x) || 0;
            const my = Number(mon.y) || 0;
            
            // If Team Rocket has a havoc target, move toward it
            let targetX, targetY;
            if (mon?.havoc_target) {
              targetX = Number(mon.havoc_target.x) || 0;
              targetY = Number(mon.havoc_target.y) || 0;
            } else {
              // Otherwise flee from player
              const playerX = Number(currentPlayer.x) || 0;
              const playerY = Number(currentPlayer.y) || 0;
              const dx = mx - playerX;
              const dy = my - playerY;
              const dist = Math.hypot(dx, dy);
              const fleeRange = 180;
              if (!Number.isFinite(dist) || dist > fleeRange || dist === 0) {
                return mon;
              }
              // Flee away from player
              targetX = mx + (dx > 0 ? 100 : -100);
              targetY = my + (dy > 0 ? 100 : -100);
            }

            const dx = targetX - mx;
            const dy = targetY - my;
            const dist = Math.hypot(dx, dy);
            
            // Stop moving if close enough to target
            if (mon?.havoc_target && dist < 20) {
              return mon;
            }

            const baseStepSize = Number(TILE_SIZE) || 8;
            const stepSize = Math.max(4, Math.round(baseStepSize * 0.75));
            const primaryAxis = Math.abs(dx) >= Math.abs(dy) ? "x" : "y";

            const tryMove = (dir) => {
              let nx = mx;
              let ny = my;
              if (dir === "left") nx -= stepSize;
              if (dir === "right") nx += stepSize;
              if (dir === "up") ny -= stepSize;
              if (dir === "down") ny += stepSize;

              nx = clamp(nx, 0, MAP_WIDTH - 32);
              ny = clamp(ny, 0, MAP_HEIGHT - 32);

              if (isBlocked(nx, ny)) return null;

              const pokemonBox = { x: nx, y: ny, width: 16, height: 16 };
              const hitOther = (all || []).some((other, j) => {
                if (j === idx) return false;
                if (other?.capturing) return false;
                return boxesOverlap(pokemonBox.x, pokemonBox.y, other.x, other.y, 16, 16, 16, 16);
              });
              if (hitOther) return null;

              return { nx, ny, dir };
            };

            let primaryDir;
            let secondaryDir;
            if (primaryAxis === "x") {
              primaryDir = dx >= 0 ? "right" : "left";
              secondaryDir = dy >= 0 ? "down" : "up";
            } else {
              primaryDir = dy >= 0 ? "down" : "up";
              secondaryDir = dx >= 0 ? "right" : "left";
            }

            const attemptA = tryMove(primaryDir);
            const attemptB = attemptA ? null : tryMove(secondaryDir);
            const chosen = attemptA || attemptB;
            if (!chosen) return { ...mon, direction: primaryDir };

            const step = ((Number(mon.step) || 0) + 1) % 4;
            return {
              ...mon,
              x: chosen.nx,
              y: chosen.ny,
              direction: chosen.dir,
              step,
            };
          }

          if (!Boolean(mon?.aggressive) || mon?.capturing) return mon;

          const receiverTargetRangePx = 260;
          const target = (() => {
            if (!isNight) return null;
            if (!activeReceiverPlacements.length) return null;
            const mx = Number(mon.x) || 0;
            const my = Number(mon.y) || 0;
            const nearest = activeReceiverPlacements
              .map((r) => ({
                r,
                dist: Math.hypot((Number(r.x) || 0) - mx, (Number(r.y) || 0) - my),
              }))
              .sort((a, b) => a.dist - b.dist)[0];
            if (!nearest) return null;
            if (!Number.isFinite(nearest.dist) || nearest.dist > receiverTargetRangePx) return null;
            return { x: nearest.r.x, y: nearest.r.y };
          })();

          const tx = target ? Number(target.x) || 0 : (Number(currentPlayer.x) || 0);
          const ty = target ? Number(target.y) || 0 : (Number(currentPlayer.y) || 0);

          const dx = tx - (Number(mon.x) || 0);
          const dy = ty - (Number(mon.y) || 0);
          const dist = Math.hypot(dx, dy);
          const range = target ? receiverTargetRangePx : AGGRO_RANGE_PX;
          if (!Number.isFinite(dist) || dist > range) return mon;

          const baseStepSize = Number(TILE_SIZE) || 8;
          const stepSize = Math.max(8, baseStepSize * 2);
          const primaryAxis = Math.abs(dx) >= Math.abs(dy) ? "x" : "y";

          const tryMove = (dir) => {
            let nx = Number(mon.x) || 0;
            let ny = Number(mon.y) || 0;
            if (dir === "left") nx -= stepSize;
            if (dir === "right") nx += stepSize;
            if (dir === "up") ny -= stepSize;
            if (dir === "down") ny += stepSize;

            nx = clamp(nx, 0, MAP_WIDTH - 32);
            ny = clamp(ny, 0, MAP_HEIGHT - 32);

            if (isBlocked(nx, ny)) return null;

            const pokemonBox = { x: nx, y: ny, width: 16, height: 16 };

            const hitOther = (all || []).some((other, j) => {
              if (j === idx) return false;
              if (other?.capturing) return false;
              return boxesOverlap(pokemonBox.x, pokemonBox.y, other.x, other.y, 16, 16, 16, 16);
            });
            if (hitOther) return null;

            return { nx, ny, dir };
          };

          let primaryDir;
          let secondaryDir;
          if (primaryAxis === "x") {
            primaryDir = dx >= 0 ? "right" : "left";
            secondaryDir = dy >= 0 ? "down" : "up";
          } else {
            primaryDir = dy >= 0 ? "down" : "up";
            secondaryDir = dx >= 0 ? "right" : "left";
          }

          const attemptA = tryMove(primaryDir);
          const attemptB = attemptA ? null : tryMove(secondaryDir);
          const chosen = attemptA || attemptB;
          if (!chosen) return { ...mon, direction: primaryDir };

          const step = ((Number(mon.step) || 0) + 1) % 4;
          return {
            ...mon,
            x: chosen.nx,
            y: chosen.ny,
            direction: chosen.dir,
            step,
          };
        });
      });
    }, 200);

    return () => clearInterval(iv);
  }, [MAP_ID, TILE_SIZE, MAP_WIDTH, MAP_HEIGHT, inBattle, isFishing, showPouch, showFishingShop, achievementsModalOpen, dialogueModalOpen, placingDecor, decorRemoveMode, showDecorPanel, receiverWorldPowerStatus, decorPlacements]);

  useEffect(() => {
    if (MAP_ID !== MY_ISLAND_MAP_ID) return;

    const ENGAGE_RANGE_PX = 10;
    const COOLDOWN_MS = 4500;

    const iv = setInterval(() => {
      if (inBattle) return;
      if (isFishing) return;
      if (showPouch || showFishingShop || achievementsModalOpen || dialogueModalOpen) return;
      if (placingDecor || decorRemoveMode || showDecorPanel) return;

      const currentPlayer = playerPosRef.current;
      if (!currentPlayer) return;

      const mons = wildPokemonRef.current || [];
      const now = Date.now();

      const target = mons.find((m) => Boolean(m?.is_team_rocket) && !m?.capturing);
      if (!target) return;

      const dist = Math.hypot(
        (Number(currentPlayer.x) || 0) - (Number(target.x) || 0),
        (Number(currentPlayer.y) || 0) - (Number(target.y) || 0)
      );
      if (dist > ENGAGE_RANGE_PX) return;
      if (now - (Number(lastTeamRocketEngageAtRef.current) || 0) < COOLDOWN_MS) return;

      lastTeamRocketEngageAtRef.current = now;
      setWildPokemon((prev) =>
        prev.map((p) =>
          p.id === target.id ? { ...p, team_rocket_revealed: true } : p
        )
      );

      const trainerId = Number(target?.team_rocket_trainer_id) || null;
      if (!trainerId) return;
      setBattleSpawnId(null);
      startTrainerBattle(trainerId, "Team Rocket", null, MAP_ID);
    }, 200);

    return () => clearInterval(iv);
  }, [MAP_ID, inBattle, isFishing, showPouch, showFishingShop, achievementsModalOpen, dialogueModalOpen, placingDecor, decorRemoveMode, showDecorPanel]);

   // —— Steam effects for EmberSpa map or maps with showSteamEffects enabled —— 
   useEffect(() => {

    // Return if not Ember Spa and showSteamEffects is not enabled
    if (MAP_ID !== 12 && (!config || !config.showSteamEffects)) return;

    // Use provided steam points or default Ember Spa points
    const steamPoints = config.steamPoints || [
      // Hot spring areas distributed throughout the map
      { x: 180, y: 180 }, { x: 200, y: 185 }, { x: 220, y: 175 }, { x: 240, y: 180 },
      { x: 280, y: 190 }, { x: 300, y: 185 }, { x: 320, y: 195 },
      { x: 190, y: 140 }, { x: 250, y: 145 }, { x: 310, y: 150 },
      { x: 170, y: 220 }, { x: 210, y: 225 }, { x: 270, y: 230 }, { x: 320, y: 235 },
      { x: 150, y: 200 }, { x: 350, y: 210 }, { x: 200, y: 160 }, { x: 290, y: 170 },
      // Additional scattered steam points
      { x: 160, y: 250 }, { x: 300, y: 260 }, { x: 230, y: 120 }, { x: 340, y: 180 },
      { x: 120, y: 190 }, { x: 380, y: 220 }, { x: 250, y: 280 }, { x: 180, y: 300 }
    ];

    let puffIdCounter = 0;
    const intervals = [];

    const createSteamPuff = (x, y) => {
      const puffId = puffIdCounter++;
      const randomOffset = {
        x: (Math.random() - 0.5) * 8, // Small random horizontal offset
        y: (Math.random() - 0.5) * 4  // Small vertical offset
      };

      const newPuff = {
        id: puffId,
        x: x + randomOffset.x,
        y: y + randomOffset.y,
        size: 3 + Math.random() * 2, // 3-5px size (smaller than before)
        opacity: 0.6 + Math.random() * 0.4, // 0.6-1.0 opacity
        duration: 1500 + Math.random() * 1000, // 1.5-2.5 seconds
        startTime: Date.now()
      };

      setSteamPuffs(prev => [...prev, newPuff]);

      // Remove puff after animation completes
      setTimeout(() => {
        setSteamPuffs(prev => prev.filter(puff => puff.id !== puffId));
      }, newPuff.duration + 100);
    };

    // Create intervals for each steam point
    steamPoints.forEach(point => {
      const interval = setInterval(() => {
        createSteamPuff(point.x, point.y);
      }, 800 + Math.random() * 1200); // Random interval 0.8-2.0 seconds
      intervals.push(interval);
    });

    return () => {
      intervals.forEach(clearInterval);
    };
  }, [MAP_ID, config]);

  useEffect(() => {
    const onKey = (e) => {
      if (document.hidden) return;

      const currentPlayerPos = playerPosRef.current;
      if (!currentPlayerPos) return;

      const currentDirection = directionRef.current;
      let nx = currentPlayerPos.x,
        ny = currentPlayerPos.y,
        dir = currentDirection;
      switch (e.key) {
        case "ArrowUp":
        case "w":
        case "W":
          ny -= TILE_SIZE;
          dir = "up";
          break;
        case "ArrowDown":
        case "s":
        case "S":
          ny += TILE_SIZE;
          dir = "down";
          break;
        case "ArrowLeft":
        case "a":
        case "A":
          nx -= TILE_SIZE;
          dir = "left";
          break;
        case "ArrowRight":
        case "d":
        case "D":
          nx += TILE_SIZE;
          dir = "right";
          break;
        default:
          return;
      }
      e.preventDefault();
      nx = clamp(nx, 0, MAP_WIDTH - 32);
      ny = clamp(ny, 0, MAP_HEIGHT - 32);
      const wallFn = isCollidingRef.current;
      const decorFn = isCollidingWithDecorRef.current;
      const hitWall = (typeof wallFn === 'function' ? wallFn(nx, ny) : false) || (typeof decorFn === 'function' ? decorFn(nx, ny) : false);

      const currentRocks = rocksRef.current || [];
      const hitRock = !hasGlideBoots && currentRocks
        .filter((r) => !r.collected)
        .some((r) => rockOverlap(nx, ny, r.x, r.y));
        
      // Check for Pokemon collisions, but ONLY when not capturing or mining
      // This allows walking through Pokemon only during active capture/mining animations
      const currentWildPokemon = wildPokemonRef.current || [];
      const anyCapturing = currentWildPokemon.some(p => p.capturing);
      const anyMining = Object.keys(breakingFramesRef.current || {}).length > 0;
      
      // Only check Pokemon collisions if we're not capturing or mining
      const hitPokemon = !hasGlideBoots && !anyCapturing && !anyMining && currentWildPokemon.some(p => {
        const dist = Math.hypot(nx - p.x, ny - p.y);
        return dist < 24; // Collision radius with Pokemon
      });

      // Check for trainer collisions - prevent walking through trainers
      const currentTrainerStates = trainerStatesRef.current || [];
      const hitTrainer = currentTrainerStates.some(trainer => {
        // For static trainers (wander=0), use 16x24 dimensions
        const isStatic = Number(trainer.wander) === 0;
        const trainerWidth = isStatic ? 16 : 32;
        const trainerHeight = isStatic ? 24 : 32;
        const tPadX = isStatic ? 1 : 4;
        const tPadY = isStatic ? 1 : 4;
        
        // Use shrinkRectOverlap for precise collision detection
        return shrinkRectOverlap(
          nx, ny, 32, 32,
          trainer.x, trainer.y, trainerWidth, trainerHeight,
          8, 8, tPadX, tPadY // Add some padding to make collision feel natural
        );
      });

      // Check for NPC collisions - prevent walking through NPCs
      const currentMapNpcs = mapNpcsRef.current;
      const hitNPC = currentMapNpcs && currentMapNpcs.some(npc => {
        const npcWidth = Number(npc.width || 16);
        const npcHeight = Number(npc.height || 24);
        return shrinkRectOverlap(
          nx, ny, 32, 32, // Player hitbox
          npc.x, npc.y, npcWidth, npcHeight, // NPC hitbox
          8, 8, 4, 4 // Padding for natural feel
        );
      });

      // Check for berry plant collisions - prevent walking through plants and empty slots
      let hitBerryPlant = false;
      if (MAP_ID === 1 || MAP_ID === MY_ISLAND_MAP_ID) {
        const currentBerryPlants = berryPlantsRef.current || [];
        // Check planted berries
        hitBerryPlant = currentBerryPlants.some(plant => {
          return shrinkRectOverlap(
            nx, ny, 32, 32, // Player hitbox
            plant.x, plant.y, 16, 16, // Berry plant hitbox (16x16)
            8, 8, 4, 4 // Padding for natural feel
          );
        });
        
        // Also check empty planting spots (all 12 positions)
        if (!hitBerryPlant) {
          const occupiedSlots = currentBerryPlants.map(p => p.slot_index);

          if (MAP_ID === 1) {
            hitBerryPlant = BERRY_PLANT_POSITIONS.some(([x, y], index) => {
              if (occupiedSlots.includes(index)) return false;
              return shrinkRectOverlap(nx, ny, 32, 32, x, y, 16, 16, 8, 8, 4, 4);
            });
          } else {
            const entries = Object.entries(soilbedSlotPositionsRef.current || {});
            hitBerryPlant = entries.some(([slotStr, xy]) => {
              const idx = Number(slotStr);
              if (occupiedSlots.includes(idx)) return false;
              const x = Number(xy?.[0]) || 0;
              const y = Number(xy?.[1]) || 0;
              return shrinkRectOverlap(nx, ny, 32, 32, x, y, 16, 16, 8, 8, 4, 4);
            });
          }
        }
      }

      // Allow movement if not hitting any obstacles
      if (!hitWall && !hitRock && !hitPokemon && !hitTrainer && !hitNPC && !hitBerryPlant) {
        // Check for teleport before updating position
        const teleportZone = checkTeleportRef.current?.(nx, ny);
        if (teleportZone) {
          handleTeleportRef.current?.(teleportZone.targetFloor, teleportZone.targetX, teleportZone.targetY);
          return;
        }

        setPlayerPos({ x: nx, y: ny });
        setDirection(dir);

        if (e.repeat) {
          setPlayerStep((prev) => (prev + 1) % 3);
        } else {
          const walkSeq = [1, 2, 0];
          walkSeq.forEach((step, i) =>
            setTimeout(() => setPlayerStep(step), i * 150)
          );
        }
      }
    };
    window.addEventListener("keydown", onKey);
    
    return () => window.removeEventListener("keydown", onKey);
  }, [TILE_SIZE, MAP_WIDTH, MAP_HEIGHT, MAP_ID, MY_ISLAND_MAP_ID, BERRY_PLANT_POSITIONS, hasGlideBoots]);


    useEffect(() => {
   if (selectedAction?.type === "item" && selectedAction.payload === "rock") {
      localStorage.setItem("selectedRock", "true");
    } else {
      localStorage.removeItem("selectedRock");
    }
    if (selectedAction?.type === "item" && selectedAction.payload === "rock") {
      setSelectedBall(null);
      localStorage.removeItem("selectedBall");
    }
  }, [selectedAction]);

  // —— Camera —— 
  const snapPlayerX = Math.round(Number(playerPos.x) || 0);
  const snapPlayerY = Math.round(Number(playerPos.y) || 0);
  const camX = clamp(snapPlayerX - VIEWPORT_WIDTH / 2, 0, MAP_WIDTH - VIEWPORT_WIDTH);
  const camY = clamp(snapPlayerY - VIEWPORT_HEIGHT / 2, 0, MAP_HEIGHT - VIEWPORT_HEIGHT);
  const playerBottomEdge = (Number(playerPos.y) || 0) + 32;
  const myIslandZBase = 1000;

  const getWorldPosFromMouseEvent = useCallback(
    (e) => {
      const el = gameContainerRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      const sx = (Number(e?.clientX) || 0) - rect.left;
      const sy = (Number(e?.clientY) || 0) - rect.top;

      const lx = sx / (Number(mapScale) || 1);
      const ly = sy / (Number(mapScale) || 1);

      const clampedX = Math.max(0, Math.min(lx, VIEWPORT_WIDTH));
      const clampedY = Math.max(0, Math.min(ly, VIEWPORT_HEIGHT));

      return {
        worldX: (Number(camX) || 0) + clampedX,
        worldY: (Number(camY) || 0) + clampedY,
      };
    },
    [camX, camY, mapScale, VIEWPORT_WIDTH, VIEWPORT_HEIGHT]
  );

  const handleTrainerNpcMapClick = useCallback(
    async (e) => {
      if (!isMyIsland) return;
      if (trainerNpcMode !== "move") return;
      if (!selectedOwnedTrainerNpc) return;
      e?.preventDefault?.();
      e?.stopPropagation?.();

      const pos = getWorldPosFromMouseEvent(e);
      if (!pos) return;

      const gx = decorSnap(pos.worldX - 8);
      const gy = decorSnap(pos.worldY - 12);

      try {
        await api.post(
          "/v1/island/trainer-npcs/place",
          {
            owned_npc_id: Number(selectedOwnedTrainerNpc?.owned_npc_id) || 0,
            x: Math.round(Number(gx) || 0),
            y: Math.round(Number(gy) || 0),
            map_id: MY_ISLAND_MAP_ID,
          },
          { withCredentials: true }
        );
        spawnText("Placed!", gx, gy - 12, "#22c55e");
        refreshOwnedTrainerNpcs();
        exitTrainerNpcMode();
      } catch (err) {
        const msg = err?.response?.data?.detail || "Cannot place here";
        spawnText(msg, playerPos.x, playerPos.y - 16, "#f87171");
      }
    },
    [
      isMyIsland,
      trainerNpcMode,
      selectedOwnedTrainerNpc,
      getWorldPosFromMouseEvent,
      decorSnap,
      spawnText,
      refreshOwnedTrainerNpcs,
      exitTrainerNpcMode,
      playerPos,
    ]
  );

  const handleGameCornerNpcMapClick = useCallback(
    async (e) => {
      if (!isMyIsland) return;
      if (gameCornerNpcMode !== "move") return;
      if (!selectedOwnedGameCornerNpc) return;
      e?.preventDefault?.();
      e?.stopPropagation?.();

      const pos = getWorldPosFromMouseEvent(e);
      if (!pos) return;

      const gx = decorSnap(pos.worldX - 8);
      const gy = decorSnap(pos.worldY - 12);

      try {
        await api.post(
          "/v1/island/gamecorner-npcs/place",
          {
            owned_npc_id: Number(selectedOwnedGameCornerNpc?.owned_npc_id) || 0,
            x: Math.round(Number(gx) || 0),
            y: Math.round(Number(gy) || 0),
            map_id: MY_ISLAND_MAP_ID,
          },
          { withCredentials: true }
        );
        spawnText("Placed!", gx, gy - 12, "#22c55e");
        refreshOwnedGameCornerNpcs();
        setGameCornerNpcMode(null);
        setSelectedOwnedGameCornerNpc(null);
      } catch (err) {
        const msg = err?.response?.data?.detail || "Cannot place here";
        spawnText(msg, playerPos.x, playerPos.y - 16, "#f87171");
      }
    },
    [
      isMyIsland,
      gameCornerNpcMode,
      selectedOwnedGameCornerNpc,
      getWorldPosFromMouseEvent,
      decorSnap,
      spawnText,
      refreshOwnedGameCornerNpcs,
      playerPos,
    ]
  );

  const handlePlacementMouseMove = useCallback(
    (e) => {
      if (decorMoveMode && movingDecor) {
        const pos = getWorldPosFromMouseEvent(e);
        if (!pos) return;

        const sw = Number(movingDecor?.sprite_w) || 16;
        const sh = Number(movingDecor?.sprite_h) || 16;

        let gx = decorSnap(pos.worldX - sw / 2);
        let gy = decorSnap(pos.worldY - sh / 2);

        gx = Math.max(0, Math.min(Number(gx) || 0, Math.max(0, MAP_WIDTH - sw)));
        gy = Math.max(0, Math.min(Number(gy) || 0, Math.max(0, MAP_HEIGHT - sh)));

        setMouseGhostPos({ x: gx, y: gy });
        return;
      }
      
      if (!placingDecor || decorRemoveMode || !selectedDecor) return;
      const pos = getWorldPosFromMouseEvent(e);
      if (!pos) return;

      const sw = Number(selectedDecor?.sprite_w) || 16;
      const sh = Number(selectedDecor?.sprite_h) || 16;

      let gx = decorSnap(pos.worldX - sw / 2);
      let gy = decorSnap(pos.worldY - sh / 2);

      gx = Math.max(0, Math.min(Number(gx) || 0, Math.max(0, MAP_WIDTH - sw)));
      gy = Math.max(0, Math.min(Number(gy) || 0, Math.max(0, MAP_HEIGHT - sh)));

      setMouseGhostPos({ x: gx, y: gy });
    },
    [
      placingDecor,
      decorRemoveMode,
      decorMoveMode,
      movingDecor,
      selectedDecor,
      getWorldPosFromMouseEvent,
      decorSnap,
      MAP_WIDTH,
      MAP_HEIGHT,
    ]
  );

  const sortedDecorPlacements = useMemo(() => {
    const rows = Array.isArray(decorPlacements) ? decorPlacements : [];
    return [...rows].sort((a, b) => {
      const aSpriteBottom = (Number(a?.y) || 0) + (Number(a?.sprite_h) || 0);
      const aCollisionBottom =
        (Number(a?.y) || 0) +
        (Number(a?.collision_y) || 0) +
        (Number(a?.collision_h) || 0);
      const ay = Math.max(aSpriteBottom, aCollisionBottom);

      const bSpriteBottom = (Number(b?.y) || 0) + (Number(b?.sprite_h) || 0);
      const bCollisionBottom =
        (Number(b?.y) || 0) +
        (Number(b?.collision_y) || 0) +
        (Number(b?.collision_h) || 0);
      const by = Math.max(bSpriteBottom, bCollisionBottom);
      const dy = ay - by;
      if (dy !== 0) return dy;
      const ap = Number(a?.placement_id) || 0;
      const bp = Number(b?.placement_id) || 0;
      return ap - bp;
    });
  }, [decorPlacements]);

  const ghostDecor = useMemo(() => {
    if (decorMoveMode && movingDecor) {
      const sw = Number(movingDecor?.sprite_w) || 16;
      const sh = Number(movingDecor?.sprite_h) || 16;
      
      let gx = snapPlayerX;
      let gy = snapPlayerY;

      if (mouseGhostPos && mouseGhostPos.x != null && mouseGhostPos.y != null) {
        gx = Number(mouseGhostPos.x) || 0;
        gy = Number(mouseGhostPos.y) || 0;
      }

      gx = Math.max(0, Math.min(Number(gx) || 0, Math.max(0, MAP_WIDTH - sw)));
      gy = Math.max(0, Math.min(Number(gy) || 0, Math.max(0, MAP_HEIGHT - sh)));

      gx = decorSnap(gx);
      gy = decorSnap(gy);

      return {
        x: gx,
        y: gy,
        sprite_w: sw,
        sprite_h: sh,
        sprite_path: movingDecor?.sprite_path,
        collision_x: Number(movingDecor?.collision_x) || 0,
        collision_y: Number(movingDecor?.collision_y) || 0,
        collision_w: Number(movingDecor?.collision_w) || sw,
        collision_h: Number(movingDecor?.collision_h) || sh,
      };
    }
    
    if (!placingDecor || !selectedDecor) return null;

    const sw = Number(selectedDecor?.sprite_w) || 16;
    const sh = Number(selectedDecor?.sprite_h) || 16;

    let gx = snapPlayerX;
    let gy = snapPlayerY;

    if (mouseGhostPos && mouseGhostPos.x != null && mouseGhostPos.y != null) {
      gx = Number(mouseGhostPos.x) || 0;
      gy = Number(mouseGhostPos.y) || 0;
    } else {
      if (direction === "up") {
        gy = snapPlayerY - sh;
      } else if (direction === "down") {
        gy = snapPlayerY + 32;
      } else if (direction === "left") {
        gx = snapPlayerX - sw;
      } else if (direction === "right") {
        gx = snapPlayerX + 32;
      }
    }

    gx = Math.max(0, Math.min(Number(gx) || 0, Math.max(0, MAP_WIDTH - sw)));
    gy = Math.max(0, Math.min(Number(gy) || 0, Math.max(0, MAP_HEIGHT - sh)));

    gx = decorSnap(gx);
    gy = decorSnap(gy);

    return {
      ...selectedDecor,
      x: gx,
      y: gy,
      sprite_w: sw,
      sprite_h: sh,
    };
  }, [placingDecor, selectedDecor, snapPlayerX, snapPlayerY, direction, MAP_WIDTH, MAP_HEIGHT, decorSnap, mouseGhostPos]);

  const canPlaceGhost = useMemo(() => {
    if (!ghostDecor) return false;

    const cx = Number(ghostDecor?.collision_x) || 0;
    const cy = Number(ghostDecor?.collision_y) || 0;
    const cw = Number(ghostDecor?.collision_w) || 0;
    const ch = Number(ghostDecor?.collision_h) || 0;
    const usesCollision = cw > 0 && ch > 0;

    const ax = (Number(ghostDecor?.x) || 0) + (usesCollision ? cx : 0);
    const ay = (Number(ghostDecor?.y) || 0) + (usesCollision ? cy : 0);
    const aw = usesCollision ? cw : Number(ghostDecor?.sprite_w) || 16;
    const ah = usesCollision ? ch : Number(ghostDecor?.sprite_h) || 16;

    const rectOverlap = (x1, y1, w1, h1, x2, y2, w2, h2) =>
      x1 < x2 + w2 && x1 + w1 > x2 && y1 < y2 + h2 && y1 + h1 > y2;

    // Walls (only enforced for decor that actually has collision)
    if (usesCollision) {
      const hitWall = (collisionAreas || []).some((w) => {
        const padX = Number(w.padX ?? 0);
        const padY = Number(w.padY ?? 0);
        const wx = Number(w.x || 0) + padX;
        const wy = Number(w.y || 0) + padY;
        const ww = Number(w.width || 0) - padX * 2;
        const wh = Number(w.height || 0) - padY * 2;
        if (ww <= 0 || wh <= 0) return false;
        return rectOverlap(ax, ay, aw, ah, wx, wy, ww, wh);
      });
      if (hitWall) return false;
    }

    // Existing placements
    const hitExisting = (decorPlacements || []).some((p) => {
      const pcw = Number(p?.collision_w) || 0;
      const pch = Number(p?.collision_h) || 0;
      const pUsesCollision = pcw > 0 && pch > 0;

      const px = (Number(p?.x) || 0) + (pUsesCollision ? (Number(p?.collision_x) || 0) : 0);
      const py = (Number(p?.y) || 0) + (pUsesCollision ? (Number(p?.collision_y) || 0) : 0);
      const pw = pUsesCollision ? pcw : Number(p?.sprite_w) || 16;
      const ph = pUsesCollision ? pch : Number(p?.sprite_h) || 16;

      if (pw <= 0 || ph <= 0) return false;
      return rectOverlap(ax, ay, aw, ah, px, py, pw, ph);
    });
    if (hitExisting) return false;

    return true;
  }, [ghostDecor, collisionAreas, decorPlacements]);

  const handlePlacementMapClick = useCallback(
    (e) => {
      if (decorMoveMode && selectedMovePlacementId && ghostDecor) {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        handleMoveDecorAt(ghostDecor.x, ghostDecor.y);
        return;
      }
      if (!placingDecor || decorRemoveMode) return;
      if (!ghostDecor || !canPlaceGhost) return;
      e?.preventDefault?.();
      e?.stopPropagation?.();
      handlePlaceDecorAt(ghostDecor.x, ghostDecor.y);
    },
    [placingDecor, decorRemoveMode, decorMoveMode, selectedMovePlacementId, ghostDecor, canPlaceGhost, handlePlaceDecorAt, handleMoveDecorAt]
  );

  const handleHutClickForAssignment = useCallback(
    async (placement) => {
      if (!isMyIsland) return;
      if (!trainerNpcMode && !gameCornerNpcMode) return;
      if (!["hut", "junglehut", "tinyhome"].includes(String(placement?.decor_key || ""))) return;

      // Handle Game Corner NPC assignment
      if (gameCornerNpcMode === "assign" && selectedOwnedGameCornerNpc) {
        try {
          await api.post(
            "/v1/island/gamecorner-npcs/assign",
            {
              owned_npc_id: Number(selectedOwnedGameCornerNpc?.owned_npc_id) || 0,
              hut_placement_id: Number(placement?.placement_id) || 0,
              map_id: MY_ISLAND_MAP_ID,
            },
            { withCredentials: true }
          );
          spawnText("Assigned!", Number(placement?.x) || 0, (Number(placement?.y) || 0) - 12, "#22c55e");
          refreshOwnedGameCornerNpcs();
          setGameCornerNpcMode(null);
          setSelectedOwnedGameCornerNpc(null);
        } catch (e) {
          const msg = e?.response?.data?.detail || "Failed to assign";
          spawnText(msg, Number(placement?.x) || 0, (Number(placement?.y) || 0) - 12, "#f87171");
        }
        return;
      }

      // Handle Trainer NPC assignment
      if (trainerNpcMode !== "assign") return;
      if (!selectedOwnedTrainerNpc) return;

      try {
        await api.post(
          "/v1/island/trainer-npcs/assign",
          {
            owned_npc_id: Number(selectedOwnedTrainerNpc?.owned_npc_id) || 0,
            hut_placement_id: Number(placement?.placement_id) || 0,
            map_id: MY_ISLAND_MAP_ID,
          },
          { withCredentials: true }
        );
        spawnText("Assigned!", Number(placement?.x) || 0, (Number(placement?.y) || 0) - 12, "#22c55e");
        refreshOwnedTrainerNpcs();
        exitTrainerNpcMode();
      } catch (e) {
        const msg = e?.response?.data?.detail || "Failed to assign";
        spawnText(msg, playerPos.x, playerPos.y - 16, "#f87171");
      }
    },
    [
      trainerNpcMode,
      gameCornerNpcMode,
      selectedOwnedTrainerNpc,
      selectedOwnedGameCornerNpc,
      spawnText,
      refreshOwnedTrainerNpcs,
      refreshOwnedGameCornerNpcs,
      exitTrainerNpcMode,
      playerPos,
      isMyIsland,
    ]
  );

  const getBallPixelDistance = (id) => {
    const b = catchRates.find((c) => c.pokeball_id === id);
    return b?.pixel_distance || 0;
  };

const handlePokemonClick = async (poke) => {
  // 1) If already mid-capture, do nothing
  if (poke.capturing) return;

  if (poke?.is_team_rocket) {
    const dist = Math.hypot(playerPos.x - poke.x, playerPos.y - poke.y);
    if (dist > 20) {
      return;
    }

    setWildPokemon((prev) =>
      prev.map((p) =>
        p.id === poke.id
          ? { ...p, team_rocket_revealed: true }
          : p
      )
    );

    const trainerId = Number(poke?.team_rocket_trainer_id) || null;
    if (!trainerId) {
      spawnText(
        "Team Rocket slipped away!",
        playerPos.x,
        playerPos.y - 16,
        "#f87171"
      );
      return;
    }

    setBattleSpawnId(null);
    await startTrainerBattle(trainerId, "Team Rocket", null, MAP_ID);
    return;
  }

  // Store the player position at the moment of interaction
  // This fixes the issue with analog stick movement during capture
  capturePositionRef.current = { ...playerPos };
  
  // 2) If rock is selected, check roster & enter battle
  if (selectedAction?.type === 'item' && selectedAction.payload === 'rock') {
    // a) First: fetch your roster & ensure someone has HP > 0
    try {
      const r = await api.get('/v1/roster', { withCredentials: true });
      const rosterData = r.data;
      const anyAlive = rosterData.some(p => p.current_hp > 0);
      if (!anyAlive) {
        spawnText(
          "All your Pokémon are fainted! You can't battle.",
          playerPos.x,
          playerPos.y - 16,
          "red"
        );
        return;
      }
    } catch (err) {
      console.error("Failed to fetch roster:", err);
      spawnText(
        "Could not check your roster. Try again later.",
        playerPos.x,
        playerPos.y - 16,
        "red"
      );
      return;
    }

    // b) Do we have at least one rock?
    const rockCount = gems.rock ?? 0;
    if (rockCount < 1) {
      spawnText(
        "No rocks to throw at the Pokémon!",
        playerPos.x,
        playerPos.y - 16,
        "red"
      );
      return;
    }

    try {
      // c) Deduct one rock locally (we’ll refund on failure)
      setGems(prev => ({ ...prev, rock: prev.rock - 1 }));

      // d) Attempt to start the battle on the server
      const battleRequest = {
        opponent_type: 'wild',
        spawn_id: poke.id,
        map_id: MAP_ID
      };
      
      // If this is a fishing Pokemon, include the species
      if (poke.id && poke.id.toString().startsWith('fishing_')) {
        battleRequest.species = poke.species || poke.name;
      }
      
      const res = await api.post(
        '/v1/battle/start',
        battleRequest,
        { withCredentials: true }
      );

      // e) On success: store the initial battle payload & mount BattleScreen
      setBattleSpawnId(poke.id);
      setInitialBattleData({ ...res.data, opponent_type: "wild" });
      setInBattle(true);

    } catch (err) {
      // f) On failure: refund the rock locally & show error
      setGems(prev => ({ ...prev, rock: prev.rock + 1 }));
      const msg = err.response?.data?.message || "Your rock missed!";
      spawnText(
        msg,
        playerPos.x,
        playerPos.y - 16,
        "red"
      );
    }

    return;
  }

  // 3) If a Poké-ball is selected, do your normal capture flow
  if (selectedAction?.type === "ball") {
    const ball = selectedAction.payload;
    const dist = Math.hypot(playerPos.x - poke.x, playerPos.y - poke.y);
    if (dist > getBallPixelDistance(ball.item)) {
      spawnText(
        "Too far away!",
        playerPos.x,
        playerPos.y - 16,
        "red"
      );
      return;
    }

    // mark this Pokémon as “capturing” so the UI shows the ball animation
    setWildPokemon(prev =>
      prev.map(p =>
        p.id === poke.id
          ? {
              ...p,
              capturing:      true,
              captureFrame:   0,
              originalSprite: p.sprite,
              usedBallItemId: ball.item
            }
          : p
      )
    );
    // actually fire off the capture logic
    capture(poke, ball);
    return;
  }

  // 4) Nothing selected
  spawnText(
    "Select a Pokéball or rock first!",
    playerPos.x,
    playerPos.y - 16,
    "red"
  );
};
  
  
  

  async function capture(poke, ball) {
    // Save the player position at the moment of capture
    // Note: We already set capturePositionRef.current in handlePokemonClick
    // This ensures proper position tracking even during analog stick movement
    // We still keep a local copy for fallback
    const capturePosition = capturePositionRef.current || { ...playerPos };
    
    // Check if this is a fishing Pokemon (client-side spawned)
    const isFishingPokemon = poke.id && poke.id.toString().startsWith('fishing_');
    
    try {
      let res;
      
      if (isFishingPokemon) {
        // Handle fishing Pokemon capture - save to backend
        try {
          const captureRes = await api.post(
            "/v1/fishing/capture",
            { 
              species: poke.species, 
              ball_type: ball.item,
              level: poke.level || 5
            },
            { withCredentials: true }
          );
          res = captureRes;
        } catch (error) {
          // If backend call fails, still allow local capture
          console.error("Fishing capture backend error:", error);
          res = {
            data: {
              success: true,
              exp_gained: Math.floor(Math.random() * 50) + 50, // 50-100 XP
              pokedex_entry: false,
              coins_earned: 0,
              reward_item: null
            }
          };
        }
      } else {
        // Normal server-side Pokemon capture
        res = await api.post(
          "/v1/capture",
          { mapId: MAP_ID, pokeId: poke.id, species: poke.species, ball_type: ball.item },
          { withCredentials: true }
        );
      }
  
      // Deduct one ball from inventory (and clear selectedBall if it hit 0)
      setInventory((inv) => {
        const updated = inv.map((i) =>
          i.item === ball.item ? { ...i, amount: i.amount - 1 } : i
        );
        const usedBall = updated.find((i) => i.item === ball.item);
        if (!usedBall || usedBall.amount <= 0) {
          if (selectedBall?.item === ball.item) {
            localStorage.removeItem("selectedBall");
            setSelectedBall(null);
          }
          return updated.filter((i) => i.amount > 0);
        }
        return updated;
      });
  
      // Mark “capturing” and set initial frame = 0 (closed ball)
      setWildPokemon((wp) =>
        wp.map((p) =>
          p.id === poke.id ? { ...p, capturing: true, captureFrame: 0, originalSprite: p.sprite, usedBallItemId: ball.item } : p
        )
      );
  
      if (res.data.success) {
        //
        // ─── SUCCESS ───
        //
        // Simply mark the Pokemon as captured without any special animations
        setWildPokemon((wp) =>
          wp.map((p) =>
            p.id === poke.id
              ? { ...p, capturing: true, captureFrame: 0 }
              : p
          )
        );
        
        setTimeout(() => {
          setWildPokemon((wp) => wp.filter((p) => p.id !== poke.id));
  
          // Always use the position from the ref to ensure we're using the saved position
          const pos = capturePositionRef.current || capturePosition;
          spawnText(`+1 ${poke.species}`, pos.x, pos.y - 16, "green");
          const exp = res.data.exp_gained;
          const formatExp = exp.toLocaleString();
          setTimeout(() =>
            spawnText(`+${formatExp} XP`, pos.x, pos.y - 32, "green")
          , 250);

          const lineHeight = 16;
          let nextLine = 2;
          const scheduleStacked = (text, delayMs, color, imageUrl) => {
            const lineIndex = nextLine;
            nextLine += 1;
            const y = pos.y - (lineIndex + 1) * lineHeight;
            setTimeout(() => spawnText(text, pos.x, y, color, imageUrl), delayMs);
          };

          if (res.data.coins_earned && res.data.coins_earned > 0) {
            scheduleStacked(`+${res.data.coins_earned} coin`, 500, "#ffe066");
          }

          if (res.data.reward_item) {
            let rewardImage;
            switch (res.data.reward_item) {
              case "rock": rewardImage = rockImg; break;
              case "ruby": rewardImage = rubyImg; break;
              case "sapphire": rewardImage = sapphireImg; break;
              case "emerald": rewardImage = emeraldImg; break;
              case "gold": rewardImage = goldImg; break;
              case "diamond": rewardImage = diamondImg; break;
              default: rewardImage = null;
            }
            const rewardName = res.data.reward_item.charAt(0).toUpperCase() + res.data.reward_item.slice(1);
            scheduleStacked(`+1 ${rewardName}`, 500, "#ffe066", rewardImage);
            
            // Update gems state to reflect the new gem in inventory
            setGems(prev => ({
              ...prev,
              [res.data.reward_item]: (prev[res.data.reward_item] || 0) + 1
            }));
          }

          const extraDrops = Array.isArray(res.data?.extra_drops) ? res.data.extra_drops : [];
          if (extraDrops.length) {
            extraDrops.forEach((d) => {
              const amt = Number(d?.amount) || 1;
              const name = String(d?.item_name || "Item");
              scheduleStacked(`+${amt} ${name}`, 500, "#ffe066");
            });

            api.get("/v1/inventory", { withCredentials: true })
              .then((r) => setInventory(r.data || []))
              .catch(() => null);
          }

          if (res.data.pokedex_entry) {
            scheduleStacked(`New Pokédex Entry!`, 750, "red");
          }

          if (res.data.scent_remaining && res.data.scent_remaining > 0) {
            scheduleStacked(`Anti-flee Scent: ${res.data.scent_remaining} uses left`, 1000, "#9370DB");
          }
        }, 4000);
      } else {
        //
        // ─── FAILURE (possibly fled) ───
        //
        // Play the 3‐frame "open‐and‐shake" animation (frames 1→2→3 at 1s intervals).
        //
        [1, 2, 3].forEach((frame, i) =>
          setTimeout(() => {
            setWildPokemon((wp) =>
              wp.map((p) =>
                p.id === poke.id ? { ...p, captureFrame: frame, usedBallItemId: selectedBall?.item } : p
              )
            );
          }, i * 1000)
        );

        setTimeout(() => {
          if (res.data.fled) {
            const pos = capturePositionRef.current || capturePosition;
            const fleeMsg = res.data?.is_team_rocket
              ? (res.data?.message || "Team Rocket fled!")
              : "Pokémon fled!";
            spawnText(fleeMsg, pos.x, pos.y - 16, "red");
            setWildPokemon((wp) => wp.filter((p) => p.id !== poke.id));
            return;
          }

          const breakoutEffectId = `breakout-${Date.now()}`;
          setCaptureEffects((effects) => [
            ...effects,
            {
              id: breakoutEffectId,
              x: poke.x,
              y: poke.y,
              ballType: getBallClass(selectedBall?.item),
              created: Date.now(),
            },
          ]);

          // After a delay, clear the effect and show the Pokemon
          setTimeout(() => {
            // First clean up the burst effect
            setCaptureEffects((effects) => effects.filter((e) => e.id !== breakoutEffectId));

            // Then restore the Pokemon with its original sprite
            setWildPokemon((wp) =>
              wp.map((p) =>
                p.id === poke.id
                  ? {
                      ...p,
                      capturing: false,
                      captureFrame: 0,
                      sprite: p.originalSprite,
                      usedBallItemId: selectedBall?.item,
                    }
                  : p
              )
            );

            // Display remaining anti-flee scent attempts if available
            if (res.data.scent_active && res.data.scent_remaining > 0) {
              const pos = capturePositionRef.current || capturePosition;
              setTimeout(() => {
                // Position the message below the "Capture failed" message
                spawnText(`Anti-flee Scent: ${res.data.scent_remaining} uses left`, pos.x, pos.y - 32, "#9370DB");
              }, 300);
            }
          }, 600);
        }, 4000);
      }
    } catch (err) {
      console.error(err);
      // Only show error for actual API errors, not for player movement
      
      // Check if the error is a server/network error (not just player movement)
      // We only want to show errors for actual API/network issues
      if (err.response && err.response.status >= 400) {
        // Use the saved position from capturePositionRef to ensure consistent positioning
        const pos = capturePositionRef.current || capturePosition;
        spawnText("Error during capture!", pos.x, pos.y - 16, "red");
      }
      
      // On any error, restore pokemon immediately
      setWildPokemon((wp) =>
        wp.map((p) =>
          p.id === poke.id
            ? { ...p, capturing: false, captureFrame: 0, sprite: p.originalSprite, usedBallItemId: selectedBall?.item }
            : p
        )
      );
      
      // Clean up any effects
      setCaptureEffects(effects => effects.filter(e => 
        e.x !== poke.x || e.y !== poke.y
      ));
    }
  }
  

  
  

  // —— Floating text —— 
  function spawnText(text, x, y, color = "#ffe066", imageUrl = null) {
    const id = Date.now() + Math.random();
    setFloatingTexts((ft) => [
      ...ft,
      { id, text, x, y, color, imageUrl, created: Date.now() },
    ]);

    // Remove after 1.5 sec
    setTimeout(() => {
      setFloatingTexts((ft) => ft.filter((t) => t.id !== id));
    }, 1500);
  }


  const resetRocks = async () => {
    try {
      // Clear the current rocks
      setRocks([]);


      // Fetch new rocks from the server using the correct endpoint format
      const response = await api.get(`/v1/rocks`, { 
        params: { map_id: MAP_ID },
        withCredentials: true 
      });

      // Update rocks with the new data
      setRocks(response.data.data || []);

      // Show success message
      spawnText("Rocks refreshed!", playerPos.x, playerPos.y+32, "#00ff00");
    } catch (error) {
      console.error("Error refreshing rocks:", error);
      spawnText("Failed to refresh rocks", playerPos.x, playerPos.y - 16, "#ff0000");
    }
  };

  // —— Rock breaking —— 
  const handleRockClick = (rock) => {
    // 1) If this rock is already "breaking", do nothing
    if (breakingFrames[rock.id] != null) return;
    
    // Save the player position at the start of mining
    const miningPosition = { ...playerPos }; // Clone to avoid reference issues
    // Store the mining position in a ref to ensure we have access to it throughout the animation
    // This is critical for making mining work with analog stick movement
    miningPositionRef.current = miningPosition;
    
    // Check if this is an Item Finder item
    const isItemFinderItem = rock.item_type && rock.item_type.startsWith('item_tier');
    
    // If it's an Item Finder item, use a different animation and collection logic
    if (isItemFinderItem) {
      
      // Show a sparkle animation instead of breaking animation
      setBreaking(b => ({
        ...b,
        [rock.id]: 0
      }));
      
      // Animate a quick sparkle effect
      [1, 2, 3].forEach((frame, i) => {
        setTimeout(() => {
          setBreaking(b => ({
            ...b,
            [rock.id]: frame
          }));
        }, 150 * (i + 1)); // Faster animation for items
      });
      
      // After the sparkle animation, send the collect request
      setTimeout(async () => {
        try {
          const res = await api.post(
            "/v1/rocks/collect",
            { map_id: MAP_ID, rock_id: rock.id },
            { withCredentials: true }
          );
          
          if (res.data.success) {
            // Remove from map
            setRocks(r => r.filter(rk => rk.id !== rock.id));
            
            // Get item details
            const drop = res.data.dropItem;
            const pretty = drop.charAt(0).toUpperCase() + drop.slice(1).replace(/_/g, ' ');
            
            // Determine item color and message based on the item_type
            let color = "#ffe066";
            let message = "";
            
            if (rock.item_type.includes('tier1')) {
              color = "#a0e6ff"; // Light blue for common items
              message = `You found a ${pretty}!`;
            } else if (rock.item_type.includes('tier2')) {
              color = "#50c878"; // Green for uncommon items
              message = `You found an ${pretty}!`;
            } else if (rock.item_type.includes('tier3')) {
              color = "#ffd700"; // Gold for rare items
              message = `You found a ${pretty}!`;
            }
            
            // Get the appropriate icon URL
            const itemIconPath = `/Maps/items/${drop}.png`;
            
            // Show a special notification for Item Finder discoveries
            spawnText(
              message,
              miningPositionRef.current.x,
              miningPositionRef.current.y - 16,
              color,
              itemIconPath
            );
            
            // Show XP gained
            setTimeout(() => {
              spawnText(
                `+${res.data.expGained} Mining XP`,
                miningPositionRef.current.x,
                miningPositionRef.current.y - 32,
                color
              );
            }, 400);
            
            // No need to update pickaxe durability for Item Finder items
            // But still update lure hits
            setExpLureHits(h => Math.max(0, h - 1));
            setDropLureHits(h => Math.max(0, h - 1));
          }
        } catch (e) {
          // Handle error
          const pos = miningPositionRef.current || miningPosition;
          spawnText(
            `Error collecting item.`,
            pos.x,
            pos.y - 16,
            "red"
          );
        } finally {
          // Clear the breaking/sparkle flag
          setBreaking(b => {
            const { [rock.id]: _, ...rest } = b;
            return rest;
          });
        }
      }, 500); // Shorter timeout for item collection
    } else {
      // Original rock breaking logic for normal rocks
      // 2) Lock immediately (frame 0 = "just started")
      setBreaking(b => ({
        ...b,
        [rock.id]: 0
      }));
    
      // 3) Animate frames 1→2→3 at 250 ms intervals
      [1, 2, 3].forEach((frame, i) => {
        setTimeout(() => {
          setBreaking(b => ({
            ...b,
            [rock.id]: frame
          }));
        }, 250 * (i + 1));
      });
    
      // 4) After the "smash" animation, send the collect request
      setTimeout(async () => {
        try {
          const res = await api.post(
            "/v1/rocks/collect",
            { map_id: MAP_ID, rock_id: rock.id },
            { withCredentials: true }
          );
          if (res.data.success) {
            // remove from map
            setRocks(r => r.filter(rk => rk.id !== rock.id));
    
            // spawn loot text…
            const drop = res.data.dropItem;
            
            // Map stone IDs to proper names
            const stoneNameMap = {
              "stone_112": "Fire Stone",
              "stone_34": "Water Stone",
              "stone_114": "Thunder Stone",
              "stone_126": "Leaf Stone",
              "stone_22": "Moon Stone",
              "stone_26": "Dawn Stone",
              "stone_151": "Metal Coat",
              "stone_135": "Kings Rock"
            };
            
            // Use stone name map if it's a stone, otherwise format normally
            const pretty = stoneNameMap[drop] || (
              drop.includes('_') ? 
                drop.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 
                drop.charAt(0).toUpperCase() + drop.slice(1)
            );
            
            const iconUrl = rockIcons[drop] || rockIcons.rock;
            const colorMap = {
              rock: "#999", ruby: "#e0115f", emerald: "#50c878",
              sapphire: "#0f52ba", gold: "#ffd700", diamond: "#a0e6ff",
              fire_stone: "#ff5733", water_stone: "#33a0ff", thunder_stone: "#ffcc00",
              // Colors for stone items
              stone_112: "#ff5733", // Fire Stone - orange-red
              stone_34: "#33a0ff",  // Water Stone - blue
              stone_114: "#ffcc00", // Thunder Stone - yellow
              stone_126: "#777777", // Lead Stone - gray
              stone_22: "#e6e6fa",  // Moon Stone - light purple
              stone_26: "#ffc0cb",  // Dawn Stone - pink
              stone_151: "#c0c0c0", // Metal Coat - silver
              stone_135: "#daa520"  // Kings Rock - goldenrod
            };
            const color = colorMap[drop] || "#ffe066";

            // Always use miningPositionRef.current for consistent floating text position
            const qty = Number(res.data.dropQty || 1);
            spawnText(`+${qty} ${pretty}`, miningPositionRef.current.x, miningPositionRef.current.y - 16, color, iconUrl);
    
            setTimeout(() =>
              spawnText(`+${res.data.expGained} Mining XP`, miningPositionRef.current.x, miningPositionRef.current.y - 32, color),
            400);
    
            // update pickaxe durability, lure hits, etc…
            setEquippedPickaxe(ep => {
              if (!ep) return null;
              if (res.data.pickaxeBroken) return null;
              if (res.data.pickaxeDurability == null) return ep;
              const nd = Number(res.data.pickaxeDurability);
              return nd > 0 ? { ...ep, durability: nd } : null;
            });
            setExpLureHits(h => Math.max(0, h - 1));
            setDropLureHits(h => Math.max(0, h - 1));
          }
        } catch (e) {
          // Always use the position from the ref to ensure we're using the saved position
          const pos = miningPositionRef.current || miningPosition;
          spawnText(
            `You must equip a pickaxe before mining.`,
            pos.x,
            pos.y - 16,
            "red"
          );
        } finally {
          // 5) Unlock / clear the breaking flag
          setBreaking(b => {
            const { [rock.id]: _, ...rest } = b;
            return rest;
          });
        }
      }, 750);
    }
  };

  // —— Berry Plant Handlers ——
  const handlePlantClick = async (slotIndex) => {
    if (berryPlantInFlightRef.current.has(slotIndex)) {
      return;
    }
    berryPlantInFlightRef.current.add(slotIndex);
   
    // Check if player has selected a berry
    if (!selectedAction || selectedAction.type !== "berry") {
      spawnText("Select a berry from your pouch first!", playerPos.x, playerPos.y - 16, "#ff0000");
      berryPlantInFlightRef.current.delete(slotIndex);
      return;
    }
    
    const berryId = selectedAction.payload.item;
    
    
    // Check if player has the selected berry in inventory
    const selectedBerry = inventory.find(item => item.item === berryId);
    
    if (!selectedBerry || selectedBerry.amount < 1) {
      spawnText("You don't have any of that berry!", playerPos.x, playerPos.y - 16, "#ff0000");
      berryPlantInFlightRef.current.delete(slotIndex);
      return;
    }
    

    try {
      const response = await api.post(
        "/v1/berry-plants/plant",
        { slot_index: slotIndex, berry_id: berryId, map_id: MAP_ID },
        { withCredentials: true }
      );

      if (response.data.success) {
        spawnText(`Planted ${response.data.berry_name}!`, response.data.position.x, response.data.position.y - 16, "#00ff00");
        
        // Immediately deduct berry from inventory (optimistic update)
        setInventory((inv) => {
          const updated = inv.map((i) =>
            i.item === berryId ? { ...i, amount: i.amount - 1 } : i
          ).filter(i => i.amount > 0); // Remove if amount reaches 0
          
          // Clear selection if we just used the last berry
          const berryStillExists = updated.find(i => i.item === berryId);
          if (!berryStillExists) {
            setSelectedAction(null);
            localStorage.removeItem("selectedBerry");
          }
          
          return updated;
        });
        
        refreshBerryPlants();
      }
    } catch (error) {
      console.error("Error planting berry:", error);
      const errorMsg = error.response?.data?.detail || "Failed to plant berry";
      spawnText(errorMsg, playerPos.x, playerPos.y - 16, "#ff0000");
      
      // Reload inventory on error to sync with server
      api.get("/v1/inventory", { withCredentials: true })
        .then((r) => {
          const filtered = r.data.filter((i) =>
            [2, 3, 4, 63, 141, 142, 143, 199,200, 234,167, 235, 116, 117, 6, 7, 8, 9, 10, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007,4008, 214, 701780, 701781].includes(i.item) &&
            i.amount > 0
          );
          setInventory(filtered);
        }).catch(console.error);
    } finally {
      berryPlantInFlightRef.current.delete(slotIndex);
    }
  };

  const handleHarvestClick = async (plant) => {
    if (!plant.is_ready) {
      const remainingPercent = Math.round(100 - plant.growth_percent);
      spawnText(`Not ready yet! ${remainingPercent}% to go`, plant.x, plant.y - 16, "#ffaa00");
      return;
    }

    try {
      const response = await api.post(
        "/v1/berry-plants/harvest",
        { slot_index: plant.slot_index, map_id: MAP_ID },
        { withCredentials: true }
      );

      if (response.data.success) {
        spawnText(
          `Harvested ${response.data.harvest_amount} ${response.data.berry_name}!`,
          plant.x,
          plant.y - 16,
          "#ffd700"
        );
        
        // Display farming EXP gained
        if (response.data.farming_exp) {
          setTimeout(() => {
            spawnText(
              `+${response.data.farming_exp} Farming EXP`,
              plant.x,
              plant.y - 32,
              "#10b981"
            );
          }, 250);
        }
        
        refreshBerryPlants();
        // Reload inventory to reflect harvested berries
        api.get("/v1/inventory", { withCredentials: true })
          .then((r) => {
            const filtered = r.data.filter((i) =>
              [2, 3, 4, 63, 141, 142, 143, 199,200,201,202,203,204,205,206,207,208,209,210, 234,167, 235, 116, 117, 6, 7, 8, 9, 10, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007,4008, 214, 701780, 701781].includes(i.item) &&
              i.amount > 0
            );
            setInventory(filtered);
          }).catch(console.error);
      }
    } catch (error) {
      console.error("Error harvesting berry:", error);
      const errorMsg = error.response?.data?.detail || "Failed to harvest berry";
      spawnText(errorMsg, plant.x, plant.y - 16, "#ff0000");
    }
  };

  

  const computeShinyOdds = (copies = 0) => {
    const denom = Math.max(1, 4096 / Math.pow(2, copies));
    const basePct = (1 / denom) * 100;
    const bonusPct = (shinySkillBonus || 0) * 100;
    return Math.min(100, basePct + bonusPct);
  };

  // —— Use lure —— 
  const handleLureClick = useCallback(async (itemId) => {
    try {
      const res = await api.post(
        "/v1/use_item",
        { item_id: itemId },
        { withCredentials: true }
      );
      if (res.data.success) {
        setInventory((inv) =>
          inv
            .map((i) =>
              i.item === itemId ? { ...i, amount: i.amount - 1 } : i
            )
            .filter((i) => i.amount > 0)
        );
        spawnText(res.data.message, playerPos.x, playerPos.y - 16, "#fff");
        if ([214, 701780, 701781].includes(itemId)) {
          if (itemId === 214) setActiveLure("lure");
          else if (itemId === 701780) setActiveLure("super_lure");
          else setActiveLure("ultra_lure");
          setLureExpire(res.data.expiration);
        } else if (itemId === 234) {
          setExpLureHits((h) => h + 50);
          spawnText(
            "+50 Mining XP hits!",
            playerPos.x,
            playerPos.y - 32,
            "#ffe066",
            mineLureImg
          );
        } else if (itemId === 235) {
          setDropLureHits((h) => h + 50);
          spawnText(
            "+50 Drop‐rate hits!",
            playerPos.x,
            playerPos.y - 32,
            "#ffe066",
            dropLureImg
          );
        }
      } else {
        spawnText(
          res.data.error || "Failed to use lure",
          playerPos.x,
          playerPos.y - 16,
          "red"
        );
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
    const msg = detail || "Failed to use lure";
    console.error("use_item error:", err);
    spawnText(msg, playerPos.x, playerPos.y - 16, "red");
    }
  }, [api, playerPos, setInventory, spawnText, setActiveLure, setLureExpire, setExpLureHits, setDropLureHits]);

// 1️⃣ wrap handleLureClick so it auto‐fires, closes pouch & clears selection
const firePotion = React.useCallback(async (itemId) => {
  await handleLureClick(itemId);
  setShowPouch(false);
  setSelectedAction(null);
}, [handleLureClick]);

// 2️⃣ this is the only setter you hand to InventryPane
const handleSetSelectedAction = React.useCallback(action => {
  if (action.type === 'item' && [234, 235,214,6,7,8,9,10, 701780, 701781].includes(action.payload)) {
    // potions fire immediately
    firePotion(action.payload);
  }
  else if (action.type === 'ball') {
    // select ball in state
    setSelectedBall(action.payload);
    setSelectedAction(action);
    // persist selection
    localStorage.setItem(
      'selectedBall',
      action.payload.item.toString()
    );
  }
  else {
    // rocks or true lures
    setSelectedAction(action);
  }
}, [firePotion]);




  // —— WebSocket & reconnection logic —— 
  useEffect(() => {
    let retryDelay = 2000; // start with 2s retry
    function connectWebSocket() {
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const host =
        window.location.hostname === "localhost" ? "localhost:8000" : "pokemonradiance.com";
      ws.current = new WebSocket(`${proto}://${host}/ws/pokemon`);

      ws.current.onopen = () => {
        retryDelay = 2000; // reset delay on successful open
        const token = localStorage.getItem("jwtToken");
        if (ws.current.readyState === 1) {
          ws.current.send(JSON.stringify({ map_id: MAP_ID, token: token }));
        }
      };

      ws.current.onmessage = ({ data }) => {
        const d = JSON.parse(data);
        
        // Check for error messages
        if (d.error) {
          return;
        }
        
        // Handle portal information
        if (d.portals) {
          setPortals(d.portals);
        } else {
          setPortals([]);
        }
        
        // Check if pokemon array exists before filtering
        if (d.pokemon) {
          // Only show non‐captured Pokémon, but preserve existing step values for animation
          // Also preserve fishing Pokemon (client-side spawned)
          setWildPokemon(prev => {
            // Keep fishing Pokemon (those with IDs starting with 'fishing_')
            const fishingPokemon = prev.filter(p => p.id && p.id.toString().startsWith('fishing_'));
            
            const newPokemon = d.pokemon.filter((p) => !p.captured);
            const mappedPokemon = newPokemon.map(newPoke => {
              const existing = prev.find(p => p.id === newPoke.id);
              // Ensure step is always a valid number (0-3), defaulting to 0 if invalid
              const step = (existing && typeof existing.step === 'number' && !isNaN(existing.step)) 
                ? existing.step 
                : (typeof newPoke.step === 'number' && !isNaN(newPoke.step) ? newPoke.step : 0);
              const forcedReveal = Boolean(newPoke?.is_team_rocket) && String(newPoke?.team_rocket_mode) === "aggro";
              const revealed = forcedReveal || Boolean(existing?.team_rocket_revealed) || Boolean(newPoke.team_rocket_revealed);
              return { ...newPoke, step, team_rocket_revealed: revealed };
            });

            // Combine server Pokemon with fishing Pokemon
            return [...mappedPokemon, ...fishingPokemon];
          });
        } else {
          // Handle case where pokemon array is missing - keep fishing Pokemon
          console.warn("WebSocket response missing pokemon array:", d);
          setWildPokemon(prev => prev.filter(p => p.id && p.id.toString().startsWith('fishing_')));
        }
        
        // Handle day/night cycle information
        if (d.isNight !== undefined) {
          setIsNight(d.isNight);
        }
        
        // Only set expiration timer if expiresAt exists and is in the future
        if (d.expiresAt) {
          const ms = new Date(d.expiresAt) - new Date();
          clearTimeout(expireTO.current);
          
          // Only set timer if expiration is more than 5 seconds in the future
          // This prevents immediate re-requests and conflicts with backend timing
          if (ms > 5000) {
            expireTO.current = setTimeout(() => {
              if (ws.current?.readyState === 1) {
                ws.current.send(
                  JSON.stringify({
                    map_id: MAP_ID,
                    token: localStorage.getItem("jwtToken"),
                  })
                );
              }
            }, ms);
          }
        }
      };

      ws.current.onerror = (e) => {
        console.error("WebSocket error:", e);
      };

      ws.current.onclose = () => {
        clearTimeout(expireTO.current);
        reconnectTO.current = setTimeout(() => {
          connectWebSocket();
          retryDelay = Math.min(retryDelay * 1.5, 20000);
        }, retryDelay);
      };
    }
    connectWebSocket();
    return () => {
      clearTimeout(expireTO.current);
      clearTimeout(reconnectTO.current);
      if (ws.current) {
        ws.current.onopen = null;
        ws.current.onmessage = null;
        ws.current.onerror = null;
        ws.current.onclose = null;
        ws.current.close();
      }
    };
  }, [MAP_ID]);

  
  if (inBattle && initialBattleData) {
    return (
      <BattleScreen
      initialData={initialBattleData}
      onExit={() => { setInBattle(false); setInitialBattleData(null); }}
      onBattleEnd={handleBattleEnd}
      battleBgUrl={config.battleBackground || "/Battle/green.png"}
    />
    );
  }

  // Handle portal click - simplified version that always teleports
  const handlePortalClick = (portal) => {
    // Show teleport message
    spawnText(
      "Entering Halloween Event...",
      playerPos.x,
      playerPos.y - 16,
      "#9400D3" // Purple color for Halloween theme
    );
    
    // Navigate to Halloween event map after a short delay
    setTimeout(() => {
      navigate("/map/HalloweenEvent");
    }, 1000);
  };

  const handleTrainerClick = async (trainer) => {

  if (isMyIsland && decorRemoveMode && trainer?.owned_npc_id) {
    try {
      await api.post(
        "/v1/island/trainer-npcs/unplace",
        { owned_npc_id: Number(trainer?.owned_npc_id) || 0, map_id: MY_ISLAND_MAP_ID },
        { withCredentials: true }
      );
      spawnText("Removed!", playerPos.x, playerPos.y - 16, "#22c55e");
      refreshOwnedTrainerNpcs();
      refreshOwnedGameCornerNpcs();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to remove";
      spawnText(msg, playerPos.x, playerPos.y - 16, "#f87171");
    }
    return;
  }

  // Check if this is a Game Corner NPC (flagged during mapping to trainerStates)
  if (trainer?.isGameCornerNpc) {
    // Game Corner NPCs open their respective modals
    const npcType = trainer?.npc_type;
    switch (npcType) {
      case 'racetrack':
        setRacetrackModalOpen(true);
        break;
      case 'raffle':
        setRaffleModalOpen(true);
        break;
      case 'other':
        // Check npc_name for Balloon Pop or Diglett Dash
        const npcName = String(trainer?.npc_name || '').toLowerCase();
        if (npcName.includes('balloon')) {
          setBalloonPopModalOpen(true);
        } else if (npcName.includes('diglett')) {
          setDiglettDashModalOpen(true);
        }
        break;
      default:
        // Unknown NPC type, do nothing
        break;
    }
    return;
  }
 
  try {
    const r = await api.get("/v1/roster", { withCredentials: true });
    const anyAlive = r.data.some((p) => p.current_hp > 0);
    if (!anyAlive) {
      spawnText(
        "All your Pokémon are fainted! You can't battle trainers.",
        playerPos.x,
        playerPos.y - 16,
        "red"
      );
      return;
    }
  } catch (err) {
    console.error("Failed to fetch roster:", err);
    spawnText(
      "Could not check your roster. Try again later.",
      playerPos.x,
      playerPos.y - 16,
      "red"
    );
    return;
  }

  try {
    const leaderId = config?.gymGate?.leaderTrainerId;
    const requiredIds = Array.isArray(config?.gymGate?.requiredTrainerIds)
      ? config.gymGate.requiredTrainerIds
      : null;

    if (
      typeof leaderId === "number" &&
      Number.isFinite(leaderId) &&
      trainer?.trainer_id === leaderId &&
      requiredIds &&
      requiredIds.length > 0
    ) {
      const idsToCheck = Array.from(new Set([leaderId, ...requiredIds]));
      const results = await Promise.all(
        idsToCheck.map((id) =>
          api
            .get(`/v1/special-trainers/${id}/state`, { withCredentials: true })
            .then((r) => ({ id, data: r?.data }))
            .catch(() => ({ id, data: null }))
        )
      );

      const leaderState = results.find((x) => x.id === leaderId)?.data;
      const leaderIsSpecial = Boolean(leaderState?.is_special);
      const leaderDefeated = Boolean(leaderState?.defeated);

      if (leaderIsSpecial && leaderDefeated) {
        setActiveNPC({
          id: `trainer-${trainer.trainer_id}`,
          name: trainer.trainer_name,
          dialogue: leaderState?.dialogue_after || "",
          buttonText: "Close",
          secondaryButtonText: null,
          onSecondary: null,
        });
        setDialogueModalOpen(true);
        return;
      }

      const missing = requiredIds.filter((rid) => {
        const st = results.find((x) => x.id === rid)?.data;
        return !Boolean(st?.defeated);
      });

      if (missing.length > 0) {
        setActiveNPC({
          id: `trainer-${trainer.trainer_id}-gate`,
          name: trainer.trainer_name,
          dialogue: "You must defeat all of my apprentices before you can challenge me.",
          buttonText: "Close",
          secondaryButtonText: null,
          onSecondary: null,
        });
        setDialogueModalOpen(true);
        return;
      }

      if (leaderIsSpecial) {
        setActiveNPC({
          id: `trainer-${trainer.trainer_id}`,
          name: trainer.trainer_name,
          dialogue: leaderState?.dialogue_before || "",
          buttonText: "Close",
          secondaryButtonText: "Battle",
          onSecondary: () => startTrainerBattle(trainer.trainer_id, trainer.trainer_name, trainer.owned_npc_id || null),
        });
        setDialogueModalOpen(true);
        return;
      }
    }

    const st = await api.get(`/v1/special-trainers/${trainer.trainer_id}/state`, { withCredentials: true });
    const data = st?.data;
    if (data?.is_special) {
      const defeated = Boolean(data?.defeated);
      setActiveNPC({
        id: `trainer-${trainer.trainer_id}`,
        name: trainer.trainer_name,
        dialogue: defeated ? (data?.dialogue_after || "") : (data?.dialogue_before || ""),
        buttonText: "Close",
        secondaryButtonText: defeated ? null : "Battle",
        onSecondary: defeated ? null : () => startTrainerBattle(trainer.trainer_id, trainer.trainer_name, trainer.owned_npc_id || null),
      });
      setDialogueModalOpen(true);
      return;
    }
  } catch (e) {
  }

  await startTrainerBattle(trainer.trainer_id, trainer.trainer_name, trainer.owned_npc_id || null);
};


  // —— Render —— 
  
  const scaledViewportWidth = VIEWPORT_WIDTH * mapScale;
  const scaledViewportHeight = VIEWPORT_HEIGHT * mapScale;
  const mapShellWidth = Math.min(scaledViewportWidth, viewportSize.width);
  const mapShellHeight = Math.min(scaledViewportHeight, Math.max(0, viewportSize.height - CONTROLS_ROW_HEIGHT_PX));

  const controlsDisabled =
    inBattle ||
    showPouch ||
    showFishingShop ||
    achievementsModalOpen ||
    dialogueModalOpen ||
    raffleModalOpen ||
    luckyDipModalOpen ||
    collectionContestModalOpen ||
    fishingMissionsModalOpen ||
    farmingMissionsModalOpen ||
    auctionHouseModalOpen ||
    racetrackModalOpen ||
    wheelOfFortuneModalOpen ||
    diglettDashModalOpen ||
    balloonPopModalOpen ||
    ticketExchangeModalOpen ||
    receiverPowerModalOpen ||
    playerShopLotModalOpen ||
    playerShopBrowseModalOpen ||
    playerShopManageModalOpen ||
    departmentStoreModalOpen ||
    islandShopModalOpen;

// ...

return (
  <div className="layout-container">
    <style>{`
          .floating-text {
            position: absolute;
            pointer-events: none;
            font: bold 12px sans-serif;
            animation: float-and-fade 6s ease-out forwards;
            text-shadow: 1px 1px 2px rgba(0,0,0,0.6);
            z-index: 10;
          }
          @keyframes float-and-fade {
            0% { transform: translateY(0); opacity: 1; }
            100% { transform: translateY(-30px); opacity: 0; }
          }
          .health-bar {
            width: 100%;
            height: 6px;
            background-color: #333;
            border-radius: 3px;
            overflow: hidden;
            margin-top: 4px;
            box-shadow: 0 0 2px rgba(0,0,0,0.5) inset;
          }
          .health-bar-fill {
            height: 100%;
            border-radius: 3px;
            transition: width 0.3s ease;
            box-shadow: 0 0 3px rgba(255,255,255,0.3);
          }
          .health-bar-pickaxe {
            background-color: #f0ad4e;
            background-image: linear-gradient(to bottom, #f0ad4e, #c77c19);
          }
          .health-bar-exp {
            background-color: #5bc0de;
            background-image: linear-gradient(to bottom, #5bc0de, #2390b0);
          }
          .health-bar-drop {
            background-color: #5cb85c;
            background-image: linear-gradient(to bottom, #5cb85c, #3d8b3d);
          }

          .receiver-electric-overlay {
            position: absolute;
            left: -10px;
            top: -10px;
            width: calc(100% + 20px);
            height: calc(100% + 20px);
            pointer-events: none;
            z-index: 2;
            transform: translateZ(0);
            will-change: opacity, filter, transform;
          }

          .receiver-electric-glow {
            position: absolute;
            inset: 0;
            border-radius: 12px;
            background:
              radial-gradient(circle at 50% 50%, rgba(253, 224, 71, 0.28), rgba(253, 224, 71, 0) 60%),
              radial-gradient(circle at 30% 30%, rgba(250, 204, 21, 0.20), rgba(250, 204, 21, 0) 65%),
              radial-gradient(circle at 70% 65%, rgba(255, 255, 255, 0.16), rgba(255, 255, 255, 0) 62%);
            filter: blur(0.6px) drop-shadow(0 0 6px rgba(250, 204, 21, 0.25));
            animation: receiverGlowPulse 1.6s cubic-bezier(0.4, 0, 0.2, 1) infinite;
          }

          .receiver-electric-arcs {
            position: absolute;
            inset: 0;
            border-radius: 12px;
            background:
              repeating-linear-gradient(120deg,
                rgba(250, 204, 21, 0.0) 0px,
                rgba(250, 204, 21, 0.0) 10px,
                rgba(250, 204, 21, 0.26) 12px,
                rgba(250, 204, 21, 0.0) 18px,
                rgba(250, 204, 21, 0.0) 28px
              ),
              repeating-linear-gradient(40deg,
                rgba(253, 224, 71, 0.0) 0px,
                rgba(253, 224, 71, 0.0) 14px,
                rgba(253, 224, 71, 0.18) 16px,
                rgba(253, 224, 71, 0.0) 22px,
                rgba(253, 224, 71, 0.0) 34px
              );
            mix-blend-mode: screen;
            opacity: 0.88;
            filter: blur(0.15px) saturate(1.15);
            animation:
              receiverArcsMove 1.25s linear infinite,
              receiverArcBreath 1.9s ease-in-out infinite;
          }

          .receiver-electric-border {
            position: absolute;
            inset: 4px;
            border-radius: 10px;
            border: 1px solid rgba(253, 224, 71, 0.38);
            box-shadow:
              0 0 9px rgba(250, 204, 21, 0.22),
              0 0 16px rgba(253, 224, 71, 0.12),
              inset 0 0 10px rgba(255, 255, 255, 0.08);
            animation: receiverSparkFlicker 1.55s ease-in-out infinite;
          }

          .receiver-electric-spark {
            position: absolute;
            width: 3px;
            height: 3px;
            border-radius: 999px;
            background: rgba(255, 255, 255, 0.90);
            box-shadow:
              0 0 6px rgba(253, 224, 71, 0.65),
              0 0 12px rgba(250, 204, 21, 0.35);
            opacity: 0;
          }

          .receiver-electric-spark.spark1 {
            left: 18%;
            top: 24%;
            animation: receiverSparkPop 1.05s ease-in-out infinite;
          }

          .receiver-electric-spark.spark2 {
            left: 74%;
            top: 34%;
            animation: receiverSparkPop 1.35s ease-in-out infinite;
            animation-delay: 0.18s;
          }

          .receiver-electric-spark.spark3 {
            left: 56%;
            top: 72%;
            animation: receiverSparkPop 1.22s ease-in-out infinite;
            animation-delay: 0.31s;
          }

          @keyframes receiverGlowPulse {
            0% { transform: scale(1); opacity: 0.70; }
            50% { transform: scale(1.03); opacity: 1; }
            100% { transform: scale(1); opacity: 0.70; }
          }

          @keyframes receiverArcsMove {
            0% { background-position: 0px 0px, 0px 0px; }
            100% { background-position: 40px -24px, -34px 32px; }
          }

          @keyframes receiverArcBreath {
            0% { opacity: 0.62; }
            50% { opacity: 0.96; }
            100% { opacity: 0.68; }
          }

          @keyframes receiverSparkFlicker {
            0% { opacity: 0.50; }
            30% { opacity: 0.92; }
            60% { opacity: 0.68; }
            100% { opacity: 0.82; }
          }

          @keyframes receiverSparkPop {
            0% { transform: scale(0.6); opacity: 0; }
            30% { transform: scale(1.25); opacity: 1; }
            60% { transform: scale(0.9); opacity: 0.35; }
            100% { transform: scale(0.6); opacity: 0; }
          }
          
          @keyframes float-and-fade {
            0% { transform: translateY(0); opacity: 1; }
            100% { transform: translateY(-30px); opacity: 0; }
          }
          
          .health-bar {
            width: 100%;
            height: 6px;
            background-color: #333;
            border-radius: 3px;
            overflow: hidden;
            margin-top: 4px;
            box-shadow: 0 0 2px rgba(0,0,0,0.5) inset;
          }
          .health-bar-fill {
            height: 100%;
            border-radius: 3px;
            transition: width 0.3s ease;
            box-shadow: 0 0 3px rgba(255,255,255,0.3);
          }
          .health-bar-pickaxe {
            background-color: #f0ad4e;
            background-image: linear-gradient(to bottom, #f0ad4e, #c77c19);
          }
          .health-bar-exp {
            background-color: #5bc0de;
            background-image: linear-gradient(to bottom, #5bc0de, #2390b0);
          }
          .health-bar-drop {
            background-color: #5cb85c;
            background-image: linear-gradient(to bottom, #5cb85c, #3d8b3d);
          }
        `}</style>

      <div className="map-section">
        <div className="map-wrapper">
          <div className="map-scale-shell" style={{ width: mapShellWidth, height: mapShellHeight, overflow: 'hidden' }}>
            <div
              className="map-scale-wrapper"
              style={{
                '--map-scale': mapScale,
              }}
            >
              <div
                ref={gameContainerRef}
                className="game-container"
                style={{ width: VIEWPORT_WIDTH, height: VIEWPORT_HEIGHT }}
              >
              
{/* Commented out - lure hits now shown in BagItemsCard */}
{/* {(expLureHits > 0 || dropLureHits > 0) && (
  <div
    style={{
      position: "absolute",
      bottom: 8,
      left: 8,
      zIndex: 1000,
      background: "rgba(0,0,0,0.5)",
      padding: "4px 8px",
      borderRadius: "4px",
      color: "#fff",
      fontSize: "0.75rem",
      textAlign: "left",
      pointerEvents: "none",
      minWidth: "40px",
    }}
  >
    {expLureHits > 0 && (
      <div style={{ marginBottom: "4px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src={mineLureImg} alt="Mining Lure" style={{ width: 20, height: 20 }} />
        </div>
        <div className="health-bar">
          <div 
            className="health-bar-fill health-bar-exp" 
            style={{ 
              width: `${Math.min((expLureHits / 50) * 100, 100)}%` 
            }}
          />
        </div>
      </div>
    )}
    {dropLureHits > 0 && (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src={dropLureImg} alt="Drop Lure" style={{ width: 20, height: 20 }} />
        </div>
        <div className="health-bar">
          <div 
            className="health-bar-fill health-bar-drop" 
            style={{ 
              width: `${Math.min((dropLureHits / 50) * 100, 100)}%` 
            }}
          />
        </div>
      </div>
    )}
  </div>
)} */}


         

              
              
              {/* Ring of Department button moved to BagItemsCard */}

              {/* Held Item Swap button moved to BagItemsCard */}

              {/* Map overlay */}
              
              <div
                className="map-overlay"
                style={{
                  backgroundImage: `url(${overImageUrl})`,
                  backgroundPosition: `-${camX}px -${camY}px`,
                }}
              />

              {/* Map base */}
              <div
                className="map-visibility"
                style={{
                  backgroundImage: `url(${mapImageUrl})`,
                  backgroundPosition: `-${camX}px -${camY}px`,
                  zIndex: 1,
                }}
              />

              {isMyIsland && (placingDecor || (decorMoveMode && selectedMovePlacementId)) && !decorRemoveMode && (
                <div
                  onMouseMove={handlePlacementMouseMove}
                  onClick={handlePlacementMapClick}
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: VIEWPORT_WIDTH,
                    height: VIEWPORT_HEIGHT,
                    zIndex: 9998,
                    background: "transparent",
                    pointerEvents: "auto",
                    cursor: (decorMoveMode && selectedMovePlacementId) ? "crosshair" : (canPlaceGhost ? "crosshair" : "not-allowed"),
                  }}
                />
              )}

              {isMyIsland && trainerNpcMode === "move" && !placingDecor && !decorRemoveMode && (
                <div
                  onClick={handleTrainerNpcMapClick}
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: VIEWPORT_WIDTH,
                    height: VIEWPORT_HEIGHT,
                    zIndex: 9997,
                    background: "transparent",
                    pointerEvents: "auto",
                    cursor: "crosshair",
                  }}
                />
              )}

              {isMyIsland && gameCornerNpcMode === "move" && !placingDecor && !decorRemoveMode && (
                <div
                  onClick={handleGameCornerNpcMapClick}
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: VIEWPORT_WIDTH,
                    height: VIEWPORT_HEIGHT,
                    zIndex: 9997,
                    background: "transparent",
                    pointerEvents: "auto",
                    cursor: "crosshair",
                  }}
                />
              )}

              {/* My Island placed decor layer */}
              {isMyIsland && (sortedDecorPlacements || []).map((p) => {
                const screenX = (Number(p?.x) || 0) - camX;
                const screenY = (Number(p?.y) || 0) - camY;
                const w = Number(p?.sprite_w) || 16;
                const h = Number(p?.sprite_h) || 16;
                const isFlowers = String(p?.decor_key || "") === "flowers" || String(p?.sprite_path || "").includes("/flowers.png");
                const isFountain = String(p?.decor_key || "") === "fountain" || String(p?.sprite_path || "").includes("/fountain.png");
                const isCrater = String(p?.decor_key || "") === "crater";
                const isSpawnPoint = String(p?.decor_key || "") === "spawn-point";
                const isReceiver = String(p?.decor_key || "") === "reciever";
                const isDrill = String(p?.decor_key || "") === "drill";
                const isHut = ["hut", "junglehut", "tinyhome"].includes(String(p?.decor_key || ""));
                const isFarmingShop = String(p?.decor_key || "") === "farmingshop";
                const powerReceivers = receiverPowerModalOpen
                  ? (receiverPowerStatus?.receivers || [])
                  : (receiverWorldPowerStatus?.receivers || []);
                const receiverRow = isReceiver
                  ? powerReceivers.find((r) => Number(r?.receiver_placement_id) === Number(p?.placement_id))
                  : null;
                const receiverActive = Boolean(receiverRow?.pokemon_id) && Number(receiverRow?.pokemon_id) > 0;
                const poweredDrills = receiverWorldPowerStatus?.powered_drills || [];
                const drillPowered = isDrill && poweredDrills.includes(Number(p?.placement_id));
                const isSelectedForRemoval = Number(selectedRemovalPlacementId) === Number(p?.placement_id);
                const isAssigningHere = (trainerNpcMode === "assign" || gameCornerNpcMode === "assign") && isHut;

                if (screenX < -w || screenX > VIEWPORT_WIDTH + w || screenY < -h || screenY > VIEWPORT_HEIGHT + h) {
                  return null;
                }

                const spriteBottom = (Number(p?.y) || 0) + h;
                const collisionBottom =
                  (Number(p?.y) || 0) +
                  (Number(p?.collision_y) || 0) +
                  (Number(p?.collision_h) || 0);
                const feetY = Math.max(spriteBottom, collisionBottom);
                const zIndex = (isCrater || isSpawnPoint) ? myIslandZBase + 1 : myIslandZBase + Math.round(Number(feetY) || 0);

                const farmMeta = isFarmingShop
                  ? (berryShopPower.byShopPlacementId?.[Number(p?.placement_id) || 0] || null)
                  : null;
                const farmPowered = farmMeta ? Boolean(farmMeta.powered) : true;

                return (
                  <div
                    key={`decor-${p.placement_id}`}
                    onClick={(e) => {
                      e?.stopPropagation?.();
                      if (isAssigningHere) {
                        handleHutClickForAssignment(p);
                        return;
                      }
                      if (decorRemoveMode) {
                        setSelectedRemovalPlacementId(Number(p?.placement_id) || null);
                        return;
                      }
                      if (decorMoveMode) {
                        setSelectedMovePlacementId(Number(p?.placement_id) || null);
                        setMovingDecor(p);
                        return;
                      }
                      if (isDrill) {
                        setDrillPlacementId(Number(p?.placement_id) || null);
                        setDrillModalOpen(true);
                        return;
                      }
                      if (!isReceiver) return;
                      setReceiverPowerPlacementId(Number(p?.placement_id) || null);
                      setReceiverPowerModalOpen(true);
                      setReceiverPowerError(null);
                    }}
                    style={{
                      position: "absolute",
                      left: screenX,
                      top: screenY,
                      width: w,
                      height: h,
                      overflow: "visible",
                      backgroundImage: `url(${p.sprite_path})`,
                      backgroundSize: isFlowers ? "80px 16px" : isFountain ? `${w * 5}px ${h}px` : `${w}px ${h}px`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: isFlowers || isFountain ? "0 0" : undefined,
                      ...(isFlowers ? { animation: "flowersAnim 0.9s steps(5) infinite" } : isFountain ? { animation: "flowersAnim 1.05s steps(5) infinite", ["--flowers-sheet-end"]: `-${w * 5}px` } : null),
                      imageRendering: "pixelated",
                      zIndex,
                      opacity: (isFarmingShop && !farmPowered) || (decorMoveMode && selectedMovePlacementId === Number(p?.placement_id)) ? 0.55 : 1,
                      pointerEvents: decorRemoveMode || decorMoveMode || isReceiver || isDrill || isAssigningHere ? "auto" : "none",
                      outline: isSelectedForRemoval ? "2px solid rgba(239,68,68,0.95)" : (decorMoveMode && selectedMovePlacementId === Number(p?.placement_id)) ? "2px solid rgba(59,130,246,0.95)" : "none",
                      outlineOffset: 1,
                      filter: isSelectedForRemoval
                        ? "drop-shadow(0 0 4px rgba(239,68,68,0.8))"
                        : (decorMoveMode && selectedMovePlacementId === Number(p?.placement_id))
                        ? "drop-shadow(0 0 4px rgba(59,130,246,0.8))"
                        : (isFarmingShop && !farmPowered ? "grayscale(1) brightness(0.75)" : undefined),
                      cursor: decorRemoveMode || decorMoveMode || isReceiver || isDrill || isAssigningHere ? "pointer" : "default",
                    }}
                  >
                    {isDrill && drillPowered && (
                      <>
                        {[...Array(3)].map((_, i) => (
                          <div
                            key={`steam-${i}`}
                            style={{
                              position: "absolute",
                              left: `${30 + i * 15}%`,
                              bottom: "80%",
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              background: "rgba(200, 200, 200, 0.6)",
                              animation: `steamRise ${1.5 + i * 0.3}s ease-in-out infinite`,
                              animationDelay: `${i * 0.5}s`,
                              pointerEvents: "none",
                            }}
                          />
                        ))}
                      </>
                    )}
                    {isHut && (() => {
                      const hutPlacementId = Number(p?.placement_id) || 0;
                      const assignedTrainerNpc = ownedTrainerNpcs.find(
                        (npc) => Number(npc?.hut_placement_id) === hutPlacementId
                      );
                      const assignedGameCornerNpc = ownedGameCornerNpcs.find(
                        (npc) => Number(npc?.hut_placement_id) === hutPlacementId
                      );
                      const assignedNpc = assignedTrainerNpc || assignedGameCornerNpc;
                      
                      if (!assignedNpc) return null;
                      
                      const npcName = assignedNpc?.trainer_name || assignedNpc?.npc_name || "NPC";
                      
                      return (
                        <div
                          style={{
                            position: "absolute",
                            bottom: "100%",
                            left: "50%",
                            transform: "translateX(-50%)",
                            backgroundColor: "rgba(0, 0, 0, 0.8)",
                            color: "white",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontSize: "10px",
                            fontWeight: "bold",
                            whiteSpace: "nowrap",
                            pointerEvents: "none",
                            marginBottom: "2px",
                            zIndex: 1000,
                          }}
                        >
                          {npcName}
                        </div>
                      );
                    })()}
                    {isReceiver && receiverActive && (
                      <div className="receiver-electric-overlay">
                        <div className="receiver-electric-glow" />
                        <div className="receiver-electric-arcs" />
                        <div className="receiver-electric-border" />
                        <div className="receiver-electric-spark spark1" />
                        <div className="receiver-electric-spark spark2" />
                        <div className="receiver-electric-spark spark3" />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* My Island placement ghost */}
              {ghostDecor && (
                (() => {
                  const isFlowers =
                    String(ghostDecor?.decor_key || "") === "flowers" ||
                    String(ghostDecor?.sprite_path || "").includes("/flowers.png");
                  const isFountain =
                    String(ghostDecor?.decor_key || "") === "fountain" ||
                    String(ghostDecor?.sprite_path || "").includes("/fountain.png");

                  return (
                <div
                  style={{
                    position: "absolute",
                    left: (Number(ghostDecor.x) || 0) - camX,
                    top: (Number(ghostDecor.y) || 0) - camY,
                    width: Number(ghostDecor.sprite_w) || 16,
                    height: Number(ghostDecor.sprite_h) || 16,
                    backgroundImage: `url(${ghostDecor.sprite_path})`,
                    backgroundSize: isFlowers
                      ? "80px 16px"
                      : isFountain
                        ? `${(Number(ghostDecor.sprite_w) || 16) * 5}px ${Number(ghostDecor.sprite_h) || 16}px`
                        : `${Number(ghostDecor.sprite_w) || 16}px ${Number(ghostDecor.sprite_h) || 16}px`,
                    backgroundRepeat: "no-repeat",
                    ...(isFlowers
                      ? { animation: "flowersAnim 0.9s steps(5) infinite" }
                      : isFountain
                        ? { animation: "flowersAnim 1.05s steps(5) infinite", ["--flowers-sheet-end"]: `-${(Number(ghostDecor.sprite_w) || 16) * 5}px` }
                        : null),
                    imageRendering: "pixelated",
                    zIndex: myIslandZBase + Math.round((Number(ghostDecor.y) || 0) + (Number(ghostDecor.sprite_h) || 16)) + 1,
                    pointerEvents: "none",
                    opacity: canPlaceGhost ? 0.45 : 0.35,
                    filter: canPlaceGhost ? "grayscale(1)" : "grayscale(1) drop-shadow(0 0 2px rgba(248,113,113,0.7))",
                    outline: canPlaceGhost ? "1px dashed rgba(255,255,255,0.25)" : "1px dashed rgba(248,113,113,0.65)",
                    outlineOffset: -1,
                  }}
                />
                  );
                })()
              )}

              {/* My Island placement/removal controls (in-map HUD) */}
              {isMyIsland && (placingDecor || decorRemoveMode || trainerNpcMode) && (
                <div
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 9999,
                    display: "flex",
                    gap: 8,
                    pointerEvents: "auto",
                  }}
                >
                  {trainerNpcMode && (
                    <button
                      onClick={() => {
                        exitTrainerNpcMode();
                      }}
                      style={{
                        background: "rgba(239, 68, 68, 0.16)",
                        border: "1px solid rgba(239, 68, 68, 0.75)",
                        color: "#fff",
                        borderRadius: 10,
                        padding: "8px 12px",
                        cursor: "pointer",
                        fontWeight: 900,
                        fontSize: 12,
                        backdropFilter: "blur(4px)",
                      }}
                      title="Cancel"
                    >
                      Cancel
                    </button>
                  )}

                  {!decorRemoveMode && !decorMoveMode && (
                    <>
                      <button
                        onClick={() => {
                          setDecorMoveMode(true);
                          setSelectedDecor(null);
                          setMouseGhostPos(null);
                          setSelectedMovePlacementId(null);
                          setMovingDecor(null);
                        }}
                        style={{
                          background: "rgba(59,130,246,0.12)",
                          border: "1px solid rgba(59,130,246,0.75)",
                          color: "#fff",
                          borderRadius: 10,
                          padding: "8px 12px",
                          cursor: "pointer",
                          fontWeight: 900,
                          fontSize: 12,
                          backdropFilter: "blur(4px)",
                        }}
                        title="Move existing decor"
                      >
                        Move Mode
                      </button>
                      <button
                        onClick={() => {
                          setDecorRemoveMode(true);
                          setSelectedDecor(null);
                          setMouseGhostPos(null);
                          setSelectedRemovalPlacementId(null);
                        }}
                        style={{
                          background: "rgba(239,68,68,0.12)",
                          border: "1px solid rgba(239,68,68,0.75)",
                          color: "#fff",
                          borderRadius: 10,
                          padding: "8px 12px",
                          cursor: "pointer",
                          fontWeight: 900,
                          fontSize: 12,
                          backdropFilter: "blur(4px)",
                        }}
                        title="Remove decor"
                      >
                        Remove Mode
                      </button>
                    </>
                  )}

                  {decorMoveMode && (
                    <button
                      onClick={() => {
                        setDecorMoveMode(false);
                        setSelectedMovePlacementId(null);
                        setMovingDecor(null);
                      }}
                      style={{
                        background: "rgba(59,130,246,0.24)",
                        border: "1px solid rgba(59,130,246,0.75)",
                        color: "#fff",
                        borderRadius: 10,
                        padding: "8px 12px",
                        cursor: "pointer",
                        fontWeight: 900,
                        fontSize: 12,
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      {selectedMovePlacementId ? 'Cancel Move' : 'Exit Move Mode'}
                    </button>
                  )}

                  {decorRemoveMode && Boolean(selectedRemovalPlacementId) && (
                    <button
                      onClick={() => {
                        const target = (decorPlacements || []).find(
                          (p) => Number(p?.placement_id) === Number(selectedRemovalPlacementId)
                        );
                        if (!target) return;
                        handleRemovePlacement(target);
                        setSelectedRemovalPlacementId(null);
                      }}
                      style={{
                        background: "rgba(239,68,68,0.24)",
                        border: "1px solid rgba(239,68,68,0.75)",
                        color: "#fff",
                        borderRadius: 10,
                        padding: "8px 12px",
                        cursor: "pointer",
                        fontWeight: 900,
                        fontSize: 12,
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      Remove Selected
                    </button>
                  )}

                  {!decorRemoveMode && placingDecor && (
                    <button
                      onClick={() => {
                        if (!ghostDecor) return;
                        if (!canPlaceGhost) {
                          spawnText("Cannot place here", ghostDecor.x, ghostDecor.y - 12, "#f87171");
                          return;
                        }
                        handlePlaceDecorAt(ghostDecor.x, ghostDecor.y);
                      }}
                      disabled={!ghostDecor || !canPlaceGhost}
                      style={{
                        background: canPlaceGhost ? "rgba(34, 197, 94, 0.22)" : "rgba(34, 197, 94, 0.10)",
                        border: "1px solid rgba(34, 197, 94, 0.75)",
                        color: "#fff",
                        borderRadius: 10,
                        padding: "8px 12px",
                        cursor: ghostDecor && canPlaceGhost ? "pointer" : "not-allowed",
                        fontWeight: 900,
                        fontSize: 12,
                        opacity: ghostDecor && canPlaceGhost ? 1 : 0.55,
                        backdropFilter: "blur(4px)",
                      }}
                      title={canPlaceGhost ? "Place" : "Cannot place here"}
                    >
                      Place
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setSelectedDecor(null);
                      setMouseGhostPos(null);
                      setDecorRemoveMode(false);
                      setSelectedRemovalPlacementId(null);
                      exitTrainerNpcMode();
                    }}
                    style={{
                      background: "rgba(239, 68, 68, 0.16)",
                      border: "1px solid rgba(239, 68, 68, 0.75)",
                      color: "#fff",
                      borderRadius: 10,
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontWeight: 900,
                      fontSize: 12,
                      backdropFilter: "blur(4px)",
                    }}
                    title="Exit"
                  >
                    Exit
                  </button>
                </div>
              )}
              
              {/* Night effect overlay - only shown during nighttime */}
              {isNight && (
                <div
                  className="night-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 40, 0.5)',
                    pointerEvents: 'none',
                    zIndex: 2
                  }}
                />
              )}

              {/* {showDebugZones && config?.npcs && (
                <>
                  {config.npcs.map((npc, idx) => {
                    const npcWidth = Number(npc.width || 16);
                    const npcHeight = Number(npc.height || 24);
                    const screenX = Number(npc.x || 0) - camX;
                    const screenY = Number(npc.y || 0) - camY;
                    const isFusion = String(npc.id || "").startsWith("BerryFusionStation");

                    return (
                      <div
                        key={`npc-debug-${npc.id || idx}`}
                        style={{
                          position: "absolute",
                          left: screenX,
                          top: screenY,
                          width: npcWidth,
                          height: npcHeight,
                          border: isFusion ? "2px solid rgba(250,204,21,0.95)" : "1px solid rgba(239,68,68,0.85)",
                          background: isFusion ? "rgba(250,204,21,0.20)" : "rgba(239,68,68,0.08)",
                          zIndex: 9999,
                          pointerEvents: "none",
                          boxSizing: "border-box",
                        }}
                      />
                    );
                  })}
                </>
              )} */}
              {showDebugZones && (
                <>
                  {collisionAreas.map((area, idx) => {
                    const screenX = area.x - camX;
                    const screenY = area.y - camY;
                    return (
                      <div
                        key={idx}
                        className="collision-overlay"
                        style={{
                          left: screenX,
                          top: screenY,
                          width: area.width,
                          height: area.height,
                        }}
                      />
                    );
                  })}
                </>
              )}
              
              {/* Teleport Zones Layer - Commented out after debugging
                  <TeleportZonesLayer
                    teleportZones={teleportZones}
                    cameraPos={{ x: camX, y: camY }}
                  />
              */}

              {/* Character - Hidden during fishing animation */}
              {!isFishing && (
                <div
                  className="character"
                  style={{
                    left: snapPlayerX - camX,
                    top: snapPlayerY - camY,
                    backgroundImage: `url(${characterSpriteUrl})`,
                    backgroundPosition: `-${playerStep * 32}px -${getPlayerSpriteRowOffset(
                      direction
                    )}px`,
                    ...(isMyIsland ? { zIndex: myIslandZBase + Math.round(Number(playerBottomEdge) || 0) } : null),
                  }}
                />
              )}

              {/* Floating texts */}
              {floatingTexts.map((ft) => {
                const sx = ft.x - camX,
                  sy = ft.y - camY;
                return (
                  <div
                    key={ft.id}
                    className="floating-text"
                    style={{ left: sx, top: sy, color: ft.color, zIndex: isMyIsland ? 9000 : 20 }}
                  >
                    <span style={{ marginRight: ft.imageUrl ? 4 : 0 }}>
                      {ft.text}
                    </span>
                    {ft.imageUrl && (
                      <img
                        src={ft.imageUrl}
                        alt=""
                        style={{ width: 16, height: 16, verticalAlign: "middle" }}
                      />
                    )}
                  </div>
                );
              })}

           
              {/* Steam effects for EmberSpa or maps with showSteamEffects */}
              {(MAP_ID === 12 || (config && config.showSteamEffects)) && steamPuffs.map((puff) => {
                const sx = puff.x - camX;
                const sy = puff.y - camY;
                const elapsed = Date.now() - puff.startTime;
                const progress = Math.min(elapsed / puff.duration, 1);

                // Steam rises and fades
                const riseDistance = progress * 24; // Rise 24px total
                const currentOpacity = puff.opacity * (1 - progress * 0.8); // Fade to 20% of original
                const currentSize = puff.size * (1 + progress * 0.5); // Grow slightly

                return (
                  <div
                    key={puff.id}
                    style={{
                      position: 'absolute',
                      left: sx,
                      top: sy - riseDistance,
                      width: currentSize,
                      height: currentSize,
                      borderRadius: '50%',
                      background: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.9) 0%, rgba(240, 248, 255, 0.7) 50%, rgba(220, 230, 240, 0.4) 80%, transparent 100%)',
                      boxShadow: 'inset 0 0 2px rgba(255, 255, 255, 0.8), 0 0 3px rgba(255, 255, 255, 0.3)',
                      opacity: currentOpacity,
                      pointerEvents: 'none',
                      zIndex: 8 // Above map but below UI
                    }}
                  />
                );
              })}

              {/* Rocks Layer */}
              <RocksLayer
                rocks={rocks}
                camX={camX}
                camY={camY}
                playerY={playerPos.y}
                onRockClick={handleRockClick}
                breakingFrames={breakingFrames}
              />

              {(MAP_ID === 1 || MAP_ID === MY_ISLAND_MAP_ID) && berryPlants.map((plant) => {
                const [x, y] = [plant.x, plant.y];
                const screenX = x - camX;
                const screenY = y - camY;

                const playerFeetY = (Number(playerPos?.y) || 0) + 32;
                const plantFeetY = (Number(plant?.y) || 0) + 16;
                const plantZIndex = isMyIsland
                  ? myIslandZBase + Math.round(Number(plantFeetY) || 0) + 50
                  : playerFeetY > plantFeetY
                    ? 6
                    : 8;

                // Only render if visible
                if (
                  screenX < -32 || screenX > VIEWPORT_WIDTH ||
                  screenY < -32 || screenY > VIEWPORT_HEIGHT
                ) {
                  return null;
                }

                // Backend already calculates sprite, frame_offset, and sprite_height
                const spriteUrl = plant.sprite;
                const frameOffset = plant.frame_offset;
                const spriteHeight = plant.sprite_height || 16;
                const spriteWidth = plant.sprite_width || 16;

                const mulchId = plant?.mulch?.mulch_item_id;
                const mulchClass = mulchId === 141 ? "mulch-gains" : mulchId === 142 ? "mulch-time" : "";

                return (
                  <React.Fragment key={plant.id}>
                    <div
                      className={`berry-plant${mulchClass ? ` ${mulchClass}` : ""}`}
                      style={{
                        position: "absolute",
                        left: screenX,
                        top: screenY - (spriteHeight - 16), // Adjust Y position for taller sprites
                        width: spriteWidth,
                        height: spriteHeight,
                        cursor: plant.is_ready ? "pointer" : selectedAction?.type === "mulch" ? "pointer" : "default",
                        zIndex: plantZIndex,
                        pointerEvents: "auto",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();

                        if (plant.is_ready) {
                          handleHarvestClick(plant);
                          return;
                        }

                        if (selectedAction?.type === "mulch") {
                          handleApplyMulchToPlant(plant);
                          return;
                        }

                        spawnText(
                          `Growing: ${Math.round(plant.growth_percent)}%`,
                          plant.x,
                          plant.y - 16,
                          "#00aaff"
                        );
                      }}
                      title={plant.is_ready ? "Click to harvest!" : `Growing: ${Math.round(plant.growth_percent)}%`}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          backgroundImage: `url(${spriteUrl})`,
                          backgroundPosition: `-${frameOffset}px 0px`,
                          backgroundSize: `auto ${spriteHeight}px`,
                          backgroundRepeat: "no-repeat",
                          imageRendering: "pixelated",
                        }}
                      />

                      {mulchId && (
                        <div className="mulch-overlay">
                          <div className="mulch-spark" />
                          <div className="mulch-spark" />
                          <div className="mulch-spark" />
                          <div className="mulch-spark" />
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}

              {(MAP_ID === 1 || MAP_ID === MY_ISLAND_MAP_ID) && (() => {
                let allSlots = [];
                if (MAP_ID === 1) {
                  allSlots = Array.from({ length: BERRY_PLANT_POSITIONS.length }, (_, i) => i);
                } else {
                  allSlots = Object.keys(soilbedSlotPositions || {}).map((k) => Number(k));
                }
                const occupiedSlots = berryPlants.map(p => p.slot_index);
                const emptySlots = allSlots.filter(i => !occupiedSlots.includes(i));
                
                // Check if player has a berry selected
                const hasBerrySelected = selectedAction?.type === "berry";
                
   
         
                return emptySlots.map(slotIndex => {
                  const xy = MAP_ID === 1 ? BERRY_PLANT_POSITIONS[slotIndex] : soilbedSlotPositions?.[slotIndex];
                  if (!xy) return null;
                  const [x, y] = xy;
                  const screenX = x - camX;
                  const screenY = y - camY;
                  
                  // Only render if visible
                  if (
                    screenX < -32 || screenX > VIEWPORT_WIDTH ||
                    screenY < -32 || screenY > VIEWPORT_HEIGHT
                  ) {
                    return null;
                  }

                  // Make more visible when berry is selected
                  const isActive = hasBerrySelected;

    

                  return (
                    <div
                      key={`empty-${slotIndex}`}
                      style={{
                        position: "absolute",
                        left: screenX,
                        top: screenY,
                        width: 16,
                        height: 16,
                        // border: isActive ? "2px solid rgba(100, 255, 100, 0.8)" : "1px dashed rgba(100, 200, 100, 0.3)",
                        // backgroundColor: isActive ? "rgba(50, 255, 50, 0.2)" : "rgba(50, 150, 50, 0.05)",
                        cursor: isActive ? "pointer" : "default",
                        // boxSizing: "border-box",
                        transition: "all 0.2s ease",
                        pointerEvents: isActive ? "auto" : "none",
                        zIndex: MAP_ID === MY_ISLAND_MAP_ID ? myIslandZBase + Math.round(Number(y) || 0) + 16 : 100  // Make sure it's above other elements
                      }}
                      onClick={(e) => {
                       
                        if (isActive) {
                          handlePlantClick(slotIndex);
                        }
                      }}
                      title={isActive ? "Click to plant a berry" : "Select a berry from your pouch"}
                    />
                  );
                });
              })()}

<TrainersLayer
          trainers={trainerStates}
          camX={camX}
          camY={camY}
          playerX={playerPos.x}
          playerY={playerPos.y}
          onTrainerClick={handleTrainerClick}
          zBase={isMyIsland ? myIslandZBase : undefined}
        />

              <PokemonLayer
                pokemons={wildPokemon}
                camX={camX}
                camY={camY}
                onPokemonClick={handlePokemonClick}
                selectedBall={selectedBall}
                playerY={playerPos.y}
                zBase={isMyIsland ? myIslandZBase : undefined}
              />
              
              {/* Fishing Spots - Island + My Island */}
              {(MAP_ID === 1 || MAP_ID === MY_ISLAND_MAP_ID) && fishingSpots.length > 0 && !isFishing && (
                <FishingSpots
                  spots={fishingSpots}
                  onSpotClick={handleFishingSpotClick}
                  playerX={playerPos.x}
                  playerY={playerPos.y}
                  camX={camX}
                  camY={camY}
                />
              )}

              {/* Fishing Animation */}
              {fishingAnimation && (
                <FishingAnimation
                  x={fishingAnimation.x - camX}
                  y={fishingAnimation.y - camY}
                  direction={fishingAnimation.direction}
                  duration={fishingAnimation.duration}
                  zIndex={isMyIsland ? myIslandZBase + Math.round((fishingAnimation.y || 0) + 32) : 100}
                  onComplete={() => {
                    setFishingAnimation(null);
                    setIsFishing(false);
                  }}
                />
              )}
              
              {/* Portal Layer */}
              <PortalLayer
                portals={portals}
                camX={camX}
                camY={camY}
                onPortalClick={handlePortalClick}
              />
              
              {/* NPCs Layer */}
              <NPCLayer
                npcs={mapNpcs || []}
                camX={camX}
                camY={camY}
                playerX={playerPos.x}
                playerY={playerPos.y}
                onNPCClick={handleNPCClick}
              />
              
              {/* Shop Lots Layer - Only shown on the player shop map */}
              {config.id === 'playerShop' && (
                <ShopLotsLayer
                  camX={camX}
                  camY={camY}
                  onShopLotClick={handleNPCClick}
                />
              )}



              {/* Professional game-quality capture animations */}
              {captureEffects.map(effect => {
                // ...
                const screenX = effect.x - camX;
                const screenY = effect.y - camY;
                // Ball type is already mapped to CSS class name when the effect was created
                const ballTypeClass = effect.ballType || 'pokeball';
                
                // Success animations have been removed as requested
                if (effect.type === 'success') {
                  return null; // Don't render any success animations
                }
                
                // Breakout animations with ball-specific colors
                if (effect.type === 'breakout') {
                  const particleCount = ballTypeClass === 'ultraball' ? 12 : 
                                      ballTypeClass === 'greatball' ? 10 : 8;
                  
                  return (
                    <div key={effect.id} style={{position: 'absolute', left: screenX, top: screenY}}>
                      <div className={`breakout-burst ${ballTypeClass}`}></div>
                      <div className="breakout-particles">
                        {Array.from({length: particleCount}).map((_, i) => {
                          const angle = (i / particleCount) * Math.PI * 2;
                          const distance = 5 + Math.random() * 15;
                          const delay = Math.random() * 0.3;
                          const duration = 0.3 + Math.random() * 0.3;
                          const x = Math.cos(angle) * distance;
                          const y = Math.sin(angle) * distance;
                          
                          return (
                            <div 
                              key={`particle-${i}`} 
                              className={`breakout-particle ${ballTypeClass}`}
                              style={{
                                left: x + 16,
                                top: y + 16,
                                animation: `breakout-burst ${duration}s ease-out forwards`,
                                animationDelay: `${delay}s`
                              }}
                            ></div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                
                return null;
              })}
              </div>
            </div>
          </div>
        </div>

        {!controlsDisabled && (
          <div className="movement-controls-row">
            <div className="movement-controls-group" style={{ width: VIEWPORT_WIDTH }}>
              <div className="analog-stick-wrapper">
                {controlType === 'analog' ? (
                  <AnalogStick onMove={handleStick} />
                ) : (
                  <DPad onMove={handleDPad} />
                )}
              </div>

              {isMyIsland && (
                <button
                  type="button"
                  className="inventory-i-button"
                  aria-label="Decorate"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onPointerUp={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedDecor(null);
                    setDecorRemoveMode(false);
                    setMouseGhostPos(null);
                    setShowDecorPanel(true);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedDecor(null);
                    setDecorRemoveMode(false);
                    setMouseGhostPos(null);
                    setShowDecorPanel(true);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedDecor(null);
                    setDecorRemoveMode(false);
                    setMouseGhostPos(null);
                    setShowDecorPanel(true);
                  }}
                >
                  <img
                    src="/Maps/objects_maps/tree.png"
                    alt="Decorate"
                    className="inventory-i-button-icon"
                    style={{ imageRendering: "pixelated", width: 30, height: 30, objectFit: "contain" }}
                  />
                </button>
              )}

              <button
                type="button"
                className="inventory-i-button"
                aria-label="Open Bag"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onPointerUp={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  togglePouch();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  togglePouch();
                }}
              >
                <img
                  src={pouchIcon}
                  alt="Open Bag"
                  className="inventory-i-button-icon"
                />
              </button>
            </div>
          </div>
        )}
      </div>

      {showPouch && (
        <div
          className="pouch-overlay"
          style={{
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100dvh",
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            overflowY: "auto",
            padding: "16px",
            boxSizing: "border-box",
          }}
          onClick={togglePouch}
        >
          <div
            style={{
              maxWidth: "calc(100vw - 32px)",
              width: "100%",
              maxHeight: "calc(100dvh - 32px)",
              overflowY: "auto",
              display: "flex",
              justifyContent: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <InventoryPanel
              inventory={inventory}
              gems={gems}
              selectedAction={selectedAction}
              setSelectedAction={setSelectedAction}
              handleLureClick={handleLureClick}
              resetRocks={resetRocks}
              spawnText={spawnText}
              playerPos={playerPos}
              onMineralPotionUse={handleMineralPotionUse}
              onClose={togglePouch}
              activeLure={activeLure}
              expLureHits={expLureHits}
              dropLureHits={dropLureHits}
              activeGummy={activeGummy}
            />
          </div>
        </div>
      )}

      {receiverPowerModalOpen && (
        <div
          className="receiver-power-overlay"
          onClick={() => {
            setReceiverPowerModalOpen(false);
            setReceiverPowerPlacementId(null);
            setReceiverPowerStatus(null);
            setReceiverSelectedPokemonId("");
            setReceiverPowerError(null);
          }}
        >
          <div
            className="receiver-power-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const fmt = (n) => {
                const v = Number(n);
                if (!Number.isFinite(v)) return "0";
                try {
                  return Math.floor(v).toLocaleString();
                } catch {
                  return String(Math.floor(v));
                }
              };

              const electric = "rgba(56,189,248,0.95)";
              const electricSoft = "rgba(56,189,248,0.22)";

              const receivers = receiverPowerStatus?.receivers || [];
              const chargers = receiverPowerStatus?.chargers || [];
              const selectedReceiver = receivers.find(
                (r) => Number(r?.receiver_placement_id) === Number(receiverPowerPlacementId)
              );

              const prettyChargerName = (k) => {
                const key = String(k || "");
                if (key === "batterycharger") return "Battery Storage";
                if (key === "bigbatterycharger") return "Big Battery Storage";
                return key || "Charger";
              };

              const receiverPlacement = (decorPlacements || []).find(
                (p) => Number(p?.placement_id) === Number(receiverPowerPlacementId)
              );
              const rx = Number(receiverPlacement?.x) || 0;
              const ry = Number(receiverPlacement?.y) || 0;
              const rW = Number(receiverPlacement?.sprite_w) || 0;
              const rH = Number(receiverPlacement?.sprite_h) || 0;
              const rxCenter = rx + rW / 2;
              const ryCenter = ry + rH / 2;

              const chargerPlacements = (decorPlacements || []).filter(
                (p) => ["batterycharger", "bigbatterycharger"].includes(String(p?.decor_key || ""))
              );
              const chargersWithPos = chargerPlacements.map((cp) => {
                const st = chargers.find((c) => Number(c?.charger_placement_id) === Number(cp?.placement_id)) || null;
                const cx = Number(cp?.x) || 0;
                const cy = Number(cp?.y) || 0;
                const cW = Number(cp?.sprite_w) || 0;
                const cH = Number(cp?.sprite_h) || 0;
                const cxCenter = cx + cW / 2;
                const cyCenter = cy + cH / 2;
                const inRange = Math.abs(cxCenter - rxCenter) <= 100 && Math.abs(cyCenter - ryCenter) <= 100;
                const cap = Number(st?.capacity) || 0;
                const stored = Number(st?.stored_voltage) || 0;
                const pct = cap > 0 ? Math.max(0, Math.min(100, (stored / cap) * 100)) : 0;
                return {
                  placement_id: cp?.placement_id,
                  decor_key: cp?.decor_key,
                  name: prettyChargerName(cp?.decor_key),
                  x: cx,
                  y: cy,
                  inRange,
                  capacity: cap,
                  stored_voltage: stored,
                  percent: pct,
                };
              });

              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div className="receiver-power-header">
                    <div className="receiver-power-title">
                      Power Receiver
                    </div>
                    <button
                      onClick={() => {
                        setReceiverPowerModalOpen(false);
                        setReceiverPowerPlacementId(null);
                        setReceiverPowerStatus(null);
                        setReceiverSelectedPokemonId("");
                        setReceiverPowerError(null);
                      }}
                      className="receiver-close-btn"
                    >
                      Close
                    </button>
                  </div>

                  {receiverPowerError && (
                    <div className="receiver-error">
                      {String(receiverPowerError)}
                    </div>
                  )}

                  <div className="receiver-section">
                    <div className="receiver-section-title">
                      Assigned Pokémon
                    </div>
                    {selectedReceiver?.pokemon_id && selectedReceiver?.sprite_path ? (
                      <div className="receiver-sprite-container">
                        <img 
                          src={selectedReceiver.sprite_path} 
                          alt={selectedReceiver.pokemon_name}
                          className="receiver-sprite-image"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div className="receiver-pokemon-info">
                            <div className="receiver-info-row">
                              <span className="receiver-info-label">Pokémon:</span>
                              <span className="receiver-info-value">
                                {selectedReceiver?.pokemon_name || "None"}
                              </span>
                            </div>
                            <div className="receiver-info-row">
                              <span className="receiver-info-label">Stored voltage:</span>
                              <span className="receiver-info-value electric">{fmt(selectedReceiver?.stored_voltage || 0)}</span>
                            </div>
                            <div className="receiver-info-row">
                              <span className="receiver-info-label">Level:</span>
                              <span className="receiver-info-value">{fmt(selectedReceiver?.level || 0)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="receiver-pokemon-info">
                        <div className="receiver-info-row">
                          <span className="receiver-info-label">Pokémon:</span>
                          <span className="receiver-info-value">
                            {selectedReceiver?.pokemon_name || "None"}
                          </span>
                        </div>
                        <div className="receiver-info-row">
                          <span className="receiver-info-label">Stored voltage:</span>
                          <span className="receiver-info-value electric">{fmt(selectedReceiver?.stored_voltage || 0)}</span>
                        </div>
                        <div className="receiver-info-row">
                          <span className="receiver-info-label">Level:</span>
                          <span className="receiver-info-value">{fmt(selectedReceiver?.level || 0)}</span>
                        </div>
                      </div>
                    )}
                    <div className="receiver-controls">
                      <select
                        value={receiverSelectedPokemonId}
                        onChange={(e) => setReceiverSelectedPokemonId(e.target.value)}
                        className="receiver-select"
                      >
                        <option value={""}>Select Electric Pokémon…</option>
                        {(receiverEligiblePokemon || []).map((pp) => (
                          <option key={`recv-p-${pp?.id}`} value={String(pp?.id || "")}>
                            {String(pp?.name || "")} (Lv {fmt(pp?.level || 0)})
                          </option>
                        ))}
                      </select>

                      <button
                        disabled={!receiverSelectedPokemonId || receiverPowerLoading}
                        onClick={async () => {
                          const pid = Number(receiverSelectedPokemonId);
                          if (!pid) return;
                          try {
                            setReceiverPowerLoading(true);
                            await api.post(
                              "/v1/island/decor/receiver/assign",
                              {
                                receiver_placement_id: Number(receiverPowerPlacementId),
                                pokemon_id: pid,
                                map_id: MY_ISLAND_MAP_ID,
                              },
                              { withCredentials: true }
                            );
                            setReceiverSelectedPokemonId("");
                            const eligRes = await api.get("/v1/island/decor/receiver/eligible-pokemon", { withCredentials: true });
                            setReceiverEligiblePokemon(eligRes?.data?.data || []);
                          } catch (e) {
                            setReceiverPowerError(e?.response?.data?.detail || "Failed to assign");
                          } finally {
                            setReceiverPowerLoading(false);
                          }
                        }}
                        className="receiver-btn receiver-btn-assign"
                      >
                        Assign
                      </button>

                      <button
                        disabled={!selectedReceiver?.pokemon_id || receiverPowerLoading}
                        onClick={async () => {
                          try {
                            setReceiverPowerLoading(true);
                            await api.post(
                              "/v1/island/decor/receiver/unassign",
                              {
                                receiver_placement_id: Number(receiverPowerPlacementId),
                                map_id: MY_ISLAND_MAP_ID,
                              },
                              { withCredentials: true }
                            );
                            const eligRes = await api.get("/v1/island/decor/receiver/eligible-pokemon", { withCredentials: true });
                            setReceiverEligiblePokemon(eligRes?.data?.data || []);
                          } catch (e) {
                            setReceiverPowerError(e?.response?.data?.detail || "Failed to unassign");
                          } finally {
                            setReceiverPowerLoading(false);
                          }
                        }}
                        className="receiver-btn receiver-btn-unassign"
                      >
                        Unassign
                      </button>
                    </div>
                  </div>

                  <div className="receiver-section">
                    <div className="receiver-section-title">
                      Live Voltage
                    </div>
                    <div style={{ fontSize: 12 }}>
                      {(() => {
                        const attackers = Number(selectedReceiver?.attackers_nearby) || 0;
                        const mult = Number(selectedReceiver?.generation_multiplier);
                        const hasMult = Number.isFinite(mult);
                        const pct = hasMult ? Math.max(-1, Math.min(1, mult)) : 1;

                        const perHour = Number(selectedReceiver?.voltage_per_hour) || 0;
                        const perHourBase = Number(selectedReceiver?.voltage_per_hour_base);
                        const showBase = Number.isFinite(perHourBase) && perHourBase > 0 && Math.floor(perHourBase) !== Math.floor(perHour);

                        return (
                          <>
                            <div>
                              <span style={{ opacity: 0.8 }}>Receiver buffer:</span>{" "}
                              <span style={{ fontWeight: 900 }}>{fmt(selectedReceiver?.stored_voltage || 0)}</span>
                            </div>
                            <div>
                              <span style={{ opacity: 0.8 }}>Charging into nearby chargers (all receivers):</span>{" "}
                              {(() => {
                                // Sum moved_to_chargers across ALL receivers
                                const allReceivers = receiverPowerStatus?.receivers || [];
                                const totalMoved = allReceivers.reduce((sum, r) => sum + (Number(r?.moved_to_chargers) || 0), 0);
                                const dtSec = Number(selectedReceiver?.tick_dt_sec) || 0;
                                const movedPerHour = dtSec > 0 ? Math.floor((totalMoved / dtSec) * 3600) : 0;
                                return (
                                  <>
                                    <span style={{ fontWeight: 900 }}>{fmt(totalMoved)}</span>
                                    <span style={{ opacity: 0.75 }}> / tick</span>
                                    {dtSec > 0 ? (
                                      <span style={{ opacity: 0.85 }}> ({fmt(movedPerHour)} / hour)</span>
                                    ) : null}
                                  </>
                                );
                              })()}
                            </div>
                            <div>
                              <span style={{ opacity: 0.8 }}>Consumption (all consumers):</span>{" "}
                              {(() => {
                                const drainTotal = Number(receiverPowerStatus?.drain_total) || 0;
                                const drainDt = Number(receiverPowerStatus?.drain_dt_sec) || 0;
                                const drainPerHour = drainDt > 0 ? Math.floor((drainTotal / drainDt) * 3600) : 0;
                                return (
                                  <>
                                    <span style={{ fontWeight: 900 }}>{fmt(drainTotal)}</span>
                                    <span style={{ opacity: 0.75 }}> / tick</span>
                                    {drainDt > 0 ? (
                                      <span style={{ opacity: 0.85 }}> ({fmt(drainPerHour)} / hour)</span>
                                    ) : null}
                                  </>
                                );
                              })()}
                            </div>
                            <div>
                              <span style={{ opacity: 0.8 }}>Aggressors nearby:</span>{" "}
                              <span style={{ fontWeight: 900 }}>{fmt(attackers)}</span>
                            </div>
                            <div>
                              <span style={{ opacity: 0.8 }}>Generation:</span>{" "}
                              <span style={{ fontWeight: 900 }}>{Math.round(pct * 100)}%</span>
                            </div>
                            <div>
                              <span style={{ opacity: 0.8 }}>Generation per hour:</span>{" "}
                              <span style={{ fontWeight: 900 }}>{fmt(perHour)}</span>
                              {showBase ? (
                                <span style={{ opacity: 0.85 }}> (base {fmt(perHourBase)})</span>
                              ) : null}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="receiver-section">
                    <div className="receiver-section-title">
                      Consumers
                    </div>
                    <div className="receiver-section-subtitle">
                      Voltage is stored in chargers. Consumers only draw from nearby chargers.
                    </div>
                    {(() => {
                      const shops = (decorPlacements || []).filter((p) => String(p?.decor_key || "") === "farmingshop");
                      const buildings = (decorPlacements || []).filter((p) => ["hut", "junglehut", "tinyhome"].includes(String(p?.decor_key || "")));
                      const drills = (decorPlacements || []).filter((p) => String(p?.decor_key || "") === "drill");
                      
                      if (!shops.length && !buildings.length && !drills.length) {
                        return <div className="receiver-empty">No consumers placed.</div>;
                      }
                      return (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {shops.map((sp) => {
                            const pid = Number(sp?.placement_id) || 0;
                            const meta = berryShopPower.byShopPlacementId?.[pid] || { powered: false, activated: false, canPower: false, totalVoltage: 0, availableVoltage: 0, inRangeCount: 0 };
                            const stored = Number(meta.totalVoltage) || 0;
                            const need = 50000;
                            const usagePerHour = Math.floor(need / 24);
                            const spritePath = sp?.sprite_path;
                            return (
                              <div key={`consumer-farm-${pid}`} className="receiver-item-card">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  {spritePath && (
                                    <img 
                                      src={spritePath} 
                                      alt="Farming Shop"
                                      className="receiver-building-sprite"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                      }}
                                    />
                                  )}
                                  <div style={{ flex: 1 }}>
                                    <div className="receiver-item-header">
                                      <div className="receiver-item-name">Farming Shop</div>
                                    </div>
                                    <div className="receiver-item-info">
                                      Charger voltage in range (shared): <strong>{fmt(stored)}</strong>
                                    </div>
                                    <div className="receiver-item-info">
                                      Usage: <strong>{fmt(need)}</strong> / day ({fmt(usagePerHour)} / hour)
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {buildings.map((bp) => {
                            const pid = Number(bp?.placement_id) || 0;
                            const buildingName = String(bp?.decor_key || "") === "hut" ? "Hut" : 
                                                String(bp?.decor_key || "") === "junglehut" ? "Jungle Hut" : 
                                                String(bp?.decor_key || "") === "tinyhome" ? "Tiny Home" : "Building";
                            const need = 20000;
                            const usagePerHour = Math.floor(need / 24);
                            const spritePath = bp?.sprite_path;
                            
                            return (
                              <div key={`consumer-building-${pid}`} className="receiver-item-card building">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  {spritePath && (
                                    <img 
                                      src={spritePath} 
                                      alt={buildingName}
                                      className="receiver-building-sprite"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                      }}
                                    />
                                  )}
                                  <div style={{ flex: 1 }}>
                                    <div className="receiver-item-header">
                                      <div className="receiver-item-name">{buildingName}</div>
                                      <div className="receiver-item-status active">Active</div>
                                    </div>
                                    <div className="receiver-item-info">
                                      Usage: <strong>{fmt(need)}</strong> / day ({fmt(usagePerHour)} / hour)
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {drills.map((dp) => {
                            const pid = Number(dp?.placement_id) || 0;
                            const poweredDrills = receiverPowerStatus?.powered_drills || [];
                            const isPowered = poweredDrills.includes(pid);
                            const need = 800000;
                            const usagePerHour = Math.floor(need / 24);
                            const spritePath = dp?.sprite_path;
                            
                            return (
                              <div key={`consumer-drill-${pid}`} className="receiver-item-card drill">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  {spritePath && (
                                    <img 
                                      src={spritePath} 
                                      alt="Drill"
                                      className="receiver-building-sprite"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                      }}
                                    />
                                  )}
                                  <div style={{ flex: 1 }}>
                                    <div className="receiver-item-header">
                                      <div className="receiver-item-name">⚙️ Drill</div>
                                      <div className="receiver-item-status" style={{ 
                                        color: isPowered ? '#10b981' : '#ef4444' 
                                      }}>
                                        {isPowered ? 'Powered' : 'No Power'}
                                      </div>
                                    </div>
                                    <div className="receiver-item-info">
                                      Usage: <strong>{fmt(need)}</strong> / day ({fmt(usagePerHour)} / hour)
                                    </div>
                                    <div className="receiver-item-info" style={{ fontSize: 12, opacity: 0.8 }}>
                                      Mines 1 mineral every 30 mins when powered
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {(() => {
                            const research = (decorPlacements || []).filter((p) => String(p?.decor_key || "") === "research");
                            return research.map((rp) => {
                              const pid = Number(rp?.placement_id) || 0;
                              const poweredResearch = receiverPowerStatus?.powered_research || [];
                              const isPowered = poweredResearch.includes(pid);
                              const need = 600000;
                              const usagePerHour = Math.floor(need / 24);
                              const spritePath = rp?.sprite_path;
                              
                              return (
                                <div key={`consumer-research-${pid}`} className="receiver-item-card research">
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    {spritePath && (
                                      <img 
                                        src={spritePath} 
                                        alt="Research Building"
                                        className="receiver-building-sprite"
                                        onError={(e) => {
                                          e.currentTarget.style.display = 'none';
                                        }}
                                      />
                                    )}
                                    <div style={{ flex: 1 }}>
                                      <div className="receiver-item-header">
                                        <div className="receiver-item-name">🔬 Research Building</div>
                                        <div className="receiver-item-status" style={{ 
                                          color: isPowered ? '#10b981' : '#ef4444' 
                                        }}>
                                          {isPowered ? 'Powered' : 'No Power'}
                                        </div>
                                      </div>
                                      <div className="receiver-item-info">
                                        Usage: <strong>{fmt(need)}</strong> / day ({fmt(usagePerHour)} / hour)
                                      </div>
                                      <div className="receiver-item-info" style={{ fontSize: 12, opacity: 0.8 }}>
                                        Unlocks new features via researcher NPC
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      );
                    })()}
                  </div>

                  <div className="receiver-section">
                    <div className="receiver-section-title">
                      Chargers
                    </div>
                    {chargersWithPos.length === 0 ? (
                      <div className="receiver-empty">No chargers placed.</div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {chargersWithPos.map((c) => (
                          <div
                            key={`charger-${c?.placement_id}`}
                            className={`receiver-item-card charger ${c?.inRange ? 'in-range' : ''}`}
                          >
                            <div className="receiver-item-header">
                              <div className="receiver-item-name">
                                {String(c?.name || c?.decor_key || "Charger")}
                              </div>
                              <div className="receiver-item-status" style={{ color: c?.inRange ? "#7dd3fc" : "#94a3b8" }}>
                                {c?.inRange ? "Near receiver" : "Far"}
                              </div>
                            </div>
                            <div className="receiver-item-info">
                              {fmt(c?.stored_voltage || 0)} / {fmt(c?.capacity || 0)} ({fmt(c?.percent || 0)}%)
                            </div>
                            <div className="receiver-progress-container">
                              <div className="receiver-progress-bar">
                                <div className="receiver-progress-fill active" style={{ width: `${c?.percent || 0}%` }} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {drillModalOpen && (
        <DrillModal
          drillPlacementId={drillPlacementId}
          drillStatus={drillStatus}
          drillLoading={drillLoading}
          onClose={() => {
            setDrillModalOpen(false);
            setDrillPlacementId(null);
            setDrillStatus(null);
          }}
          onUpdateGems={(mineralType, quantity) => {
            setGems(prev => ({
              ...prev,
              [mineralType]: (prev[mineralType] || 0) + quantity
            }));
          }}
        />
      )}

      {showFishingShop && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100dvh",
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            overflowY: "auto",
            padding: "16px",
            boxSizing: "border-box",
          }}
          onClick={toggleFishingShop}
        >
          <div
            style={{
              maxWidth: "calc(100vw - 32px)",
              width: "100%",
              maxHeight: "calc(100dvh - 32px)",
              overflowY: "auto",
              display: "flex",
              justifyContent: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <FishingShopPanel onClose={() => setShowFishingShop(false)} refreshEquippedItems={refreshEquippedItems} />
          </div>
        </div>
      )}

      {showBerryShop && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100dvh",
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            overflowY: "auto",
            padding: "16px",
            boxSizing: "border-box",
          }}
          onClick={toggleBerryShop}
        >
          <div
            style={{
              maxWidth: "calc(100vw - 32px)",
              width: "100%",
              maxHeight: "calc(100dvh - 32px)",
              overflowY: "auto",
              display: "flex",
              justifyContent: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <BerryShopPanel
              mapId={MAP_ID}
              shopPlacementId={activeBerryShopPlacementId}
              onClose={() => {
                setShowBerryShop(false);
                setActiveBerryShopPlacementId(null);
              }}
              refreshEquippedItems={refreshEquippedItems}
            />
          </div>
        </div>
      )}

      {showBerryFusion && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100dvh",
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            overflowY: "auto",
            padding: "16px",
            boxSizing: "border-box",
          }}
          onClick={toggleBerryFusion}
        >
          <div
            style={{
              maxWidth: "calc(100vw - 32px)",
              width: "100%",
              maxHeight: "calc(100dvh - 32px)",
              overflowY: "auto",
              display: "flex",
              justifyContent: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <BerryFusionPanel
              stationId={activeBerryFusionStationId}
              onClose={() => {
                setShowBerryFusion(false);
                setActiveBerryFusionStationId(null);
              }}
              refreshEquippedItems={refreshEquippedItems}
            />
          </div>
        </div>
      )}

      {/* Bag Items Card - Compact display of active items */}
      <BagItemsCard
        mineralPotionActive={mineralPotionActive}
        mineralPotionCountdown={mineralPotionCountdown}
        shinyPotionActive={shinyPotionActive}
        shinyPotionCountdown={shinyPotionCountdown}
        shinyCharm={shinyCharm}
        shinySkillBonus={shinySkillBonus}
        rodDurabilityBonus={rodDurabilityBonus}
        pickaxeEfficiency={pickaxeEfficiency}
        miningCharmStacks={miningCharmStacks}
        equippedPickaxe={equippedPickaxe}
        pickaxeMaxDurability={equippedPickaxe ? pickaxeMaxDurability[equippedPickaxe.item] : null}
        equippedRod={equippedRod}
        rodMaxDurability={
          equippedRod ? (rodConfig?.[equippedRod.item]?.max_durability || equippedRod.durability) : null
        }
        equippedItemFinder={equippedItemFinder}
        equippedCatchingGloves={equippedCatchingGloves}
        activeLure={activeLure}
        countdown={countdown}
        expLureHits={expLureHits}
        dropLureHits={dropLureHits}
        onOpenBag={togglePouch}
        onActiveGummyChange={setActiveGummy}
        spawnText={spawnText}
        playerPos={playerPos}
        equippedRings={equippedRings}
        onOpenHeldItemSwap={() => setShowHeldItemSwap(true)}
        onOpenDepartmentStore={() => {
          setDepartmentStoreModalOpen(true);
          
          // Fetch store items and currency data when opening
          Promise.all([
            api.get('/v1/store', { withCredentials: true }),
            api.get('/v1/profile', { withCredentials: true }),
            api.get('/v1/catchrate', { withCredentials: true })
          ])
            .then(([storeRes, currencyRes, catchRateRes]) => {
              // Filter store items to only include department store items
              const departmentStoreItems = (storeRes.data || []).filter(item => 
                // Pokeballs
                [2, 3, 4, 63, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007].includes(item.id) ||
                // Potions
                [29, 30, 31, 32].includes(item.id) ||
                // Revives
                [84, 85].includes(item.id)
              );
              setStoreItems(departmentStoreItems);
              setCurrency({ money: currencyRes.data.money, coins: currencyRes.data.coins });
              setCatchRates(catchRateRes.data);
            })
            .catch(console.error);
        }}
        onOpenAchievements={() => setAchievementsModalOpen(true)}
        onOpenDecorate={() => {
          if (!isMyIsland) {
            spawnText?.("Decorations only available on My Island.", playerPos?.x || 0, (playerPos?.y || 0) - 16, "#fbbf24");
            return;
          }
          setShowDecorPanel(true);
        }}
        showDecorateButton={isMyIsland}
      />

      {showDecorPanel && isMyIsland && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100dvh",
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            overflowY: "auto",
            padding: "16px",
            boxSizing: "border-box",
            pointerEvents: "auto",
          }}
          onClick={() => setShowDecorPanel(false)}
        >
          <div
            style={{
              maxWidth: "calc(100vw - 32px)",
              width: "100%",
              maxHeight: "calc(100dvh - 32px)",
              overflowY: "auto",
              display: "flex",
              justifyContent: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ width: "100%", maxWidth: 720 }}>
              <MyIslandDecorPanel
                isMobile={viewportSize.width <= 768}
                catalog={decorCatalog}
                placements={decorPlacements}
                trainerNpcCatalog={trainerNpcCatalog}
                ownedTrainerNpcs={ownedTrainerNpcs}
                gameCornerNpcCatalog={gameCornerNpcCatalog}
                ownedGameCornerNpcs={ownedGameCornerNpcs}
                selectedDecorId={selectedDecor?.decor_id}
                onSelectDecor={(d) => {
                  exitTrainerNpcMode();
                  setSelectedDecor(d);
                  setDecorRemoveMode(false);
                  setSelectedRemovalPlacementId(null);
                  setMouseGhostPos(null);
                  setShowDecorPanel(false);
                }}
                onBuyTrainerNpc={handleBuyTrainerNpc}
                onAssignTrainerNpc={enterAssignTrainerNpcMode}
                onMoveTrainerNpc={enterMoveTrainerNpcMode}
                onBuyGameCornerNpc={handleBuyGameCornerNpc}
                onAssignGameCornerNpc={enterAssignGameCornerNpcMode}
                onMoveGameCornerNpc={enterMoveGameCornerNpcMode}
                onRemovePlacement={handleRemovePlacement}
                onEnterRemoveMode={() => {
                  exitTrainerNpcMode();
                  setSelectedDecor(null);
                  setMouseGhostPos(null);
                  setDecorRemoveMode(true);
                  setSelectedRemovalPlacementId(null);
                  setShowDecorPanel(false);
                }}
                onClose={() => {
                  setShowDecorPanel(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {achievementsModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.65)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={() => setAchievementsModalOpen(false)}
        >
          <div
            style={{
              maxWidth: "calc(100vw - 32px)",
              width: "100%",
              maxHeight: "calc(100dvh - 32px)",
              overflowY: "auto",
              display: "flex",
              justifyContent: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Achievements embedded={true} />
          </div>
        </div>
      )}

      {/* Commented out - now shown in BagItemsCard */}
      {/* <MineralPotionIndicator /> */}
      {/* <ShinyPotionIndicator /> */}

      {/* NPC Dialogue Modal */}
      <DialogueModal
        isOpen={dialogueModalOpen}
        toggle={() => setDialogueModalOpen(false)}
        npc={activeNPC}
        onComplete={(npc) => {
          // Handle any post-dialogue actions here
          if (npc && npc.onComplete) {
            npc.onComplete();
          }
        }}
      />

      {/* Raffle Modal */}
      <RaffleModal
        isOpen={raffleModalOpen}
        toggle={() => setRaffleModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />
      
      {/* Department Store Modal */}
      <DepartmentStoreModal
        isOpen={departmentStoreModalOpen}
        toggle={() => setDepartmentStoreModalOpen(false)}
        filteredItems={activeCategory === 'all' ? 
          // Only show items that should be in the department store
          storeItems.filter(item => 
            // Pokeballs
            [2, 3, 4, 63, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007].includes(item.id) ||
            // Map items (lures, charms)
            [214, 701780, 701781, 194, 195].includes(item.id)
          ) : 
          // Filter by category
          storeItems.filter(item => {
            if (activeCategory === 'pokeballs') {
              return [2, 3, 4, 63, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007].includes(item.id);
            } else if (activeCategory === 'lures') {
              return [214, 701780, 701781].includes(item.id);
            } else if (activeCategory === 'charms') {
              return [194, 195].includes(item.id);
            }
            return true;
          })
        }
        currency={currency}
        categories={[
          { id: 'all', name: 'All Items' },
          { id: 'pokeballs', name: 'Pokéballs' },
          { id: 'lures', name: 'Lures' },
          { id: 'items', name: 'Other Items' }
        ]}
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        buyAmounts={quantityMap}
        handleQuantityChange={(id, value) => setQuantityMap(prev => ({ ...prev, [id]: value }))}
        handlePurchase={async (item) => {
          const qty = parseInt(quantityMap[item.id] || 1, 10);
          try {
            // Perform the purchase
            await api.post(
              "/v1/purchase",
              { item: item.id, quantity: qty },
              { withCredentials: true }
            );
            
            // Show success message
            spawnText(`Bought ${qty}× ${item.name}!`, playerPos.x, playerPos.y - 16, "#00ff00");
            
            // Update currency
            const currRes = await api.get("/v1/currency", { withCredentials: true });
            if (Array.isArray(currRes.data)) {
              if (currRes.data.length) setCurrency(currRes.data[0]);
            } else {
              setCurrency(currRes.data);
            }
          } catch (err) {
            const detail = err.response?.data?.detail || "Purchase failed.";
            spawnText(detail, playerPos.x, playerPos.y - 16, "red");
          }
        }}
        loadingItems={storeItems.length === 0}
        catcherExp={0}
        catcherLevel={0}
      />

      {/* Island Shop Modal */}
      {islandShopModalOpen && (
        <IslandShopModal
          onClose={() => setIslandShopModalOpen(false)}
          onPokemonChange={(slotIndex, newSpecies) => {
            // Update the Pokemon on the map immediately
            setWildPokemon(prev => {
              const updated = [...prev];
              // Find the Pokemon at this slot index (non-fishing Pokemon)
              const nonFishingPokemon = updated.filter(p => !p.id?.toString().startsWith('fishing_'));
              if (slotIndex < nonFishingPokemon.length) {
                const targetPokemon = nonFishingPokemon[slotIndex];
                const targetIndex = updated.findIndex(p => p.id === targetPokemon.id);
                if (targetIndex !== -1) {
                  updated[targetIndex] = {
                    ...updated[targetIndex],
                    species: newSpecies,
                    name: newSpecies,
                    sprite: `/Maps/Overworld/${newSpecies}.png`
                  };
                }
              }
              return updated;
            });
          }}
        />
      )}

      {/* Lucky Dip Modal */}
      <LuckyDipModal
        isOpen={luckyDipModalOpen}
        toggle={() => setLuckyDipModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />

      {/* Collection Contest Modal */}
      <CollectionContestModal
        isOpen={collectionContestModalOpen}
        toggle={() => setCollectionContestModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />

      {/* Fishing Missions Modal */}
      <FishingMissionsModal
        isOpen={fishingMissionsModalOpen}
        toggle={() => setFishingMissionsModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
        npc={activeNPC}
      />

      <FarmingMissionsModal
        isOpen={farmingMissionsModalOpen}
        toggle={() => setFarmingMissionsModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
        npc={activeNPC}
      />

      {/* Auction House Modal */}
      <AuctionHouseModal
        isOpen={auctionHouseModalOpen}
        toggle={() => setAuctionHouseModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />

<DiglettDashModal
        isOpen={diglettDashModalOpen}
        toggle={() => setDiglettDashModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />


      {/* Held Item Quick Swap Modal */}
      <HeldItemQuickSwap
        isVisible={showHeldItemSwap}
        onClose={() => setShowHeldItemSwap(false)}
        onSwapComplete={() => {
          refreshEquippedItems(); // Refresh equipped items display
          spawnText("Held item swapped!", playerPos.x, playerPos.y - 16, "green");
        }}
      />
      {/* Racetrack Modal */}
      <RacetrackModal
        isOpen={racetrackModalOpen}
        toggle={() => setRacetrackModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />
      {/* Wheel of Fortune Modal */}
      <WheelOfFortuneModal
        isOpen={wheelOfFortuneModalOpen}
        toggle={() => setWheelOfFortuneModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />
      
      {/* Balloon Pop Modal */}
      <BalloonPopModal
        isOpen={balloonPopModalOpen}
        toggle={() => setBalloonPopModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />
      
      {/* Ticket Exchange Modal */}
      <TicketExchangeModal
        isOpen={ticketExchangeModalOpen}
        toggle={() => setTicketExchangeModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />
      
      {/* Player Shop Lot Modal */}
      <PlayerShopLotModal
        isOpen={playerShopLotModalOpen}
        toggle={() => setPlayerShopLotModalOpen(false)}
      />
      
      {/* Player Shop Browse Modal */}
      <PlayerShopBrowseModal
        isOpen={playerShopBrowseModalOpen}
        toggle={() => setPlayerShopBrowseModalOpen(false)}
        lotId={selectedLotId}
        shopName={selectedShopName}
      />
      
      {/* Player Shop Management Modal */}
      <PlayerShopManageModal
        isOpen={playerShopManageModalOpen}
        toggle={() => setPlayerShopManageModalOpen(false)}
        lotId={selectedLotId}
      />
      
      {/* Researcher Modal */}
      {researcherModalOpen && (
        <ResearcherModal
          researchPlacementId={selectedResearchPlacementId}
          researchUnlocks={researchUnlocks}
          onClose={() => {
            setResearcherModalOpen(false);
            setSelectedResearchPlacementId(null);
          }}
          onUnlockSuccess={() => {
            // Refetch catalog to show newly unlocked items
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}
